/**
 * The English message catalogue - the source of truth for every user-facing
 * string.
 *
 * Keys are added here first. `ar.ts` is typed as an exact mirror of this
 * object, so a key added without its Arabic counterpart does not compile:
 * translation completeness is a build error rather than a QA pass.
 *
 * Vocabulary follows 15_Naming_Conventions. Deal, never Opportunity. Next
 * step, never Next activity. Workspace, never Tenant. Suggestion, never
 * Recommendation. The engine's spelling never reaches a screen.
 */
export const en = {
  // --- product ------------------------------------------------------------
  'app.name': 'WhinERP Sales & Marketing',
  'app.shortName': 'WhinERP',

  // --- object vocabulary --------------------------------------------------
  'object.lead': 'Lead',
  'object.lead.plural': 'Leads',
  'object.contact': 'Contact',
  'object.contact.plural': 'Contacts',
  'object.account': 'Company',
  'object.account.plural': 'Companies',
  'object.deal': 'Deal',
  'object.deal.plural': 'Deals',
  'object.activity': 'Activity',
  'object.activity.plural': 'Activities',
  'object.nextStep': 'Next step',
  'object.nextStep.plural': 'Next steps',
  'object.campaign': 'Campaign',
  'object.campaign.plural': 'Campaigns',
  'object.segment': 'Audience',
  'object.segment.plural': 'Audiences',
  'object.case': 'Ticket',
  'object.case.plural': 'Tickets',
  'object.quote': 'Quote',
  'object.quote.plural': 'Quotes',
  'object.product': 'Product',
  'object.product.plural': 'Products',
  'object.subscription': 'Subscription',
  'object.subscription.plural': 'Subscriptions',
  'object.workspace': 'Workspace',
  'object.permissionSet': 'Permission set',
  'object.module': 'Module',
  'object.agentRun': 'Agent activity',
  'object.proposal': 'Suggestion',
  'object.proposal.plural': 'Suggestions',

  // --- navigation ---------------------------------------------------------
  'nav.group.dashboard': 'Dashboard',
  'nav.group.crm': 'CRM',
  'nav.group.sales': 'Sales',
  'nav.group.marketing': 'Marketing',
  'nav.group.service': 'Service',
  'nav.group.agents': 'Agents',
  'nav.group.insight': 'Insight',
  'nav.group.admin': 'Setup',
  'nav.promotions': 'Promotions',
  'nav.chat': 'Live chat',
  'nav.agentActivity': 'Agent activity',
  'nav.modules': 'Modules',
  'nav.import': 'Import',
  'nav.plan': 'Plan and usage',
  'nav.inbox': 'Inbox',
  'nav.home': 'Home',
  'nav.work': 'My work',
  'nav.contacts': 'Contacts',
  'nav.leads': 'Leads',
  'nav.forecast': 'Forecast',
  'nav.revenue': 'Revenue',
  'nav.deals': 'Deals',
  'nav.campaigns': 'Campaigns',
  'nav.companies': 'Companies',
  'nav.reportsList': 'Reports',
  'nav.segments': 'Audiences',
  'nav.journeys': 'Journeys',
  'nav.automations': 'Automations',
  'nav.products': 'Products',
  'nav.pricelists': 'Pricelists',
  'nav.support': 'Support',
  'nav.agentsList': 'Agents',
  // The item at /agents/proposals, whose own definition calls it "Waiting for
  // you". Its label said 'Agents', which is what the list of agents is called.
  'nav.agents': 'Waiting for you',
  // The item at /dashboards/home. Its label said 'Reports', which is what the
  // list of saved reports is called - two sidebar items reading the same word,
  // one of which opened something else entirely.
  'nav.reports': 'Dashboards',
  'nav.setup': 'Setup',
  'nav.more': 'More',
  'nav.allAreas': 'All areas',
  'nav.expand': 'Expand navigation',
  'nav.collapse': 'Collapse navigation',
  'nav.primary': 'Primary',

  // --- top bar ------------------------------------------------------------
  'topbar.search': 'Search',
  'topbar.searchPlaceholder': 'Search contacts, deals, companies',
  'topbar.commandPalette': 'Commands',
  'topbar.notifications': 'Notifications',
  'topbar.help': 'Help',
  'topbar.setup': 'Setup',
  'topbar.account': 'Your account',
  'topbar.workspaceSwitcher': 'Switch workspace',

  // --- common actions -----------------------------------------------------
  'action.new': 'New',
  'action.import': 'Import',
  'action.save': 'Save',
  'action.saveAs': 'Save as',
  'action.cancel': 'Cancel',
  'action.discard': 'Discard',
  'action.close': 'Close',
  'action.add': 'Add',
  'action.remove': 'Remove',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.retry': 'Try again',
  'action.refresh': 'Refresh',
  'action.more': 'More actions',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.skip': 'Skip',
  'action.done': 'Done',
  'action.undo': 'Undo',
  'action.signIn': 'Sign in',
  'action.signOut': 'Sign out',
  'action.signUp': 'Create workspace',
  'action.continue': 'Continue',
  'action.complete': 'Complete',
  'action.snooze': 'Snooze',
  'action.reassign': 'Reassign',
  'action.follow': 'Follow',
  'action.unfollow': 'Unfollow',
  'action.loadSample': 'Load sample data',
  'action.clearFilters': 'Clear filters',
  'action.copyLink': 'Copy link',

  // --- states -------------------------------------------------------------
  'state.loading': 'Loading',
  'state.empty': 'Nothing here yet',
  'state.error': 'Something went wrong',
  'state.saving': 'Saving',
  'state.saved': 'Saved',
  'state.unsaved': 'Unsaved changes',
  'state.filteredToNothing': 'No records match these filters',
  'state.filteredToNothing.body': 'Every record is still here - the filters are hiding them.',
  'state.permissionDenied': 'You do not have access to this',
  'state.nothingScheduled': 'Nothing scheduled',
  'state.notSet': 'Not set',
  'state.hidden': 'Hidden',

  // --- capability unavailable --------------------------------------------
  'capability.planLocked.title': 'Included from a higher plan',
  'capability.planLocked.body': 'This is part of {plan}. Your workspace is on {current}.',
  'capability.planLocked.action': 'See what {plan} includes',
  'capability.notConfigured.title': 'Not connected yet',
  'capability.notConfigured.body': '{capability} needs to be connected before this can run.',
  'capability.notConfigured.action': 'Connect {capability}',
  'capability.notBuilt.title': 'Not available in this build',
  'capability.notBuilt.body': '{capability} is specified but not implemented here yet.',
  'capability.notBuilt.action': 'Read what is planned',
  'capability.moduleOff.title': 'Switched off for this workspace',
  'capability.moduleOff.body': '{capability} is available on your plan but switched off.',
  'capability.moduleOff.action': 'Open Setup, Modules',

  // --- sign up / sign in --------------------------------------------------
  'auth.signup.title': 'Create your workspace',
  'auth.signup.subtitle': 'One workspace, everyone on your team.',
  'auth.signup.orgName': 'Workspace name',
  'auth.signup.orgNameHint': 'Your company or team name. You can change it later.',
  'auth.signup.email': 'Work email',
  'auth.signup.password': 'Password',
  'auth.signup.passwordHint': 'At least 12 characters.',
  'auth.signup.useMagicLink': 'Email me a sign-in link instead',
  'auth.signup.usePassword': 'Use a password instead',
  'auth.signup.submit': 'Create workspace',
  'auth.signup.duplicateEmail': 'An account already exists for {email}.',
  'auth.signup.duplicateEmailAction': 'Sign in instead',
  'auth.signup.provisioning': 'Setting up your workspace',
  'auth.signup.step.tenant': 'Creating the workspace',
  'auth.signup.step.roles': 'Adding roles and permissions',
  'auth.signup.step.schema': 'Preparing objects and fields',
  'auth.signup.step.modules': 'Switching on your modules',
  'auth.signup.step.ready': 'Ready',
  'auth.signup.slow': 'This is taking longer than usual. It is still running.',
  'auth.signin.title': 'Sign in',
  'auth.signin.email': 'Work email',
  'auth.signin.magicLink': 'Email me a sign-in link',
  'auth.signin.usePassword': 'Use a password instead',
  'auth.signin.useMagicLink': 'Email me a link instead',
  'auth.signin.sso': 'Continue with single sign-on',
  'auth.signin.sso.body': 'Your organisation signs in through its own identity provider.',
  'auth.signin.submit': 'Sign in',
  'auth.signin.failed': 'That email address and password do not match.',
  'auth.signin.linkSent': 'Check {email} for a sign-in link. It expires in 15 minutes.',
  'auth.signin.deactivated': 'This account is deactivated. Ask an administrator to reactivate it.',
  'auth.mfa.title': 'Enter your verification code',
  'auth.mfa.subtitle': 'Open your authenticator app and enter the six-digit code.',
  'auth.mfa.code': 'Verification code',
  'auth.mfa.useRecovery': 'Use a recovery code instead',
  'auth.mfa.recovery': 'Recovery code',
  'auth.mfa.failed': 'That code is not right. Codes change every 30 seconds.',
  'auth.mfa.submit': 'Verify',

  // --- setup wizard -------------------------------------------------------
  'onboarding.title': 'Five questions, then a working system',
  'onboarding.progress': 'Question {current} of {total}',
  'onboarding.q.industry': 'What does your business do?',
  'onboarding.q.teamSize': 'How many people will use this?',
  'onboarding.q.sells': 'What do you sell?',
  'onboarding.q.cycle': 'How long does a typical sale take?',
  'onboarding.q.channels': 'How do you reach customers?',
  'onboarding.applying': 'Setting up your workspace',
  'onboarding.applying.modules': 'Switching on the modules you need',
  'onboarding.applying.pipeline': 'Creating your pipeline stages',
  'onboarding.applying.fields': 'Adding the fields for your industry',
  'onboarding.applying.automations': 'Installing starter automations',
  'onboarding.applying.sample': 'Adding sample records you can delete',
  'onboarding.failed': 'That step did not finish. Your answers are kept.',

  // --- my work ------------------------------------------------------------
  'work.title': 'My work',
  'work.greeting.morning': 'Good morning, {name}',
  'work.greeting.afternoon': 'Good afternoon, {name}',
  'work.greeting.evening': 'Good evening, {name}',
  'work.section.overdue': 'Overdue',
  'work.section.today': 'Today',
  'work.section.thisWeek': 'This week',
  'work.section.later': 'Later',
  'work.empty.title': 'Nothing scheduled',
  'work.empty.body': 'Open a contact or a deal and schedule your next step from there.',
  'work.empty.action': 'Go to Contacts',
  'work.complete.title': 'How did it go?',
  'work.complete.outcome': 'Outcome',
  'work.complete.note': 'Note',
  'work.complete.nextStep': 'And what is next?',
  'work.complete.nextStepType': 'Next step',
  'work.complete.nextStepDue': 'When',
  'work.complete.submit': 'Complete and schedule',
  'work.complete.noNext': 'Nothing next for now',
  'work.complete.noNextWarning': 'This record will have no next step.',

  // --- lists --------------------------------------------------------------
  'work.section.overdueLabel': 'Overdue',
  'work.snooze': 'Move to tomorrow',
  'work.reassign': 'Pass to somebody else',
  'work.complete.chooseNothing': 'Nothing next for now',
  'work.complete.keepScheduling': 'Schedule the next step instead',
  'work.complete.nothingWarning': 'This record will have no next step. It will not appear in anybody\u2019s work until somebody schedules one.',
  'work.complete.nothingReason': 'Why is there nothing next?',
  'work.complete.submitNothing': 'Complete with nothing scheduled',
  'work.outcome.reached': 'Reached them',
  'work.outcome.noAnswer': 'No answer',
  'work.outcome.rescheduled': 'They asked to reschedule',
  'work.outcome.notInterested': 'Not interested',
  'work.outcome.done': 'Done',
  'work.unscheduled': '{count} contacts have nothing scheduled',
  'work.unscheduled.action': 'Show them',
  'list.status.records': '{count} records',
  'list.status.sortedBy': 'Sorted by {field}',
  'list.status.filteredBy': 'Filtered by {filters}',
  'list.status.updated': 'Updated {when}',
  'list.selected': '{count} selected',
  'list.selectAll': 'Select all {count}',
  'list.clearSelection': 'Clear selection',
  // --- index screens (SC-49..SC-56) ----------------------------------------
  'list.companies.title': 'Companies',
  'list.companies.empty.title': 'No companies yet',
  'list.companies.empty.body': 'A company is the organisation a contact works for, and what a deal is against.',
  'list.companies.contacts': 'People',
  'list.companies.industry': 'Industry',
  'list.companies.owner': 'Owner',
  'list.journeys.title': 'Journeys',
  'list.journeys.empty.title': 'No journeys yet',
  'list.journeys.empty.body': 'A journey takes somebody through a sequence of steps over days or weeks.',
  'list.automations.title': 'Automations',
  'list.automations.empty.title': 'No automations yet',
  'list.automations.empty.body': 'An automation reacts to something happening and does the next thing without being asked.',
  'list.reports.title': 'Reports',
  'list.reports.empty.title': 'No saved reports yet',
  'list.reports.empty.body': 'A report groups records and summarises them, and can be put on a dashboard.',
  'list.agents.title': 'Agents',
  'list.agents.empty.title': 'No agents yet',
  'list.agents.empty.body': 'An agent does work on your behalf, within limits you set.',
  'product.editor.new': 'New product',
  'product.editor.edit': 'Change product',
  'product.editor.sku': 'Code',
  'product.editor.skuHelp': 'What a quote line refers to. Unique in this workspace.',
  'product.editor.name': 'Name',
  'product.editor.family': 'Family',
  'product.editor.description': 'Description',
  'product.editor.price': 'List price',
  'product.editor.priceHelp': 'What a pricelist rule adjusts. A discount belongs on the rule, not here.',
  'product.editor.unit': 'Sold by',
  'product.editor.currency': 'Currency',
  'product.editor.save': 'Save product',
  'product.editor.cancel': 'Cancel',
  'product.editor.archive': 'Stop selling this',
  'product.editor.archived': 'No longer sold',
  'product.editor.archiveHelp': 'It stays on quotes and orders that already name it.',
  'product.editor.restore': 'Sell this again',
  'list.products.title': 'Products',
  'list.products.empty.title': 'No products yet',
  'list.products.empty.body': 'A product is something you sell, with a list price a quote starts from.',
  'list.pricelists.title': 'Pricelists',
  'list.pricelists.empty.title': 'No pricelists yet',
  'list.pricelists.empty.body': 'A pricelist decides what a particular customer pays, by rules rather than by hand.',
  'list.deals.title': 'Deals',
  'list.deals.empty.title': 'No deals yet',
  'list.deals.empty.body': 'A deal is a sale you are working on, with an amount and a close date.',
  'list.deals.board': 'Show as a board',
  'list.deals.value': 'Value of these deals',
  'list.cases.title': 'Cases',
  'list.cases.empty.title': 'No cases yet',
  'list.cases.empty.body': 'A case is a customer problem, worked to a resolution against an agreed clock.',
  'list.cases.showAsList': 'Show as a list',
  'list.cases.queue': 'Show as a queue',
  'list.campaigns.title': 'Campaigns',
  'list.campaigns.empty.title': 'No campaigns yet',
  'list.campaigns.empty.body': 'A campaign is one send to one audience, with what it did afterwards.',
  'list.campaigns.showAsList': 'Show as a list',
  'list.campaigns.cards': 'Show as cards',
  'list.column.amount': 'Amount',
  'list.column.closeDate': 'Closes',
  'list.column.company': 'Company',
  'list.column.subject': 'Subject',
  'list.column.priority': 'Priority',
  'list.column.channel': 'Channel',
  'list.column.sent': 'Sent',
  'list.column.audience': 'Audience',
  'list.column.name': 'Name',
  'list.column.status': 'Status',
  'list.column.updated': 'Updated',
  'list.column.visibility': 'Who can see it',
  'list.column.about': 'About',
  'list.column.purpose': 'Purpose',
  'list.column.sku': 'Code',
  'list.column.price': 'List price',
  'list.column.currency': 'Currency',
  'list.column.rules': 'Rules',
  'list.column.validity': 'Valid',
  'list.expired': 'Expired',
  'list.active': 'Active',
  'list.searchThisList': 'Search this list',
  'list.settings': 'List settings',
  'list.density': 'Row height',
  'list.density.comfortable': 'Comfortable',
  'list.density.compact': 'Compact',
  'list.columns': 'Columns',
  'list.inlineEdit': 'Inline edit',
  'list.chartView': 'Chart',
  'list.tableView': 'Table',
  'list.filters': 'Filters',
  'list.view': 'View',
  'list.view.pin': 'Pin this view',
  'list.view.unpin': 'Unpin this view',
  'list.view.unsaved': 'This view has unsaved changes',
  'list.moreActions': 'More actions',
  'list.removeFilter': 'Remove filter {filter}',
  'list.pinNamed': 'Pin {name}',
  'list.unpinNamed': 'Unpin {name}',
  'list.bulkToolbar': 'Actions for the selected rows',
  'list.sortBy': 'Sort by {column}',
  'list.selectRow': 'Select row {row}',
  'list.rowsShown': '{count} rows shown',
  'list.page.next': 'Next page',
  'list.page.previous': 'Previous page',
  'list.page.showing': 'Showing {from} to {to}',

  // --- errors -------------------------------------------------------------
  'error.network': 'Could not reach the server. Check your connection and try again.',
  'error.unexpected': 'Something went wrong on our side. Nothing was changed.',
  'error.notFound': 'That record was not found. It may have been deleted.',
  'error.forbidden': 'You do not have permission to do that.',
  'error.validation': 'Some details need fixing before this can be saved.',
  'error.conflict': 'Someone else changed this while you were editing.',
  'error.traceId': 'Reference {traceId}',

  // --- account menu -------------------------------------------------------
  'account.profile': 'Your profile',
  'account.preferences': 'Preferences',
  'account.theme': 'Appearance',
  'account.theme.light': 'Light',
  'account.theme.dark': 'Dark',
  'account.theme.system': 'Match system',
  'account.language': 'Language',
  'account.language.en': 'English',
  'account.language.ar': 'العربية',
  'setup.category.pricing': 'Pricing',
  'setup.pricing.body': 'Price books and the rules that decide a price.',
  'pricelist.ruleCount': '{count} rules',
  'pricelist.empty': 'No rules yet',
  'pricelist.expired': 'Nothing in date',
  'pricelist.inactive': 'Switched off',
  'pricelist.none': 'No price books yet. The list price on each product is used until there is one.',
  'agent.budget': 'Budget',
  'agent.budget.workspace': 'Workspace',
  'agent.budget.ofLimit': '{spent} of {limit}',
  'agent.budget.ok': 'Within budget',
  'agent.budget.warning': 'Approaching the limit',
  'agent.budget.exceeded': 'Over the limit',
  'agent.budget.stops': 'Agents stop when this is reached',
  'agent.budget.reports': 'Reported only; agents keep running',
  'agent.budget.none': 'No spending limit is set.',
  'agent.spend.byAgent': 'By agent',
  'agent.spend.byUser': 'By person',
  'agent.spend.who': 'Where it went',
  'agent.spend.runs': 'Runs',
  'agent.spend.unnamed': 'Deleted',
  'report.countedOf': '{counted} of {count} records',
  'report.inWorkspaceCurrency': '{counted} of {count} records, in this workspace\u2019s currency',
  'chart.ofPrevious': '{change} of {step}',
  'chart.rose': 'up on {step}',
  'variants.title': 'Variants',
  'variants.attributes': 'Attributes',
  'variants.attribute.name': 'Attribute',
  'variants.attribute.values': 'Values, separated by commas',
  'variants.attribute.add': 'Add an attribute',
  'variants.attribute.remove': 'Remove {name}',
  'variants.save': 'Save attributes',
  'variants.generate': 'Generate variants',
  'variants.generate.count': '{count} combinations',
  'variants.generate.existing': '{count} already exist and are left alone',
  'variants.generate.warn': 'That would make {count} variants, which is a lot. Generate them anyway?',
  'variants.generated': '{created} created, {skipped} left alone',
  'variants.table': 'Generated variants',
  'variants.sku': 'SKU',
  'variants.adjustment': 'Price adjustment',
  'variants.price': 'Price',
  'variants.active': 'Available',
  'variants.inactive': 'Not available',
  'variants.empty.title': 'No attributes yet',
  'variants.empty.body': 'An attribute is something the product varies by, such as Colour or Size. Add one and its values, and every combination becomes a variant with its own SKU and price.',
  'variants.grid': 'Order across the grid',
  'variants.grid.needsTwo': 'A grid needs exactly two attributes: one across and one down.',
  'variants.grid.total': '{count} units across {variants} variants',
  'variants.grid.missing': 'No variant',
  'promotions.title': 'Promotions and loyalty',
  'promotions.coupons': 'Coupons',
  'promotions.automatic': 'Automatic',
  'promotions.all': 'All',
  'promotions.code': 'Code',
  'promotions.noCode': 'Applies without a code',
  'promotions.used': 'Used {count} times',
  'promotions.usedOf': 'Used {count} of {max}',
  'promotions.unlimited': 'No limit',
  'promotions.until': 'Until {date}',
  'promotions.state.inactive': 'Switched off',
  'promotions.state.expired': 'Expired',
  'promotions.state.not-started': 'Not started yet',
  'promotions.state.exhausted': 'Used up',
  'promotions.state.live': 'Live',
  'promotions.conditions': 'It applies when',
  'promotions.log': 'Recent uses',
  'promotions.log.empty': 'No uses recorded yet.',
  'promotions.log.gap': 'The count is older than the log kept here.',
  'promotions.select': 'Choose a promotion to see how it works and where it has been used.',
  'promotions.empty.title': 'No promotions yet',
  'promotions.empty.body': 'A coupon is a promotion with a code somebody types; an automatic promotion applies whenever its conditions match. Both use the same conditions, so a rule that works on one works on the other.',
  'quote.lapsed': 'Out of date',
  'quote.lapsed.body': 'This quote was valid until {date}. Change the date before sending it.',
  'guardrail.no-discount': 'Never offer a discount',
  'guardrail.max-spend': 'Spending limit per conversation',
  'guardrail.mask-pii': 'Keep contact details out of answers',
  'guardrail.require-citation': 'Only answer from records it has read',
  'guardrail.refuse-topic': 'Refuse a topic',
  'guardrail.escalate-on': 'Hand over to a person when',
  'guardrail.enforced': 'Checked by the engine',
  'guardrail.instructed': 'Asked of the model',
  'guardrail.enforced.help': 'The engine refuses when this is broken.',
  'guardrail.instructed.help': 'Stated in the instructions. Nothing here can verify it.',
  'setup.category.people': 'People',
  'setup.people.body': 'Who is here, who reports to whom, and which teams they are in.',
  'people.roles': 'Reporting line',
  'people.teams': 'Teams',
  'people.members': '{count} people',
  'people.noMembers': 'Nobody yet',
  'people.role.none': 'No role',
  'people.role.change': 'Role for {name}',
  'people.admin': 'Administrator',
  'people.seesBelow': 'Sees everything owned below this line',
  'people.teams.independent': 'A team is not part of the reporting line. People from anywhere can be in one.',
  'people.reportsTo': '{role} reports into',
  'people.reportsTo.none': 'Nobody (top of the line)',
  'people.inspect': 'What can {name} see?',
  'people.reach.title': 'What {name} can see',
  'people.reach.own': 'Owns',
  'people.reach.visible': 'Can see',
  'people.reach.hierarchy': 'Of those, only because people report to them',
  'people.reach.nobody': 'Nobody reports to {name}, so the reporting line adds nothing.',
  'people.reach.everything': '{name} is an administrator and already sees every record, so the reporting line adds nothing.',
  'people.reach.reports': 'Reports to {name}',
  'people.reach.close': 'Close',
  'fls.title': 'Fields',
  'fls.open': 'Field rules',
  'fls.close': 'Done',
  'fls.affected': 'Changes what {count} people see',
  'fls.affected.none': 'Nobody holds this set yet',
  'fls.access.edit': 'Edit',
  'fls.access.read': 'Read only',
  'fls.access.hidden': 'Hidden',
  'fls.object': 'Object',
  'fls.field': 'Field',
  'fls.empty': 'Every field is editable for this set. Choose a field to restrict it.',
  'sharing.title': 'Sharing',
  'sharing.body': 'Who can see each kind of record. Owners and their managers always can.',
  'sharing.mode.private': 'Owner only',
  'sharing.mode.private.help': 'The person who owns it, and whoever they report to.',
  'sharing.mode.team': 'Their team',
  'sharing.mode.team.help': 'Everybody in the team the record belongs to.',
  'sharing.mode.everyone': 'Everyone',
  'sharing.mode.everyone.help': 'Every person in this workspace.',
  'sharing.mode.criteria': 'Records that match a rule',
  'sharing.mode.criteria.help': 'Chosen records, shared with one team or one role.',
  'sharing.access': 'How much',
  'sharing.access.read': 'Read',
  'sharing.access.read-write': 'Read and edit',
  'sharing.criteria.team': 'Share with team',
  'sharing.criteria.role': 'Share with role',
  'sharing.noOwner': 'This has no owner, so it cannot be owner-only.',
  'customObject.title': 'Your objects',
  'customObject.body': 'Things this workspace tracks that the product does not ship with.',
  'customObject.new': 'New object',
  'customObject.step1': 'What is it called',
  'customObject.step2': 'Its API name',
  'customObject.step3': 'Who can see it',
  'customObject.label': 'One of them is a',
  'customObject.plural': 'Several of them are',
  'customObject.key': 'API name',
  'customObject.key.help': 'Used in URLs and the API. It cannot be changed later.',
  'customObject.icon': 'Icon',
  'customObject.create': 'Create object',
  'customObject.records': '{count} records',
  'customObject.none': 'No records yet',
  'customObject.empty': 'Nothing yet. An object is a kind of record, like a shipment or a contract.',
  'formula.title': 'Formula and rollup fields',
  'formula.body': 'A formula works out its value when somebody reads it. A rollup counts or totals related records.',
  'formula.new': 'New field',
  'formula.name': 'Field name',
  'formula.label': 'Label',
  'formula.kind': 'Kind',
  'formula.kind.formula': 'Formula',
  'formula.kind.rollup': 'Rollup',
  'formula.expression': 'Formula',
  'formula.functions': 'Functions',
  'formula.fields': 'Fields',
  'formula.preview': 'On your most recent record',
  'formula.preview.none': 'No records yet to try it against.',
  'formula.save': 'Add field',
  'formula.rollup.object': 'Count records from',
  'formula.rollup.key': 'Linked by',
  'formula.rollup.fn': 'What to work out',
  'formula.rollup.field': 'Of which field',
  'formula.empty': 'No formula or rollup fields yet.',
  'error.brand.tooPale': '{colour} is too pale to be seen against the page. The nearest shade of it that works is {suggestion}.',
  'dup.title': 'This might already be here',
  'dup.reason.email': 'Same email address',
  'dup.reason.phone': 'Same phone number',
  'dup.reason.name-and-domain': 'Similar name at the same company',
  'dup.reason.name': 'Similar name',
  'dup.confidence.certain': 'Almost certainly the same',
  'dup.confidence.likely': 'Probably the same',
  'dup.confidence.possible': 'Might be the same',
  'dup.view': 'Open it',
  'dup.useInstead': 'Use this one instead',
  'dup.saveAnyway': 'Save anyway',
  'dup.notDuplicate': 'Not a duplicate',
  'dup.queue': 'Possible duplicates',
  'dup.queue.body': 'Pairs that look like the same person or company. Nothing here has been changed.',
  'dup.queue.none': 'Nothing looks duplicated.',
  'dup.merge': 'Merge',
  'dup.merge.preview': 'What the merged record will hold',
  'dup.merge.keepWinner': 'Keep',
  'dup.merge.takeLoser': 'Take',
  'dup.merge.confirm': 'Merge these two',
  'dup.merge.moving': '{count} related rows move to the surviving record',
  'dup.merge.redundant': '{count} rows are the same on both and will be dropped',
  'dup.merge.done': 'Merged. The other record is in the recycle bin.',
  'contact.new': 'New contact',
  'contact.new.first': 'First name',
  'contact.new.last': 'Last name',
  'contact.new.email': 'Email',
  'contact.new.phone': 'Phone',
  'contact.new.save': 'Create contact',
  'audit.title': 'Audit log',
  'audit.body': 'Who changed what, and when. This log cannot be edited or deleted.',
  'audit.history': 'History',
  'audit.history.none': 'No changes recorded yet.',
  'audit.none': 'Nothing recorded yet.',
  'audit.actor.user': 'Person',
  'audit.actor.agent': 'Agent',
  'audit.actor.system': 'System',
  'audit.filter.actorType': 'Changed by',
  'audit.filter.object': 'Object',
  'audit.filter.since': 'From',
  'audit.filter.all': 'Anyone',
  'audit.changed': '{field}: {before} → {after}',
  'audit.wasEmpty': 'empty',
  'search.recents': 'Recently opened',
  'search.results': 'Results',
  'search.none': 'Nothing matched {term}.',
  'search.lookedIn': 'Searched {count} kinds of record: {objects}.',
  'search.seeAll': 'See all results',
  'search.hint': 'Type to search, or pick something you had open.',
  'search.title': 'Search results',
  // "All records", not "All contacts": the same bar is now above deals,
  // leads, cases and campaigns, and a label naming one object was wrong on
  // four screens out of five.
  'view.all': 'All records',
  'view.switch': 'View',
  'view.save': 'Save',
  'view.saveAs': 'Save as new view',
  'view.discard': 'Discard changes',
  'view.unsaved': 'This view has unsaved changes',
  'view.name': 'View name',
  'view.visibility': 'Who can see it',
  /*
   * "and administrators", because that is what is enforced.
   *
   * `loadView` lets a workspace administrator read any view by id, which is
   * what makes it possible to tidy up the views of somebody who has left. The
   * label said "Only me", which is a promise the code does not keep - and a
   * displayed rule has to be the rule.
   */
  'view.visibility.private': 'Only me and administrators',
  'view.visibility.team': 'My team',
  'view.visibility.everyone': 'Everyone',
  'view.duplicate': 'Duplicate',
  'view.delete': 'Delete view',
  'view.pin': 'Pin',
  'view.unpin': 'Unpin',
  'view.readOnly': 'This view belongs to somebody else. Duplicate it to make changes.',
  'view.created': 'View saved.',
  'view.columns': 'Columns',
  'view.columns.chosen': '{shown} of {total} columns',
  'view.groupBy': 'Group by',
  'view.groupBy.none': 'No grouping',
  'view.group.empty': 'No value',
  'notify.title': 'Notifications',
  'notify.none': 'Nothing new.',
  'notify.today': 'Today',
  'notify.earlier': 'Earlier',
  'notify.markAll': 'Mark all read',
  'notify.mute': 'Stop notifying me about this',
  'notify.muted': 'Muted. You will not hear about this record again.',
  'notify.unread': '{count} unread',
  'notify.preferences': 'Notification preferences',
  'notify.preferences.body': 'Switch off what you do not want to hear about. Everything here appears on the bell.',
  'notify.channel.in-app': 'On the bell',
  'notify.onlyInApp': 'Email and push notifications are not sent yet, so they are not offered here.',
  'env.title': 'Environments',
  'env.body': 'Move configuration between a sandbox and production. Objects, fields, layouts, permission sets and sharing — never records.',
  'env.export': 'Export this workspace',
  'env.paste': 'Paste a bundle',
  'env.review': 'Review changes',
  'env.new': 'New',
  'env.changed': 'Changed',
  'env.identical': 'Already the same',
  'env.onlyHere': 'Only in this workspace ({count}). Nothing here is deleted.',
  'env.apply': 'Apply {count} selected',
  'env.applied': 'Applied {count}. Skipped {skipped}.',
  'env.nothingSelected': 'Nothing selected. Tick the items to bring across.',
  'env.selectAllChanged': 'Select everything new or changed',
  'plan.target': 'Move to plan',
  'plan.change': 'Change plan',
  'plan.moveTo': 'Move to {plan}',
  'plan.review': 'See what changes',
  'plan.upgrade.gains': 'You would gain {count} features.',
  'plan.downgrade.warns': 'You would lose access to {count} features.',
  'plan.downgrade.records': '{count} records become unreachable',
  'plan.downgrade.noRecords': 'No records behind this one',
  'plan.downgrade.kept': 'Nothing is deleted. The records stay; they stop being reachable until you upgrade again.',
  'plan.confirm': 'Confirm this change',
  'plan.same': 'This is the plan you are on.',
  'plan.changed': 'Now on {plan}.',
  'usage.title': 'Usage',
  'usage.of': '{used} of {limit}',
  'usage.unlimited': '{used} · no limit on this plan',
  'usage.period': 'this month',
  'usage.warning': 'You are close to a limit',
  'usage.critical': 'You are about to reach a limit',
  'usage.exceeded': 'A limit has been reached',
  'usage.seeePlans': 'See plans',
  'empty.action.openAgents': 'Open agents',
  'empty.action.addWidget': 'Add a widget',
  'empty.action.newPromotion': 'Create a promotion',
  'empty.action.addVariant': 'Add a variant',
  'empty.action.chatSettings': 'Open chat settings',
  'empty.action.addContact': 'Add a contact',
  'empty.action.importContacts': 'Import from a spreadsheet',
  'empty.action.newDeal': 'Create a deal',
  'empty.action.addLine': 'Add a line',
  'empty.action.addStep': 'Add the first step',
  'empty.action.runIt': 'Turn it on and run it',
  'empty.action.addRule': 'Add a rule',
  'empty.action.newCampaign': 'Create a campaign',
  'empty.action.addLead': 'Add a lead',
  'empty.action.openDeals': 'Open deals',
  'empty.action.newCase': 'Log a ticket',
  'empty.action.chooseType': 'Choose a field type',
  'empty.action.chooseFile': 'Choose a file',
  'empty.action.addSection': 'Add a section',
  'empty.action.openModules': 'Open modules',
  'empty.action.seePlans': 'See plans',
  'empty.action.clearSearch': 'Clear the search',
  'empty.action.search': 'Search everything',
  'empty.action.openSetup': 'Open Setup',
  'bulk.title': 'Bulk jobs',
  'bulk.body': 'High-volume work, off the interactive path. Progress is counted from rows, not guessed from the clock.',
  'bulk.empty': 'No bulk jobs. Submit one through the API when you have more rows than a screen should carry.',
  'bulk.rowLimit': 'Up to {limit} rows in one job on your plan, counted separately from the per-minute API limit.',
  'bulk.noLimit': 'No row limit on your plan. Counted separately from the per-minute API limit.',
  'bulk.progress': '{done} of {total} rows',
  'bulk.failed': '{count} failed',
  'bulk.stalled': 'Nothing has touched this job lately.',
  'bulk.resume': 'Resume',
  'bulk.cancel': 'Stop',
  'bulk.errors': 'Error file',
  'bulk.status.queued': 'Waiting',
  'bulk.status.running': 'Running',
  'bulk.status.completed': 'Finished',
  'bulk.status.failed': 'Failed',
  'bulk.status.cancelled': 'Stopped',
  'mcp.title': 'Agent access (MCP)',
  'mcp.body': 'What an external AI agent reaches with a token. This is the agent’s own view, drawn with the permissions of the person the token acts as — not yours.',
  'mcp.connection': 'Connection',
  'mcp.choose': 'Show what this token exposes',
  'mcp.actsAs': 'Acts as {name}',
  'mcp.resources': 'Resources it can read',
  'mcp.tools': 'Tools it can call',
  'mcp.withheld': 'Held back, and why',
  'mcp.readOnly': 'read only',
  'mcp.nothing': 'This token exposes nothing at all.',
  'mcp.pick': 'Choose a token',
  'sdk.title': 'Client libraries',
  'sdk.body': 'Both are generated from the contract above on every API change, so neither can drift from it.',
  'sdk.js': 'JavaScript / TypeScript',
  'sdk.py': 'Python',
  'sdk.notPublished': 'Generated and checked in this repository. Publishing to npm and PyPI is a release step that is not wired up yet.',
  'embed.title': 'Embeds',
  'embed.body': 'Render a widget inside another site. A token is minted per page load, scoped to one widget, and only for an origin you list here.',
  'embed.name': 'What is this embed for?',
  'embed.widget': 'Which widget?',
  'embed.object': 'Which object?',
  'embed.origins': 'Which sites may render it?',
  'embed.originsHelp': 'One per line, like https://partner.example. An embed with none allows nobody.',
  'embed.create': 'Create the embed',
  'embed.empty': 'No embeds. Create one when another site needs to show something from here.',
  'embed.revoke': 'Revoke',
  'embed.revoked': 'Revoked',
  'embed.snippet': 'Paste this into the host page',
  'embed.preview': 'Preview',
  'embed.previewEmpty': 'Nothing to show yet — this workspace has no records for that widget.',
  'embed.previewFailed': 'The preview could not load. Whatever is wrong here is what the host site would see.',
  'embed.tokenLife': 'Tokens live {seconds} seconds.',
  'embed.noOrigins': 'No origins yet, so nobody can render this.',
  'apps.title': 'Apps',
  'apps.body': 'Third-party code that runs here. Each one runs in its own isolate with a time and memory limit, and can do only what you grant it.',
  'apps.paste': 'Paste a plugin manifest',
  'apps.review': 'Review what it asks for',
  'apps.asking': '{name} by {publisher} is asking to:',
  'apps.unknown': 'It also asks for {scopes}, which this product does not offer. It will not get them.',
  'apps.nothingAsked': 'It asks for nothing at all.',
  'apps.install': 'Install with what I have ticked',
  'apps.cancel': 'Don’t install',
  'apps.installedOff': 'Installed and switched off. Switch it on when you are ready.',
  'apps.empty': 'No apps installed. Paste a manifest above to see what it would ask for.',
  'apps.enable': 'Switch on',
  'apps.disable': 'Switch off',
  'apps.refused': 'You refused: {scopes}',
  'apps.granted': 'Granted: {scopes}',
  'apps.noneGranted': 'No permissions granted',
  'apps.limits': '{ms}ms, {mb}MB',
  'apps.runs': 'Recent runs',
  'apps.noRuns': 'It has not run yet.',
  'apps.test': 'Run it once',
  'apps.outcome.ok': 'Worked',
  'apps.outcome.failed': 'Failed',
  'apps.outcome.timed-out': 'Ran too long',
  'apps.outcome.refused': 'Switched off',
  'tags.title': 'Tags',
  'tags.body': 'Free-form labels that work across every object. Near-duplicates are what make a tag list useless, so this one is checked for them.',
  'tags.add': 'Add a tag',
  'tags.placeholder': 'Type a tag',
  'tags.uses': '{count} records',
  'tags.governed': 'Only administrators may create a new tag',
  'tags.ungoverned': 'Anybody may create a tag',
  'tags.similar': 'These look like each other',
  'tags.merge': 'Merge into {label}',
  'tags.merged': '{count} records moved onto {label}.',
  'tags.rename': 'Rename',
  'tags.empty': 'No tags yet. Add one on a record, or create the first here.',
  'tags.noSimilar': 'No near-duplicates.',
  'files.title': 'Files',
  'files.drop': 'Drop a file anywhere on this page',
  'files.choose': 'Choose a file',
  'files.empty': 'No files yet. Drop one anywhere on this page.',
  'files.version': 'Version {n}',
  'files.notScanned': 'Not scanned',
  'files.infected': 'Malware found',
  'files.remove': 'Remove',
  'files.max': 'Up to {size}',
  'files.tooBig': 'That file is {size}. The limit is {max}.',
  'files.uploading': 'Uploading {name}…',
  'work.priority.high': 'High',
  'work.priority.low': 'Low',
  'work.reminded': '{count} reminders sent.',
  'graphql.title': 'Query playground',
  'graphql.body': 'One request across related objects. Depth is capped at {depth} and cost at about {cost} database queries, and a refusal names the field it went over at.',
  'graphql.run': 'Run',
  'graphql.result': 'Result',
  'graphql.readOnly': 'Read only. There are no mutations: writing goes through the REST endpoints.',
  'developer.title': 'Developer',
  'developer.body': 'Tokens let a program call this workspace. A token acts as the person who created it and sees exactly what they see.',
  'developer.name': 'What is this token for?',
  'developer.scopes': 'What may it do?',
  'developer.create': 'Issue a token',
  'developer.secretTitle': 'Copy this now. It will not be shown again.',
  'developer.secretBody': 'It is stored as a hash, so nobody can read it back — not even us. If you lose it, issue another.',
  'developer.done': 'I have copied it',
  'developer.empty': 'No tokens yet. Issue one when a program needs to call this workspace on its own.',
  'developer.revoke': 'Revoke',
  'developer.revoked': 'Revoked',
  'developer.neverUsed': 'Never used',
  'developer.lastUsed': 'Last used {when}',
  'developer.actsAs': 'Acts as {name}',
  'developer.contract': 'Read the API contract',
  'developer.rateLimit': '{limit} calls a minute on your plan, counted per token.',
  'developer.confirmRevoke': 'Revoke {name}?',
  'developer.confirmRevokeBody': 'Any program using this token stops working on its next call. This cannot be undone; issue a new token instead.',
  'health.title': 'Health',
  'health.body': 'What is failing now, counted over the last {hours} hours. Every figure here counts something a person or a job actually did.',
  'health.status.ok': 'Working',
  'health.status.warn': 'Degraded',
  'health.status.down': 'Failing',
  'health.status.not-measured': 'Not measured',
  'health.look': 'Take a look',
  'health.notMeasured': 'Not measured here',
  'privacy.title': 'Data subject requests',
  'privacy.body': 'Somebody asking for their data, or asking you to erase it. Answering is recorded, because the record is the evidence that you answered.',
  'privacy.subjectEmail': 'Their email address',
  'privacy.kind.access': 'Send them their data',
  'privacy.kind.erasure': 'Erase them',
  'privacy.open': 'Record the request',
  'privacy.queue': 'Requests',
  'privacy.empty': 'No requests. When somebody asks for a copy of their data or asks to be erased, record it here so the answer is evidenced.',
  'privacy.status.open': 'Open',
  'privacy.status.completed': 'Answered',
  'privacy.status.refused': 'Refused',
  'privacy.review': 'Review what would be erased',
  'privacy.export': 'Send the export',
  'privacy.refuse': 'Refuse',
  'privacy.refuseReason': 'Why is this being refused?',
  'privacy.confirmTitle': 'This will erase {email} from {count} stores.',
  'privacy.confirmBody': 'Read the list. Erasing cannot be undone, and each store is treated differently.',
  'privacy.action.delete': 'emptied',
  'privacy.action.anonymise': 'kept, with the person removed',
  'privacy.retainedTitle': 'These keep something about them',
  'privacy.erase': 'Erase from all {count} stores',
  'privacy.nothing': 'Nothing about this address is held here. There is nothing to erase.',
  'privacy.already': 'This address was already erased.',
  'privacy.erased': 'Erased from {count} stores.',
  'privacy.exported': 'Export sent. Row count: {count}.',
  'residency.title': 'Where your data is held',
  'residency.region': 'Region',
  'residency.fixed': 'This cannot be changed. Moving a workspace means moving its records, files, backups and embeddings, which is a migration rather than a setting.',
  'residency.choose': 'Where should your data be held?',
  'residency.chooseHelp': 'Choose carefully: this cannot be changed afterwards.',
  'residency.onlyHere': 'Your data will be held in {region}.',
  'brand.title': 'Branding',
  'brand.body': 'Choose one colour. Every shade the interface needs is worked out from it, so nothing you pick can end up unreadable.',
  'brand.enable': 'Use this workspace’s branding',
  'brand.colour': 'Brand colour',
  'brand.logo': 'Logo URL',
  'brand.favicon': 'Favicon URL',
  'brand.loginBackground': 'Sign-in background URL',
  'brand.sendingDomain': 'Sending domain',
  'brand.sendingDomain.help': 'The domain marketing email is sent from.',
  'brand.save': 'Save branding',
  'brand.preview': 'Preview',
  'brand.preview.nav': 'Navigation',
  'brand.preview.record': 'A record',
  'brand.preview.email': 'An email',
  'brand.preview.button': 'Primary action',
  'brand.preview.link': 'A link in body text',
  'brand.useSuggested': 'Use {colour} instead',
  'brand.derived': 'Derived from your colour',
  'setup.showAdvanced': 'Show {count} more advanced sections',
  'setup.advancedHidden': 'Sharing rules, formula fields, custom objects and single sign-on are in Advanced.',
  'sso.title': 'Single sign-on',
  'sso.body': 'Send people to your identity provider instead of asking them for a password. Sign in once yourself before you enforce it.',
  'sso.protocol': 'Protocol',
  'sso.issuer': 'Issuer',
  'sso.clientId': 'Client ID',
  'sso.clientSecret': 'Client secret',
  'sso.secretKept': 'A secret is saved. Leave this blank to keep it.',
  'sso.authorizeUrl': 'Authorize URL',
  'sso.tokenUrl': 'Token URL',
  'sso.jwksUrl': 'Keys (JWKS) URL',
  'sso.saml.metadata': 'What to give your provider',
  'sso.saml.entityId': 'Entity ID (audience)',
  'sso.saml.acs': 'Reply URL (assertion consumer service)',
  'sso.saml.signing': 'Sign the assertion itself, not only the response. A response signed around an unsigned assertion is refused.',
  'sso.saml.copy': 'Copy the metadata',
  'sso.saml.copied': 'Copied',
  'sso.entityId': 'Entity ID',
  'sso.ssoUrl': 'Sign-on URL',
  'sso.certificate': 'Signing certificate',
  'sso.domains': 'Domains',
  'sso.domains.help': 'Addresses on these domains are sent to your provider. One workspace per domain.',
  'sso.save': 'Save connection',
  'sso.verified': 'Tested {when}',
  'sso.notVerified': 'Not tested yet. Sign in with this provider once before enforcing it.',
  'sso.enforce': 'Require single sign-on',
  'sso.enforced': 'Required. Everybody except the break-glass administrator signs in through the provider.',
  'sso.notEnforced': 'Optional. People can still sign in with a password.',
  'sso.jit': 'Create accounts at sign-in',
  'sso.jit.help': 'Somebody with no account here gets one on their first sign-in. Choose what they can see.',
  'sso.jit.role': 'Role for new accounts',
  'sso.breakGlass': 'Break-glass administrator',
  'sso.breakGlass.help': 'One account that can always sign in with a password, even when the provider cannot be reached.',
  'sso.scim.title': 'Directory provisioning',
  'sso.scim.body': 'Give your provider a token so it can create, update and deactivate people here.',
  'sso.scim.create': 'Issue a token',
  'sso.scim.label': 'Label',
  'sso.scim.once': 'Copy this now. It is not shown again.',
  'sso.scim.expiry': 'Expires after',
  'sso.scim.expiry.never': 'Does not expire',
  'sso.scim.expiry.days': '{count} days',
  'sso.scim.expiresOn': 'Runs out {when}',
  'sso.scim.expired': 'Expired — this token no longer works',
  'sso.scim.revoke': 'Revoke',
  'sso.scim.none': 'No tokens. Your provider cannot provision anybody until there is one.',
  'sso.scim.lastUsed': 'Last used {when}',
  'sso.scim.neverUsed': 'Never used',
  'fx.title': 'Currencies and rates',
  'week.title': 'Working week',
  'bin.title': 'Recycle bin',
  'bin.body': 'Deleted records stay here for {days} days on this plan, then go for good.',
  'bin.empty': 'Nothing has been deleted.',
  'bin.all': 'Everything',
  'bin.filter': 'Kind of record',
  'bin.record': 'Record',
  'bin.deletedBy': 'Deleted by',
  'bin.deletedAt': 'Deleted',
  'bin.expires': 'Goes for good',
  'bin.expired': 'Past its window',
  'bin.restore': 'Restore',
  'bin.purge': 'Remove for good',
  'bin.purgeExpired': 'Remove everything past its window',
  'bin.purgeExpired.none': 'Nothing is past its window.',
  'bin.purged': 'Removed {count} for good.',
  'bin.confirm.title': 'Remove {name} for good?',
  'bin.confirm.body': 'This cannot be undone. The audit trail keeps a record that it happened and who did it.',
  'bin.confirm.yes': 'Remove for good',
  'bin.noScheduler': 'There is no scheduler, so records past their window stay until somebody removes them.',
  'week.body': 'What an SLA clock counts when a policy says it stops outside working hours.',
  'week.days': 'Days worked',
  'week.start': 'Opens',
  'week.end': 'Closes',
  'week.save': 'Save working week',
  'week.saved': 'Saved.',
  'week.day.0': 'Sunday',
  'week.day.1': 'Monday',
  'week.day.2': 'Tuesday',
  'week.day.3': 'Wednesday',
  'week.day.4': 'Thursday',
  'week.day.5': 'Friday',
  'week.day.6': 'Saturday',
  'week.zone': 'Times are in {zone}, the workspace time zone.',
  'fx.body': 'What a currency was worth, from a date. A record converts at the rate that applied on its own date.',
  'fx.reporting': 'This workspace reports in {code}',
  'fx.add': 'Add a currency',
  'fx.code': 'Currency',
  'fx.remove': 'Remove',
  'fx.rate.from': 'From',
  'fx.rate.to': 'To',
  'fx.rate.on': 'From date',
  'fx.rate.value': 'Rate',
  'fx.rate.save': 'Save rate',
  'fx.rate.none': 'No rates yet. Amounts in other currencies cannot be converted until there is one.',
  'report.converted': 'All currencies, in {code}',
  'report.converted.gap': '{count} amounts could not be converted: no {from} rate.',
  'refusal.sso.noProvider': 'There is no identity provider for that workspace.',
  'refusal.sso.refused': 'The identity provider refused the sign-in.',
  'refusal.sso.requestSpent': 'That sign-in has already been used or has expired. Start again.',
  'refusal.sso.usedAlready': 'That sign-in has already been used. Start again.',
  'refusal.sso.unverified': 'The identity provider’s response could not be verified.',
  'refusal.sso.noEmail': 'The identity provider did not send an email address, so there is nobody to sign in.',
  'refusal.sso.deactivated': 'That account has been deactivated in this workspace.',
  'refusal.sso.addressTaken': 'Your provider says your address is now {email}, but another account in this workspace already uses it. An administrator has to sort that out before you can sign in.',
  'refusal.sso.noAccount': 'You have no account in this workspace, and it does not create accounts at sign-in. Ask an administrator to invite you.',
  'refusal.sso.ambiguousWorkspace': 'More than one workspace here trusts that identity provider, and a sign-in it started on its own does not say which one is meant. Start from the sign-in page instead.',
  'refusal.session.alreadyEnded': 'That sign-in is no longer active, so there is nothing to revoke.',
  'refusal.stage.unknownField': 'A deal has no field called {fields}, so a stage requiring it could never be left.',
  'refusal.stage.exitCriteria': '{stage} needs {fields} before a deal can move on.',
  'refusal.reason.needsName': 'A reason needs a name.',
  'refusal.reason.needsOutcome': 'Say whether this reason explains a win or a loss.',
  'refusal.reason.duplicate': '{label} is already a reason for that ending.',
  'refusal.reason.wrongEnding': 'That reason explains a different ending from the one this deal is being closed with.',
  'refusal.competitor.needsName': 'A competitor needs a name.',
  'refusal.competitor.duplicate': '{name} is already on the competitor list.',
  'refusal.score.badCondition': 'That condition does not work on a lead: {problems}',
  'refusal.score.needsName': 'A scoring rule needs a name a rep will understand.',
  'refusal.score.needsCondition': 'A scoring rule needs a condition.',
  'refusal.score.needsPoints': 'A scoring rule needs a whole number of points. Negative is allowed.',
  'refusal.score.duplicate': '{label} is already a scoring rule.',
  'refusal.score.badDecay': 'Cooling is a whole number of points per week, between 0 and 100.',
  'refusal.notFound': '{nounKey} was not found. It may have been deleted, or you may be following an old link.',
  'noun.tenant': 'This workspace',
  'noun.stage': 'That stage',
  'noun.deal': 'That deal',
  'noun.pricelist': 'That price list',
  'noun.account': 'That company',
  'refusal.deal.contactRole': 'A contact’s role on a deal has to be one this product can report on.',
  'refusal.deal.teamRole': 'A team role has to be one this product can report on.',
  'refusal.deal.splitTotal': 'The shares add up to {total}%, and a deal is split a hundred percent or not at all.',
  'noun.contact': 'That contact',
  'noun.person': 'That person',
  'noun.product': 'That product',
  'noun.reason': 'That reason',
  'noun.competitor': 'That competitor',
  'noun.scoreRule': 'That scoring rule',
  'refusal.lastAdmin': 'That would leave nobody able to manage people. Give somebody the Administrator set first.',
  'refusal.currencySelf': 'A currency is always worth one of itself.',
  'refusal.currencyCode': 'A currency is a three-letter code, like AED or USD.',
  'refusal.currencyOwn': '{code} is this workspace\u2019s own currency, so it cannot be removed. Change the workspace currency first.',
  'refusal.rateShape': 'A rate is a positive number, like 3.6725.',
  'refusal.objectNoOwner': '{plural} have no owner, so they cannot be private. Choose who may see them instead.',
  'refusal.sharingAccess': 'Choose how much access this grants: read, or read and write.',
  'refusal.criteriaGrantee': 'Choose exactly one team or one role for the rule to share with.',
  'refusal.formulaCycle': 'This formula depends on itself: {cycle}.',
  'refusal.apiName': 'An API name starts with a letter, uses lower-case letters, numbers and underscores, and is between three and forty characters.',
  'permissions.title': 'Permissions',
  'permissions.body': 'What somebody may do. Six sets ship with the workspace and cover nearly everybody.',
  'permissions.preset': 'Ships with the workspace',
  'permissions.custom': 'Custom',
  'permissions.holders': '{count} people hold this',
  'permissions.holders.none': 'Nobody holds this',
  'permissions.who': 'Who holds what',
  'permissions.none': 'No permission sets',
  'permissions.edit': 'Change sets for {name}',
  'permissions.change': 'Change',
  'permissions.editSet': 'Change what it allows',
  'permissions.save': 'Save',
  'permissions.cancel': 'Cancel',
  'permissions.diff.title': 'What changes',
  'permissions.diff.gains': 'Gains',
  'permissions.diff.losses': 'Loses',
  'permissions.diff.unchanged': 'Nothing changes. These sets grant the same as the ones held now.',
  'permissions.diff.checking': 'Working out what changes',
  'permissions.create': 'Create custom set',
  'permissions.deactivate': 'Deactivate',
  'permissions.reactivate': 'Bring back',
  'permissions.status.deactivated': 'Deactivated',
  'account.region': 'Region',
  'account.locale': 'Dates and numbers',
  'account.timezone': 'Time zone',
  'account.numerals': 'Digits',
  'account.numerals.latn': '1234',
  'account.numerals.arab': '١٢٣٤',
  'account.region.example': 'Today reads {date}, and a total reads {amount}.',
  'account.density': 'Interface',
  'account.density.simple': 'Simple',
  'account.density.advanced': 'Advanced',
  'account.trial': 'Trial ends {when}',

  // --- provider sandbox ---------------------------------------------------
  'sandbox.banner': 'Sandbox mode: messages are recorded here instead of being sent.',
  'sandbox.view': 'See what would have been sent',

  // --- home, records and fields -------------------------------------------
  'home.title': 'Home',
  'home.lede': 'What is in play, what needs doing, and what moved since you were last here.',
  'home.kpi.weighted': 'Weighted',
  'home.kpi.weighted.basis': 'By stage probability',
  'home.kpi.openPipeline': 'Open pipeline',
  'home.kpi.dealCount': '{count} open deals',
  'home.kpi.noDeals': 'No open deals yet',
  'home.myDay.open': 'Open',
  'home.myDay.openWork': 'Open My work',
  'home.pipeline.covers': 'Totals cover {counted} of {count} deals, in {currency}.',
  'home.myDay': 'My day',
  'home.myDay.empty': 'Nothing scheduled for today',
  'home.myDay.all': 'See all my work',
  'home.pipeline': 'Pipeline',
  'home.pipeline.open': '{count} open, worth {amount}',
  'home.pipeline.empty': 'No pipeline set up yet',
  'home.pipeline.board': 'Open the board',
  'home.recent': 'Recently updated',
  'home.recent.empty': 'Nothing has been updated yet',
  'home.checklist': 'Getting started',
  'home.checklist.done': '{done} of {total} done',
  'home.checklist.complete': 'Everything here is set up.',
  'home.checklist.hide': 'Hide this',
  'home.checklist.show': 'Show the getting-started list',
  'home.checklist.collapse': 'Collapse',
  'home.checklist.expand': 'Expand',
  'home.checklist.hidden': 'Put away. It comes back whenever you want it.',
  'home.lists.new': 'New',
  'home.widget.failed': 'This part could not be loaded',
  'home.widget.retry': 'Try again',
  'record.mode.simple': 'Simple',
  'record.mode.advanced': 'Show all fields',
  'record.showAll': 'Show {count} more',
  'record.omitted.fieldPermission': '{count} hidden by permissions',
  'record.omitted.advancedOnly': '{count} shown in Advanced',
  'record.omitted.overBudget': '{count} more fields',
  'record.omitted.noSuchField': '{count} fields no longer exist',
  'record.edit': 'Edit',
  'record.conflict.title': 'Somebody else edited this.',
  'record.conflict.body': 'Everything else you changed was saved. Choose which value to keep for the fields below.',
  'record.conflict.keepYours': 'Keep mine',
  'record.conflict.keepTheirs': 'Use theirs',
  'record.conflict.resolved': 'Resolved',
  'record.saved.count': '{count} fields saved',
  'field.yes': 'Yes',
  'field.no': 'No',
  'field.download': 'Download',
  'field.more': '+{count} more',
  'field.choose': 'Choose',
  'field.clear': 'Clear',
  'deal.competitors': 'Who else is in it',
  'deal.competitors.none': 'Nobody else named yet',
  'deal.competitors.add': 'Add a competitor',
  'deal.competitors.newName': 'Someone new',
  'deal.competitors.outcomeFor': 'What happened against {name}',
  'deal.competitors.unknown': 'Still open',
  'deal.competitors.won-against': 'We won against them',
  'deal.competitors.lost-to': 'We lost to them',
  'deal.competitors.withdrew': 'They withdrew',
  'deal.stage.checklist': 'Before this deal leaves {stage}',
  'deal.stage.met': 'done',
  'deal.stage.needed': 'still needed',
  'deal.stage.refused': 'That move was refused',
  'deal.stage': 'Stage',
  'deal.amount': 'Amount',
  'deal.closeDate': 'Close date',

  'metric.trend.up': 'Up {change}',
  'metric.trend.down': 'Down {change}',
  'metric.trend.flat': 'No change',


  // --- record pages -------------------------------------------------------
  'record.timeline': 'Timeline',
  'record.timeline.all': 'All',
  'record.timeline.empty': 'No activity yet',
  'record.timeline.emptyAction': 'Log the first one',
  'record.timeline.byAgent': 'Done by an agent',
  'record.timeline.bySystem': 'Done automatically',
  'record.timeline.why': 'Why did this happen?',
  'record.timeline.expand': 'Show more',
  'record.timeline.collapse': 'Show less',
  'record.timeline.loadMore': 'Load older',
  'record.timeline.showing': '{shown} of {total}',
  'record.details': 'Details',
  'record.related': 'Related',
  'record.follow': 'Follow',
  'record.following': 'Following',
  'record.followers': '{count} following',
  'record.action.call': 'Call',
  'record.action.email': 'Email',
  'record.action.whatsapp': 'WhatsApp',
  'record.action.note': 'Log a note',
  'record.note.subject': 'What happened',
  'record.note.body': 'Anything worth remembering',
  'record.note.save': 'Save note',
  'record.note.cancel': 'Cancel',
  'record.action.schedule': 'Schedule a step',
  'record.more': 'More',
  'record.notFound': 'That record could not be found',
  'record.notFound.body': 'It may have been deleted, or it may belong to a colleague whose records you cannot see.',
  'consent.optedIn': 'Opted in',
  'consent.optedOut': 'Opted out',
  'consent.unknown': 'Never asked',
  'consent.channel.email': 'Email',
  'consent.channel.sms': 'SMS',
  'consent.channel.whatsapp': 'WhatsApp',
  'consent.channel.call': 'Calls',
  'consent.channel.post': 'Post',
  'activity.type.note': 'Note',
  'activity.type.call': 'Call',
  'activity.type.email': 'Email',
  'activity.type.meeting': 'Meeting',
  'activity.type.whatsapp': 'WhatsApp',
  'activity.type.sms': 'SMS',
  'activity.type.next-step': 'Next step',


  // --- leads, conversion and companies ------------------------------------
  'lead.score': 'Score',
  'lead.scoring.title': 'How leads are scored',
  'lead.scoring.body': 'Points a lead earns, and loses. A rep who cannot argue with the number will ignore it, so every rule shows up in the breakdown on the lead.',
  'lead.scoring.label': 'What to call it',
  'lead.scoring.when': 'When this has a value',
  'lead.scoring.points': 'Points (negative is allowed)',
  'lead.scoring.decay': 'A lead nobody touches',
  'lead.scoring.decay.none': 'Does not cool',
  'lead.scoring.decay.perWeek': 'Loses {points} a week',
  'lead.scoring.rescore': 'Apply to every lead',
  'lead.scoring.rescored': '{count} leads rescored',
  'lead.status': 'Status',
  'lead.source': 'Source',
  'lead.created': 'Added',
  'lead.empty.title': 'No leads yet',
  'lead.empty.body': 'A lead is someone who has shown interest but is not yet a customer. They arrive from forms, imports and campaigns.',
  'lead.scoreBreakdown': 'Why this score',
  'lead.convert': 'Convert',
  'lead.converted': 'Converted',
  'lead.converted.body': 'This lead became a company, a person and a deal.',
  'lead.undo': 'Undo the conversion',
  'lead.undo.until': 'You can undo this until {when}',
  'lead.undo.expired': 'The undo window has passed',
  'convert.title': 'Convert this lead',
  'convert.body': 'Here is exactly what will be created. Nothing is created until you confirm.',
  'convert.company': 'Company',
  'convert.person': 'Person',
  'convert.deal': 'Deal',
  'convert.willCreate': 'Will be created',
  'convert.willLink': 'Already exists - will be used',
  'convert.noDeal': 'No deal will be created',
  'convert.createDeal': 'Create a deal',
  'convert.dealName': 'Deal name',
  'convert.dealAmount': 'Amount',
  'convert.confirm': 'Create all three',
  'convert.confirmTwo': 'Create both',
  'convert.reversible': 'You can undo this for 24 hours.',
  'convert.failed': 'Nothing was created',
  'account.openPipeline': 'Open pipeline',
  'account.wonRevenue': 'Closed revenue',
  'account.people': 'People',
  'record.delete': 'Delete',
  'record.deleted': '{name} deleted',
  'record.deleted.body': 'It is in the recycle bin. Nobody else can see it.',
  'record.undo': 'Undo',
  'record.restored': '{name} is back',
  'record.delete.confirm': 'Delete {name}? It goes to the recycle bin and can be restored.',
  'record.alreadyDeleted': 'Somebody else deleted this first.',
  'account.thisCompany': 'This company',
  'account.group': 'Group total',
  'account.group.context': 'Across {count} companies you can see',
  'account.pricelist': 'Price list',
  'account.pricelist.standard': 'Standard pricing',
  'account.pricelist.help': 'Quotes for this company are priced from this list. Standard pricing uses the catalogue and any rules that apply to everyone.',
  'account.hierarchy': 'Group structure',
  'account.openCases': 'Open cases',
  'account.noSubsidiaries': 'This company has no subsidiaries.',
  'account.hierarchy.scope': 'Subsidiaries you are not allowed to open are not counted here.',
  'account.subsidiaries': 'Subsidiaries',
  'account.empty.title': 'No companies yet',
  'account.empty.body': 'Companies group the people you work with and the deals you run with them.',


  // --- deals and quotes ---------------------------------------------------
  'deal.board': 'Board',
  'deal.list': 'List',
  'deal.people.theirs': 'Who we are selling to',
  'deal.people.ours': 'Who is working it',
  'deal.people.noContacts': 'Nobody named on their side yet',
  'deal.people.noTeam': 'Just the owner',
  'deal.people.total': 'Shares add up to',
  'deal.people.roleFor': 'What {name} is to this deal',
  'deal.people.decision-maker': 'Decision maker',
  'deal.people.champion': 'Champion',
  'deal.people.blocker': 'Blocker',
  'deal.people.influencer': 'Influencer',
  'deal.people.user': 'End user',
  'deal.people.technical': 'Technical',
  'deal.people.owner': 'Owner',
  'deal.people.contributor': 'Contributor',
  'deal.people.specialist': 'Specialist',
  'deal.people.manager': 'Manager',
  'deal.people.introducer': 'Introducer',
  'deal.pipeline': 'Pipeline',
  'deal.emptyColumn': 'Drag a deal here when it reaches {column}.',
  'deal.moveCard': 'Move to another stage',
  'deal.moveTo': 'Move {card} out of {column}',
  'deal.moving': 'Moving',
  'deal.reverted': 'Put back: {reason}',
  'deal.empty.title': 'No deals yet',
  'deal.empty.body': 'A deal is a piece of revenue you are working towards, with an amount and a date.',
  'deal.health.healthy': 'On track',
  'deal.health.watch': 'Watch',
  'deal.health.atRisk': 'At risk',
  'deal.health.won': 'Won',
  'deal.health.lost': 'Lost',
  'deal.health.stalled': 'Stalled',
  'deal.health.why': 'Why this score',
  'deal.health.factors': 'What is behind it',
  'deal.closeReason': 'Why did it close this way?',
  'deal.closeReason.needed': 'Closing a deal needs a reason.',
  'quote.title': 'Quote',
  'quote.new': 'New quote',
  'quote.lines': 'Lines',
  'quote.description': 'Description',
  'quote.quantity': 'Qty',
  'quote.unitPrice': 'Unit price',
  'quote.discount': 'Disc %',
  'quote.margin': 'Margin',
  'quote.margin.unknown': 'No cost entered, so the margin is unknown',
  'quote.margin.hidden': 'Margin is not shown to you',
  'quote.lineTotal': 'Total',
  'quote.optional': 'Optional',
  'quote.addLine': 'Add a line',
  'quote.removeLine': 'Remove {description}',
  'quote.keyboardHint': 'Tab moves across, Enter adds a row, Ctrl+Backspace removes one.',
  'quote.subtotal': 'Subtotal',
  'quote.optionalTotal': 'Optional lines, if taken',
  'quote.discountLabel': 'Discount',
  'quote.total': 'Total',
  'quote.readOnly': 'This quote has been sent, so its lines cannot be changed.',
  'quote.send': 'Send the quote',
  'quote.sent': 'Sent',
  'quote.preview': 'Preview',
  'quote.validUntil': 'Valid until',
  'quote.empty.title': 'No quotes on this deal',
  'quote.empty.body': 'A quote lists what you are offering and at what price.',

  'deal.openOnly': 'Open deals only',

  'quote.rowActions': 'Row actions',

  'deal.noTotal': '—',

  'home.kpi.someCurrencies': '{counted} of {count} open deals, in this workspace\u2019s currency',


  // --- setup ---------------------------------------------------------------
  'setup.title': 'Setup',
  'setup.search': 'Search settings',
  'setup.category.objects': 'Objects and fields',
  'setup.category.modules': 'Modules',
  'setup.category.data': 'Data',
  'setup.category.plan': 'Plan and usage',
  'setup.modules.title': 'Modules',
  'setup.modules.body': 'Turn parts of the product on or off for this workspace.',
  'setup.impact.title': 'Before you switch this off',
  'setup.impact.titleOn': 'Before you switch this on',
  'setup.impact.screens': '{count} screens will disappear',
  'setup.impact.operations': '{count} API operations will stop answering',
  'setup.impact.tools': '{count} agent tools will leave the registry',
  'setup.impact.features': '{count} features go with it',
  'setup.impact.dependents': 'These also depend on it: {modules}',
  'setup.impact.retained': 'Nothing is deleted. These records stay exactly where they are:',
  'setup.impact.confirmOff': 'Switch off {module}',
  'setup.impact.confirmOn': 'Switch on {module}',
  'setup.impact.core': 'This is a core module and cannot be switched off.',
  'setup.impact.locked': 'Not included in this plan.',
  'setup.layout.title': 'Layout',
  'setup.layout.available': 'Available fields',
  'setup.layout.canvas': 'On the page',
  'setup.layout.counter': '{count} fields on the page',
  'setup.layout.overBudget': 'Simple mode shows the first {budget}. {over} will only appear in Advanced.',
  'setup.layout.addField': 'Add {field}',
  'setup.layout.removeField': 'Remove {field}',
  'setup.layout.empty': 'Nothing on the page yet',
  'setup.field.new': 'New field',
  'setup.field.type': 'What kind of field?',
  'setup.field.name': 'Name',
  'setup.field.key': 'API name',
  'setup.field.preview': 'This is how it will look',
  'setup.field.create': 'Create the field',
  'setup.import.title': 'Import',
  'setup.import.step.upload': 'Choose a file',
  'setup.import.step.map': 'Match the columns',
  'setup.import.step.check': 'Check before writing',
  'setup.import.step.done': 'Done',
  'setup.import.dryRun': 'Check the file',
  'setup.import.apply': 'Import {count} rows',
  'setup.import.valid': '{count} of {total} rows are ready',
  'setup.import.errors': '{count} rows have a problem',
  'setup.import.download': 'Download the errors',
  'setup.import.undo': 'Undo this import',
  'setup.import.undoUntil': 'You can undo this until {when}',
  'setup.import.undone': '{removed} records removed, {kept} left alone because somebody edited them',
  'setup.import.history': 'Recent imports',
  'setup.import.nothing': 'No imports yet',
  'setup.plan.title': 'Plan and usage',
  'setup.plan.current': 'Your plan',
  'setup.plan.locked': 'Needs {plan}',
  'setup.plan.included': 'Included',
  'setup.plan.usage': 'Usage',
  'setup.plan.upgrade': 'Change plan',

  'action.on': 'On',
  'action.off': 'Off',

  'setup.plan.lockedCount': 'Not in this plan',


  // --- rules, pricing and variants ---------------------------------------
  'rule.all': 'All of',
  'rule.any': 'Any of',
  'rule.none': 'None of',
  'rule.addCondition': 'Add a condition',
  'rule.addGroup': 'Add a group',
  'rule.remove': 'Remove',
  'rule.field': 'Field',
  'rule.operator': 'Operator',
  'rule.value': 'Value',
  'rule.and': 'and',
  'rule.or': 'or',
  'rule.summary': '{count} conditions produce {outcome}',
  'rule.matching': '{count} matching',
  'rule.matchingUnknown': 'Not counted yet',
  'rule.sampleRows': 'For example',
  'rule.tooDeep': 'Rules nest {max} levels deep',
  'rule.op.eq': 'is',
  'rule.op.ne': 'is not',
  'rule.op.lt': 'is less than',
  'rule.op.lte': 'is at most',
  'rule.op.gt': 'is more than',
  'rule.op.gte': 'is at least',
  'rule.op.between': 'is between',
  'rule.op.contains': 'contains',
  'rule.op.startsWith': 'starts with',
  'rule.op.in': 'is one of',
  'rule.op.notIn': 'is none of',
  'rule.op.isSet': 'has a value',
  'rule.op.isNotSet': 'has no value',
  'pricelist.title': 'Pricing rules',
  'pricelist.rules': 'Rules, in priority order',
  'pricelist.preview': 'What a customer would pay',
  'pricelist.product': 'Product',
  'pricelist.customer': 'Customer',
  'pricelist.quantity': 'Quantity',
  'pricelist.winning': 'Winning rule',
  'pricelist.listPrice': 'List price',
  'pricelist.overlap': '{name} also matches, but {winner} has the lower priority number and wins.',
  'pricelist.empty.title': 'No pricing rules yet',
  'pricelist.empty.body': 'Without a rule every product sells at its list price.',
  'pricelist.priority': 'Priority',
  'pricelist.newRule': 'Add a rule',
  'variant.title': 'Variants',
  'variant.attributes': 'Attributes',
  'variant.generated': '{count} variants',
  'variant.willGenerate': 'This would make {count} variants.',
  'variant.warn': 'That is more than {threshold}. Check the attributes before generating.',
  'variant.generate': 'Generate them',
  'variant.empty.title': 'No attributes yet',
  'variant.empty.body': 'Attributes like size and colour generate a variant for each combination.',
  'variant.sku': 'SKU',
  'variant.adjustment': 'Price adjustment',
  'variant.active': 'Active',


  // --- flows and segments -------------------------------------------------
  'flow.palette': 'Steps',
  'flow.canvas': 'The flow',
  'flow.addStep': 'Add {label}',
  'flow.removeStep': 'Remove {label}',
  'flow.step': 'Step {sequence}',
  'flow.tookBranch': 'Took {branch}',
  'flow.notReached': 'Not reached',
  'flow.input': 'Saw',
  'flow.output': 'Decided',
  'flow.atThisStep': '{count} here',
  'flow.problems': 'Before this can be switched on',
  'flow.activate': 'Switch on',
  'flow.deactivate': 'Pause',
  'flow.draft': 'Draft',
  'flow.active': 'On',
  'flow.paused': 'Paused',
  'flow.test': 'Test against a record',
  'flow.testRun': 'Run the test',
  'flow.runs': 'Runs',
  'flow.run.empty': 'This has not run yet',
  'flow.run.replay': 'Run it again',
  'flow.run.outcome': 'Outcome',
  'flow.run.steps': '{count} steps',
  'flow.run.waitingUntil': 'Waiting until {when}',
  'flow.run.left': 'Left the journey: {reason}',
  'flow.empty.title': 'Nothing here yet',
  'flow.empty.body': 'A flow starts with a trigger and runs until something stops it.',
  'segment.title': 'Audience',
  'segment.list.title': 'Audiences',
  'segment.list.new': 'New audience',
  'segment.list.name': 'Audience',
  'segment.list.count': 'People',
  'segment.list.countedAt': 'Counted',
  'segment.list.neverCounted': 'Not counted yet',
  'segment.list.recount': 'Count now',
  'segment.list.empty.title': 'No audiences saved yet',
  'segment.list.empty.body': 'An audience is everybody matching a set of conditions. Campaigns and journeys send to one.',
  'segment.members': '{count} people',
  'segment.sample': 'For example',
  'segment.empty.title': 'No audience yet',
  'segment.empty.body': 'An audience is everybody matching a set of conditions.',
  'chatbot.title': 'Chat script',
  'chatbot.publish': 'Publish',
  'chatbot.blocked': 'These would stop it working:',
  'chatbot.test': 'Try it',

  'segment.membersLabel': 'People in this audience',


  'revenue.title': 'Recurring revenue',
  'revenue.subscriptions': 'Subscriptions',
  'revenue.mrr': 'Monthly recurring revenue',
  'revenue.arr': 'Annual recurring revenue',
  'revenue.asAt': 'As at',
  'revenue.period': 'Period',
  'revenue.from': 'From',
  'revenue.to': 'To',
  'revenue.activeCount': '{count} paying',
  'revenue.statusLabel': 'Status',
  'revenue.movement': 'Movement',
  'revenue.chart': 'How recurring revenue moved over the period',
  'revenue.opening': 'Opening',
  'revenue.closing': 'Closing',
  'revenue.new': 'New',
  'revenue.expansion': 'Expansion',
  'revenue.reactivation': 'Came back',
  'revenue.contraction': 'Contraction',
  'revenue.churn': 'Churn',
  'revenue.increase': 'adds to the total',
  'revenue.decrease': 'takes away from the total',
  'revenue.drillThrough': 'See the subscriptions behind {step}',
  'revenue.nothingMoved': 'Nothing moved in this period.',
  'revenue.doesNotBalance': 'These figures do not add up: the movements miss the closing total by {difference}. The chart is not drawn, because a waterfall that does not balance looks exactly like one that does.',
  'revenue.empty.title': 'No recurring revenue yet',
  'revenue.empty.body': 'Figures appear here once a subscription exists. Nothing is estimated.',
  'revenue.movements.empty': 'Nothing changed between these two dates.',
  'revenue.subscription': 'Subscription',
  'revenue.plan': 'Plan',
  'revenue.quantity': 'Quantity',
  'revenue.unitPrice': 'Unit price',
  'revenue.startedOn': 'Started',
  'revenue.account': 'Company',
  'revenue.status.active': 'Active',
  'revenue.status.paused': 'Paused',
  'revenue.status.cancelled': 'Cancelled',
  'revenue.status.not-started': 'Not started',
  'revenue.history': 'Plan history',
  'revenue.historyNote': 'Every change is an entry with the date it takes effect. Nothing here is ever edited.',
  'revenue.effectiveOn': 'Takes effect',
  'revenue.recordedAt': 'Recorded',
  'revenue.reason': 'Reason',
  'revenue.superseded': 'Superseded by a correction',
  'revenue.corrects': 'Corrects entry {id}',
  'revenue.recordChange': 'Record a change',
  'revenue.changeKind': 'What happened',
  'revenue.newMrr': 'New monthly value',
  'revenue.save': 'Record it',
  'revenue.cancel': 'Cancel',
  'revenue.reasonRequired': 'Say why this is ending.',
  'revenue.effectiveHint': 'The date it takes effect, which is not always today.',
  'revenue.kind.started': 'Started',
  'revenue.kind.upgraded': 'Upgraded',
  'revenue.kind.downgraded': 'Downgraded',
  'revenue.kind.quantity-changed': 'Quantity changed',
  'revenue.kind.price-changed': 'Price changed',
  'revenue.kind.paused': 'Paused',
  'revenue.kind.resumed': 'Resumed',
  'revenue.kind.cancelled': 'Cancelled',
  'revenue.kind.reactivated': 'Reactivated',
  'revenue.kind.corrected': 'Corrected',
  'revenue.subscription.empty.title': 'No subscriptions yet',
  'revenue.subscription.empty.body': 'A subscription records what a customer pays and, from then on, every change to it.',


  'report.title': 'Report',
  'report.builder': 'Report builder',
  'report.source': 'What this is about',
  'report.view': 'Through a saved view',
  'report.view.none': 'No view',
  'report.fields': 'Fields',
  'report.groupBy': 'Group rows by',
  'report.summarise': 'Summarise',
  'report.filters': 'Filters',
  'report.chart': 'Show as',
  'report.preview': 'Preview',
  'report.livePreview': 'This updates as you change it. There is no run step.',
  'report.name': 'Name',
  'report.save': 'Save this report',
  'report.saved': 'Saved',
  'report.visibility': 'Who can open it',
  'report.visibility.private': 'Only me',
  'report.visibility.workspace': 'Everyone in this workspace',
  'report.records': 'Records',
  'report.group': 'Group',
  'report.value': 'Value',
  'report.rows': 'The records behind it',
  'report.rowsTruncated': 'Showing the first page. The figures above cover all of them.',
  'report.empty': 'Nothing matched these filters.',
  'report.drillThrough': 'See the records in {segment}',
  'report.noDrillThrough': 'No record list for this group.',
  'report.widerThanRows': 'These figures include {withheld} records you are not able to open. The list below shows only the ones you can.',
  'report.problems': 'This cannot run yet:',
  'report.lastValid': 'Showing the last version that worked.',
  'report.add': 'Add',
  'report.remove': 'Remove',
  'report.fn.count': 'How many',
  'report.fn.sum': 'Total',
  'report.fn.avg': 'Average',
  'report.fn.min': 'Lowest',
  'report.fn.max': 'Highest',
  'report.chart.table': 'Table',
  'report.chart.bar': 'Bars',
  'report.chart.column': 'Columns',
  'report.chart.line': 'Line',
  'report.chart.donut': 'Share',
  'report.chart.funnel': 'Funnel',
  'dashboard.title': 'Dashboard',
  'dashboard.empty.title': 'Nothing on this dashboard yet',
  'dashboard.empty.body': 'A dashboard is a set of saved questions. Add the first one, or start from a template for your role.',
  'dashboard.widgetFailed': 'This widget could not be loaded.',
  'dashboard.openReport': 'Open the full report',
  'dashboard.pick': 'Dashboard',


  'campaign.title': 'Campaigns',
  'campaign.new': 'New campaign',
  'campaign.name': 'Name',
  'campaign.channel': 'Channel',
  'campaign.status': 'Status',
  'campaign.audience': 'Audience',
  'campaign.content': 'Content',
  'campaign.owner': 'Owner',
  'campaign.sentAt': 'Sent',
  'campaign.members': 'People',
  'campaign.sent': 'Reached',
  'campaign.refused': 'Not sent',
  'campaign.opened': 'Opened',
  'campaign.clicked': 'Clicked',
  'campaign.bounced': 'Bounced',
  'campaign.performance': 'Results',
  'campaign.tab.members': 'People',
  'campaign.tab.content': 'Content',
  'campaign.tab.attribution': 'Where it led',
  'campaign.send': 'Send it',
  'campaign.sending': 'Sending',
  'campaign.reachPreview': '{reachable} of {total} can be reached',
  'campaign.whyNot': 'Why the rest will not receive it',
  'campaign.refusal.suppressed': 'Asked to stop, or bounced',
  'campaign.refusal.optedOut': 'Said no to this channel',
  'campaign.refusal.noConsent': 'Never asked',
  'campaign.refusal.noAddress': 'No address on record',
  'campaign.refusalCount': '{count} people',
  'campaign.confirmSend': 'Send to {count} people',
  'campaign.confirmSendBody': 'This cannot be taken back once it goes.',
  'campaign.needsAudience': 'Choose an audience first.',
  'campaign.needsContent': 'Choose what to send first.',
  'campaign.empty.title': 'No campaigns yet',
  'campaign.empty.body': 'A campaign sends one piece of content to one audience, and records what happened to each person.',
  'campaign.noResults': 'Nothing has been sent yet.',
  'content.title': 'Content',
  'content.email': 'Email',
  'content.landingPage': 'Landing page',
  'content.article': 'Article',
  'content.subject': 'Subject line',
  'content.preheader': 'Preview text',
  'content.save': 'Save',
  'content.saved': 'Saved',
  'content.unsaved': 'Not saved',
  'content.retry': 'Try saving again',
  'content.blocks': 'Blocks',
  'content.canvas': 'Content',
  'content.settings': 'Block settings',
  'content.preview': 'Preview',
  'content.preview.desktop': 'Desktop',
  'content.preview.mobile': 'Phone',
  'content.preview.dark': 'Dark',
  'content.testSend': 'Send myself a test',
  'content.missingTokens': 'Some of these have no value for this person.',
  'content.missingTokensHelp': 'Give each a fallback, so nobody is greeted by name when there is no name.',
  'content.empty': 'Nothing here yet. Add a block to start.',
  'content.problems': 'Before this can go out:',
  'content.selectBlock': 'Choose a block to edit it',
  'content.moveUp': 'Move up',
  'content.moveDown': 'Move down',
  'content.remove': 'Remove',
  'content.words': 'Words',
  'content.file': 'File',
  'content.description': 'Description',
  'content.link': 'Link',
  'content.align': 'Alignment',
  'content.align.start': 'Left',
  'content.align.center': 'Centre',
  'content.align.end': 'Right',
  'content.block.text': 'Text',
  'content.block.heading': 'Heading',
  'content.block.image': 'Image',
  'content.block.button': 'Button',
  'content.block.divider': 'Divider',
  'content.block.spacer': 'Space',
  'content.block.columns': 'Columns',
  'content.block.quote': 'Quote',
  'content.block.video': 'Video',
  'content.block.unsubscribe': 'Unsubscribe footer',


  'chat.title': 'Live chat',
  'chat.queue.waiting': 'Waiting',
  'chat.queue.active': 'Active',
  'chat.queue.mine': 'Mine',
  'chat.waitingFor': 'Waiting {duration}',
  'chat.noConversations': 'No conversations waiting.',
  'chat.widgetStatus': 'The chat widget is switched on.',
  'chat.settings': 'Chat settings',
  'chat.pick': 'Choose a conversation',
  'chat.reply': 'Reply',
  'chat.send': 'Send',
  'chat.claim': 'Take this conversation',
  'chat.end': 'Close the conversation',
  'chat.ended': 'This conversation was closed.',
  'chat.abandoned': 'The visitor closed their window.',
  'chat.abandonedHelp': 'Nothing more will reach them here. Your draft is kept.',
  'chat.draftKept': 'Your draft was kept.',
  'chat.author.visitor': 'Visitor',
  'chat.author.operator': 'You',
  'chat.author.bot': 'Chatbot',
  'chat.author.system': 'System',
  'chat.context': 'About this visitor',
  'chat.context.location': 'Where they are',
  'chat.context.unknownLocation': 'Not known',
  'chat.context.firstSeen': 'First seen',
  'chat.context.match': 'Matched record',
  'chat.context.noMatch': 'Not matched to anyone yet',
  'chat.context.convert': 'Make a lead from this visitor',
  'chat.context.pages': 'Pages they looked at',
  'chat.context.noPages': 'No pages recorded.',
  'chat.canned': 'Canned replies',
  'chat.cannedInsert': 'Insert {shortcut}',


  'portal.poweredBy': 'Sent by {workspace}',
  'portal.content': 'This page',
  'portal.requestNewLink': 'Send me a new link',
  'portal.refusal.invalid.title': 'This link does not work',
  'portal.refusal.invalid.body': 'Check that you copied the whole address from the message you received.',
  'portal.refusal.expired.title': 'This link has expired',
  'portal.refusal.expired.body': 'Links stop working after a while. We can send you a new one.',
  'portal.refusal.revoked.title': 'This link was withdrawn',
  'portal.refusal.revoked.body': 'Somebody at the company that sent it has taken it back. Contact them if you still need access.',
  'portal.refusal.wrong-surface.title': 'This link is for a different page',
  'portal.refusal.wrong-surface.body': 'Open it from the message you received rather than editing the address.',
  'portal.newLinkSent': 'If that address is on file, a new link is on its way.',
  'portal.field.full_name': 'Name',
  'portal.field.email': 'Email',
  'portal.field.title': 'Job title',
  'portal.field.number': 'Reference',
  'portal.field.status': 'Status',
  'portal.field.total': 'Total',
  'portal.field.currency': 'Currency',
  'portal.field.valid_until': 'Valid until',
  'preferences.title': 'Your preferences',
  'preferences.intro': 'Choose what you would like to hear about, and how.',
  'preferences.channel.email': 'Email',
  'preferences.channel.sms': 'Text message',
  'preferences.channel.whatsapp': 'WhatsApp',
  'preferences.channel.call': 'Phone call',
  'preferences.neverAsked': 'Never asked',
  'preferences.stopAll': 'Stop everything',
  'preferences.stopAllHelp': 'One click. We will stop contacting you on every channel.',
  'preferences.stopped': 'We have stopped. Nothing further will be sent.',
  'preferences.saved': 'Saved',
  'portal.quotes': 'Your quotes',
  'portal.noQuotes': 'There is nothing here yet.',
  'portal.yourDetails': 'Your details',
  'booking.title': 'Book a time',
  'booking.intro': 'Choose a time that suits you.',
  'booking.noSlots': 'There are no times available at the moment.',


  'booking.note': 'What would you like to talk about?',
  'booking.taken': 'Somebody took that time while you were choosing. Pick another - what you typed is still here.',
  'booking.confirmed': 'Booked. You will get a confirmation by email.',


  'agent.title': 'Agents',
  'agent.purpose': 'What it is for',
  'agent.new.title': 'New agent',
  'agent.new.body': 'An agent needs a name, a purpose and a key other things refer to it by.',
  'agent.new.name': 'Name',
  'agent.new.key': 'Key',
  'agent.new.keyHelp': 'Lower case, no spaces. Used in automations and cannot be changed later.',
  'agent.new.create': 'Create agent',
  'agent.instructions': 'Instructions',
  'agent.topics': 'Topics',
  'agent.actions': 'What it may do',
  'agent.sources': 'What it may read',
  'comments.title': 'Discussion',
  'record.action.clone': 'Start a copy',
  'record.clone.name': 'Name for the new deal',
  'record.clone.explain': 'The people, the internal team and the competitors come across. The stage, the close date and the history do not.',

  'qualification.title': 'Qualification',
  'qualification.answer': 'Answer',
  'qualification.evidence': 'What is this based on?',
  'qualification.state.met': 'Yes',
  'qualification.state.not-met': 'No',
  'qualification.state.refused': 'Would not say',
  'qualification.state.unknown': 'Not asked',

  'callQueue.title': 'Call queue',
  'callQueue.lists': 'Your lists',
  'callQueue.progress': '{done} of {total}',
  'callQueue.pick': 'Choose a list to work through.',
  'callQueue.finished': 'That list is worked out. Nothing left to ring.',
  'callQueue.subject': 'What to put on the timeline',
  'callQueue.notes': 'Anything worth keeping',
  'callQueue.skip': 'Skip',
  'callQueue.skipped': 'skipped',
  'callQueue.called': 'called',
  'callQueue.noNumber': 'No phone number on this record.',
  'callQueue.empty.title': 'No call lists yet',
  'callQueue.empty.body': 'Build one from a filtered list of people, and it keeps your place as you work through it.',

  'record.note.kind': 'What kind of entry',
  'record.note.kind.note': 'Note',
  'record.note.kind.call': 'Call',
  'record.call.outcome': 'How it ended',
  'record.call.outcome.connected': 'Spoke to them',
  'record.call.outcome.no-answer': 'No answer',
  'record.call.outcome.voicemail': 'Left a voicemail',
  'record.call.outcome.wrong-number': 'Wrong number',
  'record.call.outcome.busy': 'Engaged',
  'record.call.outcome.callback-requested': 'Asked us to call back',
  'record.call.direction': 'Which way',
  'record.call.outbound': 'We called',
  'record.call.inbound': 'They called',
  'record.call.minutes': 'Minutes',

  'comments.body': 'Between colleagues. This is not on the customer\u2019s timeline.',
  'comments.write': 'Ask a question, or use @ to pull somebody in',
  'comments.post': 'Post',
  'comments.reply': 'Reply',
  'comments.resolve': 'Mark answered',
  'comments.reopen': 'Reopen',
  'comments.edited': 'edited',
  'comments.somebody': 'Somebody',
  'comments.none': 'Nothing has been discussed on this one yet.',

  'agent.prebuilt.title': 'Ready-made agents',
  'agent.prebuilt.body': 'Each arrives switched off, with every action set to propose and its own tests to pass before it can be switched on. Read what it says before you do.',
  'agent.prebuilt.install': 'Add to this workspace',
  'agent.prebuilt.installed': 'Already here',

  'routing.model.title': 'Which model answers',
  'routing.model.body': 'Models are tried in order until one answers. This deployment’s own account is always last, so a chain that fails everywhere still gets a reply.',
  'routing.model.label': 'What you call this account',
  'routing.model.kind': 'Provider',
  'routing.model.baseUrl': 'Endpoint address (custom only)',
  'routing.model.key': 'API key',
  'routing.model.keySet': 'Key stored',
  'routing.model.keyMissing': 'No key',
  'routing.model.add': 'Add account',
  'routing.model.remove': 'Remove',
  'routing.model.ours': 'This deployment',
  'routing.model.default': 'Nothing set, so this deployment’s own model answers.',
  'routing.model.defaultModel': 'Its default model',
  'routing.model.addStep': 'Add to the chain',
  'routing.model.task.agent': 'Agents',
  'routing.model.task.draft': 'Drafting text',
  'routing.model.task.summarise': 'Summarising',

  'agent.guardrails': 'Guardrails',
  'agent.tests': 'Tests',
  'agent.source.knowledge': 'Published knowledge articles',
  'agent.source.documents': 'Text documents and snippets in the library',
  'agent.source.record.account': 'Companies, with their notes',
  'agent.source.record.contact': 'People, with their notes',
  'agent.source.record.deal': 'Deals, with their notes',
  'agent.source.record.lead': 'Leads, with their notes',
  'agent.reindex': 'Index what changed',
  'agent.index.meaning': 'Indexed {indexed}, skipped {skipped}. Matching on meaning.',
  'agent.index.words': 'Indexed {indexed}, skipped {skipped}. No embeddings key, so matching on words.',
  'agent.index.none': 'Nothing is indexed: no embeddings provider is configured.',
  'agent.preview': 'Show what it would read',
  'agent.preview.ask': 'A question somebody might ask it',
  'agent.preview.nothing': 'Nothing in these sources is close enough to that question to be worth citing.',

  'agent.testChat': 'Try it',
  'agent.ask': 'Ask something',
  'agent.send': 'Ask',
  'agent.publish': 'Switch it on',
  'agent.pause': 'Pause it',
  'agent.blocked': 'This cannot be switched on yet:',
  'agent.runTests': 'Run the tests',
  'agent.autonomy': 'Autonomy',
  'agent.autonomy.propose': 'Propose, and wait',
  'agent.autonomy.auto': 'Act without asking',
  'agent.autonomy.read-only': 'Reads only',
  'agent.locked': 'Needs {entitlement}',
  'agent.cost': 'This cost {amount}',
  'agent.tokens': '{input} in, {output} out',
  'agent.trace': 'How it got there',
  'agent.rawPayload': 'Show the raw data',
  'agent.step.thought': 'Thinking',
  'agent.step.retrieval': 'Looked up',
  'agent.step.tool-call': 'Wanted to act',
  'agent.step.answer': 'Answered',
  'agent.step.refusal': 'Declined',
  'agent.empty.title': 'No agents yet',
  'agent.empty.body': 'An agent answers questions and proposes changes. It never acts without being told, unless you say it may.',
  'agent.activity': 'Agent activity',
  'agent.activity.empty': 'Runs appear here once an agent is switched on.',
  'agent.spend': 'Spent so far',
  'agent.run': 'Run',
  'agent.invokedBy': 'Asked by',
  'agent.outcome': 'Outcome',
  'proposal.title': 'Waiting for you',
  'proposal.empty.title': 'Nothing needs your review',
  'proposal.empty.body': 'Agents propose changes here when they are set to ask first.',
  'proposal.settings': 'Agent settings',
  'proposal.why': 'Why',
  'proposal.evidence': 'What it read',
  'proposal.accept': 'Accept',
  'proposal.edit': 'Change it first',
  'proposal.dismiss': 'Dismiss',
  'proposal.dismissReason': 'Why not?',
  'proposal.dismissHelp': 'This is how the agent gets better. "No" on its own teaches nothing.',
  'proposal.save': 'Save the change',
  'proposal.accepted': 'Accepted',
  'proposal.edited': 'Changed and accepted',
  'proposal.dismissed': 'Dismissed',
  'proposal.failed': 'That could not be done: {reason}',
  'proposal.stillHere': 'It is still here, so nothing was lost.',


  'case.title': 'Cases',
  'case.queue.unassigned': 'Unassigned',
  'case.queue.mine': 'Mine',
  'case.queue.all': 'All',
  'case.number': 'Reference',
  'case.subject': 'Subject',
  'case.contact': 'Who asked',
  'case.priority': 'Priority',
  'case.status': 'Status',
  'case.assignee': 'Working on it',
  'case.unassigned': 'Nobody yet',
  'case.escalated': '{cases} cases have passed their deadline. {people} people have been told.',
  'case.escalated.one': 'One case has passed its deadline. {people} people have been told.',
  'case.escalated.nobody': '{cases} cases have passed their deadline, and there is nobody to tell.',
  'case.sla': 'Time left',
  'case.priority.low': 'Low',
  'case.priority.normal': 'Normal',
  'case.priority.high': 'High',
  'case.priority.urgent': 'Urgent',
  'case.status.new': 'New',
  'case.status.open': 'Open',
  'case.status.waiting-on-customer': 'Waiting on them',
  'case.status.resolved': 'Resolved',
  'case.status.closed': 'Closed',
  'case.sla.breached': 'Overdue by {duration}',
  'case.sla.dueSoon': 'Due in {duration}',
  'case.sla.onTrack': '{duration} left',
  'case.sla.met': 'Within the deadline',
  'case.sla.none': 'No deadline set',
  'case.sla.firstResponse': 'to reply',
  'case.sla.resolution': 'to resolve',
  'case.reply.public': 'Reply to the customer',
  'case.reply.internal': 'Add an internal note',
  'case.reply.publicHelp': 'They will receive this.',
  'case.reply.internalHelp': 'Only your team sees this.',
  'case.reply.draft': 'What you want to say',
  'case.reply.sent': 'Sent to the customer',
  'case.reply.noted': 'Noted for your team',
  'case.reply.failed': 'That did not send: {reason}',
  'case.reply.draftKept': 'Your draft is still here.',
  'case.message.public': 'Customer can see this',
  'case.message.internal': 'Internal only',
  'case.author.customer': 'Customer',
  'case.author.agent': 'Your team',
  'case.author.system': 'System',
  'case.articles': 'Might answer this',
  'case.articles.internal': 'internal',
  'case.articles.none': 'Nothing matched this subject.',
  'case.details': 'About this case',
  'case.empty.title': 'Nothing in the queue',
  'case.empty.body': 'Cases arrive here from email, the portal and chat. Set up email-to-case in Setup to start receiving them.',
  'case.take': 'Take it',
  'case.resolve': 'Mark resolved',


  // What a device kept while it had no network (F-139).
  'offline.title': 'Waiting to send',
  'offline.indicator.offline': 'Offline',
  'offline.indicator.waiting': '{count} waiting to send',
  'offline.indicator.needsYou': '{count} need you',
  'offline.indicator.sending': 'Sending',
  'offline.indicator.allSent': 'Everything is sent',
  'offline.open': 'Show what is waiting',
  'offline.empty.title': 'Nothing is waiting',
  'offline.empty.body': 'Everything you have changed is in the workspace. Anything you do without a connection will wait here until there is one.',
  'offline.kept': 'Saved on this device',
  'offline.keptBody': 'No connection. This is on your device and nobody else can see it yet.',
  'offline.state.waiting': 'Waiting for a connection',
  'offline.state.needsYou': 'Needs you',
  'offline.state.refused': 'Could not be sent',
  'offline.queuedAt': 'Kept {when}',
  'offline.sendNow': 'Try now',
  'offline.discard': 'Discard this',
  'offline.discard.confirm': 'Discard what you wrote? It has not been sent and cannot be recovered.',
  'offline.conflict.title': '{field} changed while this was waiting',
  'offline.conflict.body': 'You wrote one thing and somebody else wrote another. Choose which one the workspace keeps.',
  'offline.conflict.yours': 'Yours',
  'offline.conflict.theirs': 'Theirs',
  'offline.conflict.keepYours': 'Keep yours',
  'offline.conflict.keepTheirs': 'Keep theirs',
  'offline.onRecord': 'on {record}',
  'offline.refusedBecause': 'The workspace refused it: {reason}',

  'time.days': '{count}d',
  'time.hours': '{count}h',
  'time.minutes': '{count}m',

  // --- targets and attainment (F-053) ---------------------------------------
  'target.title': 'Targets',
  'target.body': 'What each person is measured against, and what they have actually closed. A deal that has been split is credited by its shares; a deal nobody split goes wholly to its owner.',
  'target.year': 'Financial year',
  'target.by': 'Measured by',
  'target.subject.user': 'People',
  'target.subject.team': 'Teams',
  'target.period.month': 'Month {index}',
  'target.period.quarter': 'Q{index}',
  'target.period.year': 'Full year',
  'target.period.custom': '{start} to {end}',
  'target.column.target': 'Target',
  'target.column.attained': 'Closed',
  'target.attainment': '{percent}% of target',
  'target.noTarget': 'No target agreed',
  'target.deals': '{count} deals',
  'target.set': 'Set',
  'target.remove': 'Remove target',
  'target.fiscalStart': 'The year starts in',
  'target.fiscalStart.body': 'Quarters are counted from this month. A workspace on an April year sees April to June as Q1.',
  'target.paste.title': 'Paste from a spreadsheet',
  'target.paste.body': 'One row per person: their name, then one amount per column, separated by tabs. Nothing is saved until every row matches somebody.',
  'target.paste.apply': 'Apply the paste',
  'target.paste.applied': '{count} targets set',
  'target.unattributed': '{amount} closed by nobody on this grid: {count} deals with no owner and no split. Not in any row above.',
  'target.unconvertible': 'Amounts in {currencies} are not in these figures. No exchange rate covers them.',
  'target.overlapping': '{count} people are in more than one of these teams, so the team rows add up to more than the workspace closed.',
  'target.strays': 'Agreed against periods this financial year no longer has',
  'target.scope': 'Figures cover the deals you can see.',
  'target.empty.title': 'No targets yet',
  'target.empty.body': 'A target is a number somebody is measured against for a period. Set one and attainment appears beside it.',
  'refusal.period.startMonth': 'A financial year starts in one of the twelve months, and {month} is not one of them.',
  'refusal.period.date': 'That is not a date.',
  'refusal.target.periodKind': 'A target covers a month, a quarter or a year.',
  'refusal.target.periodOrder': 'That period ends before it starts.',
  'refusal.target.negative': 'A target is an amount to reach, and {amount} is below zero.',
  'noun.target': 'That target',
  'noun.team': 'That team',
  'target.periodKind': 'Periods',
  'target.periodKind.month': 'Monthly',
  'target.periodKind.quarter': 'Quarterly',
  'target.periodKind.year': 'Yearly',
  'target.measure': 'Measured in',
  'target.measure.amount': 'Money',
  'target.measure.count': 'Deals closed',
  'target.dealsClosed': '{count} closed',
  'target.targetDeals': '{count} deals',
  'target.sharedDeals': '{count} deals reached more than one row, because a split deal counts as a whole deal to each person who worked it. The deal counts do not add up down a column.',
  'refusal.target.measure': 'A target is measured in money or in deals.',
  'refusal.target.currencyMissing': 'A target in money needs a currency.',
  'refusal.target.wholeCount': 'A target in deals is a whole number, and {amount} is not one.',
  // --- the forecast (F-052) --------------------------------------------------
  'forecast.title': 'Forecast',
  'forecast.body': 'What is expected to close, beside what the pipeline implies and what has already closed. The two are never added together: one is arithmetic, the other is a promise somebody made.',
  'forecast.category.commit': 'Commit',
  'forecast.category.best-case': 'Best case',
  'forecast.category.pipeline': 'Pipeline',
  'forecast.category.omitted': 'Omitted',
  'forecast.commitAndBestCase': 'Commit and best case',
  'forecast.weighted': 'Weighted',
  'forecast.weighted.hint': 'Every open deal times its stage probability. Omitted deals are left out.',
  'forecast.closed': 'Closed',
  'forecast.target': 'Target',
  'forecast.variance.over': '{amount} above target',
  'forecast.variance.under': '{amount} short of target',
  'forecast.variance.none': 'No target agreed',
  'forecast.open': 'Show the deals',
  'forecast.close': 'Hide the deals',
  'forecast.deal.split': '{percent}% of {amount}',
  'forecast.deal.whole': 'The whole deal',
  'forecast.undated': '{amount} of open deals have no close date, so they are in no period and in none of these figures. {count} deals.',
  'forecast.unattributed': '{amount} of open deals have no owner and no split, so they are on no row here. {count} deals.',
  'forecast.unconvertible': 'Open deals in {currencies} are not in these figures. No exchange rate covers them.',
  'forecast.drilldown.short': '{count} deals in this cell could not be converted and are not listed.',
  'forecast.noDeals': 'No open deals in this period.',
  'forecast.empty.title': 'Nothing to forecast yet',
  'forecast.empty.body': 'A forecast reads open deals against the targets people have agreed. Set the targets first, and the pipeline fills the rest in.',
  'forecast.setTargets': 'Set targets',
  'forecast.noTargets': 'Nobody on this grid has an agreed target for this year, so there is nothing to read the forecast against.',
  'forecast.scope': 'Figures cover the deals you can see.',
  'refusal.forecast.category': 'A forecast category is commit, best case, pipeline or omitted.',
  'refusal.forecast.closed': 'That deal has closed, and a closed deal is a result rather than a forecast.',
  'forecast.submit.title': 'Your number for this period',
  'forecast.submit.body': 'What you are telling your manager will close. Kept apart from what they adjust it to, so the two can be compared afterwards.',
  'forecast.submit.amount': 'Amount',
  'forecast.submit.note': 'Anything they should know',
  'forecast.submit.action': 'Submit',
  'forecast.submit.resubmit': 'Update',
  'forecast.submitted': 'Submitted {when}',
  'forecast.submit.none': 'Not submitted yet',
  'forecast.adjusted.by': 'Adjusted to {amount} by {name}',
  'forecast.adjust.title': 'Adjust',
  'forecast.adjust.note': 'Why (required)',
  'forecast.adjust.action': 'Adjust',
  'forecast.adjust.clear': 'Remove the adjustment',
  'forecast.rollup.title': 'Your team-s forecast',
  'forecast.rollup.total': 'Rolled up',
  'forecast.rollup.awaiting': '{count} people have not submitted, so this total does not cover them.',
  'forecast.rollup.complete': 'Everybody has submitted.',
  'forecast.rollup.notSubmitted': 'Not submitted',
  'refusal.forecast.noActor': 'A forecast is submitted by a person.',
  'refusal.forecast.negative': 'A forecast is an amount expected to close, and {amount} is below zero.',
  'refusal.forecast.adjustSelf': 'Adjusting your own forecast is submitting it again. Change the number instead.',
  'refusal.forecast.adjustReason': 'An adjustment needs a reason. Somebody is being told their number was changed.',
  'refusal.forecast.nothingToAdjust': 'That person has not submitted a forecast for this period.',
  'refusal.forecast.noAdjustment': 'There is no adjustment on that forecast to remove.',
  'refusal.forecast.notMine': '{name} does not report to you.',
  'accuracy.title': 'Forecast against actual',
  'accuracy.body': 'What was forecast each week, beside what the period actually closed. The record starts when it starts: a week nobody captured is not a week of zeros.',
  'accuracy.week': 'Week of {date}',
  'accuracy.forecast': 'Forecast',
  'accuracy.actual': 'Actual',
  'accuracy.error.over': 'Over by {amount}',
  'accuracy.error.under': 'Under by {amount}',
  'accuracy.error.none': 'Not forecast that week',
  'accuracy.partial': 'This period has not ended, so every forecast is being compared against what has closed so far. Expect them all to look optimistic.',
  'accuracy.gaps': '{count} weeks in this period were never captured. They are missing from the record, not zero.',
  'accuracy.empty': 'Nothing has been captured for this period yet. The record starts the first time somebody opens this page.',
  'accuracy.pipelineSaid': 'Pipeline said {amount}',
  // --- the funnel (F-100) ----------------------------------------------------
  'funnel.title': 'Where the pipeline leaks',
  'funnel.body': 'Of the deals that ENTERED each stage, how many went on. Not how many are sitting there now: a stage everybody passes through in a day looks empty, and therefore looks broken.',
  'funnel.entered': '{count} entered',
  'funnel.advanced': '{count} went on',
  'funnel.conversion': '{percent}% went on',
  'funnel.conversion.none': 'Nobody entered this stage',
  'funnel.conversion.terminal': 'Nothing comes after this',
  'funnel.days': '{days} days on average',
  'funnel.days.none': 'Nothing has left this stage yet',
  'funnel.stillHere': '{count} still here, not in the average',
  'funnel.since': 'The record starts on {date}.',
  'funnel.beforeHistory': 'This window reaches back further than anything recorded. Stages can only be counted from {date}, so what is shown below is not the whole period.',
  'funnel.noHistory': 'Nothing has been recorded yet. The record starts the first time a deal is created or moved.',
  'funnel.window.90': 'Last 90 days',
  'funnel.window.365': 'Last year',
  'funnel.window.all': 'Everything recorded',
  'noun.pipeline': 'That pipeline',
  // --- pipeline and stage editor (F-042) -------------------------------------
  'pipelines.title': 'Pipelines and stages',
  'pipelines.body': 'What a deal passes through on its way to being won, and what each step is worth. Every pipeline keeps a way to win and a way to lose.',
  'pipelines.new': 'New pipeline',
  'pipelines.name': 'Name',
  'pipelines.makeDefault': 'Open this one by default',
  'pipelines.default': 'Default',
  'pipelines.remove': 'Remove pipeline',
  'pipelines.stage.new': 'Add a stage',
  'pipelines.stage.name': 'Stage name',
  'pipelines.stage.probability': 'Probability',
  'pipelines.stage.outcome': 'Reaching it means',
  'pipelines.outcome.open': 'Still open',
  'pipelines.outcome.won': 'Won',
  'pipelines.outcome.lost': 'Lost',
  'pipelines.stage.deals': '{count} deals here',
  'pipelines.stage.empty': 'Nothing here',
  'pipelines.stage.up': 'Move earlier',
  'pipelines.stage.down': 'Move later',
  'pipelines.stage.remove': 'Remove stage',
  'pipelines.save': 'Save',
  'refusal.pipeline.noName': 'That needs a name.',
  'refusal.pipeline.last': 'This is the only pipeline. A workspace with none cannot hold a deal at all.',
  'refusal.pipeline.hasDeals': '{count} deals are in this pipeline. Move them first.',
  'refusal.stage.probability': 'A probability is a whole percent from 0 to 100, and {value} is not one.',
  'refusal.stage.outcome': 'Reaching a stage means the deal is open, won or lost.',
  'refusal.stage.lastWon': 'This is the only stage that wins a deal.',
  'refusal.stage.lastLost': 'This is the only stage that loses a deal.',
  'refusal.stage.hasDeals': '{count} deals are in this stage. Move them first.',
  'refusal.stage.partialOrder': 'List all {count} stages, once each.',
  // --- capture forms (F-036) -------------------------------------------------
  'form.fields': 'What this form collects',
  'form.fields.body': 'A form needs an email or a phone field, or nobody who fills it in can be followed up.',
  'form.field.add': 'Add field',
  'form.field.remove': 'Remove',
  'form.field.required': 'Required',
  'form.consent': 'Ask for consent',
  'form.consent.text': 'What they are agreeing to',
  'form.save': 'Save the form',
  'form.saved': 'Saved',
  'form.received': 'What this form received',
  'form.received.created': '{count} became leads',
  'form.received.spam': '{count} turned away as automated',
  'form.received.noConsent': '{count} did not agree',
  'form.received.invalid': '{count} were incomplete',
  'form.received.none': 'Nothing yet.',
  'form.notConfigured': 'This form has no fields yet, so it collects nothing.',
  'noun.form': 'That form',
  'refusal.form.notAForm': 'That embed is not a capture form.',
  'refusal.form.noFields': 'A form needs at least one field.',
  'refusal.form.unknownField': 'A capture form cannot collect {field}.',
  'refusal.form.noReach': 'A capture form needs an email or a phone field.',
  'refusal.form.noConsentText': 'Consent needs the sentence somebody is agreeing to.',
  'refusal.form.consent': 'Please agree before sending this.',
  'refusal.form.required': '{field} is needed.',
  'refusal.form.badToken': 'That form link is not valid or has expired.',
  'refusal.form.wrongWidget': 'That link was not made for a capture form.',
  // --- lead routing (F-034) --------------------------------------------------
  'routing.title': 'Who a new lead goes to',
  'routing.body': 'Rules are tried in order and the first that matches wins. A lead that matches nothing stays unassigned rather than going to whoever happens to be first.',
  'routing.rule.name': 'Rule name',
  'routing.rule.add': 'Add rule',
  'routing.strategy': 'Hand it to',
  'routing.strategy.round-robin': 'Each in turn',
  'routing.strategy.load-balanced': 'Whoever has fewest waiting',
  'routing.strategy.named': 'One named person',
  'routing.team': 'From the team',
  'routing.team.any': 'Anybody in the workspace',
  'routing.person': 'Person',
  'routing.pool': '{count} people it can reach',
  'routing.pool.empty': 'This rule can reach nobody. Put somebody in the team, or it will match and assign nothing.',
  'routing.up': 'Try earlier',
  'routing.down': 'Try later',
  'routing.remove': 'Remove rule',
  'routing.off': 'Switched off',
  'routing.empty': 'No rules yet, so every new lead arrives unassigned.',
  'routing.simulate': 'Test with a lead',
  'routing.simulate.matched': '{rule} would give it to {owner}.',
  'routing.simulate.noRule': 'No rule matches this lead, so it would stay unassigned.',
  'routing.simulate.poolEmpty': '{rule} matches, and can reach nobody. It would stay unassigned.',
  'routing.noTerritory': 'Routing by territory is not built yet, so it is not offered here.',
  'noun.routingRule': 'That rule',
  'refusal.routing.noName': 'A routing rule needs a name.',
  'refusal.routing.strategy': 'That is not a way this product routes a lead.',
  'refusal.routing.noPerson': 'A rule that names somebody has to name them.',
  'refusal.routing.badCondition': 'That condition does not work on a lead: {problems}',
  'refusal.routing.partialOrder': 'List all {count} rules, once each.',
  'routing.respond': 'Answer within (minutes)',
  'routing.respond.none': 'No clock',
  'routing.respond.set': '{count} working minutes to answer',
  'notify.lead.unworked': 'A lead I was given goes unanswered',
  'note.mentioned': 'Told {names}',
  'note.mentioned.none': 'Nobody was named.',
  // --- discount approval (F-049) ---------------------------------------------
  'approval.title': 'Discount approval',
  'approval.pending': 'Waiting on approval for a {percent}% discount, above the {cap}% cap.',
  'approval.pending.by': 'Asked by {name}.',
  'approval.approved': '{name} approved a {percent}% discount.',
  'approval.rejected': '{name} did not approve: {comment}',
  'approval.approve': 'Approve',
  'approval.reject': 'Reject',
  'approval.reason': 'Why',
  'approval.own': 'You asked for this, so somebody else has to agree to it.',
  'approval.cap': 'Discounts above',
  'approval.cap.none': 'No cap: no discount needs approval',
  'approval.cap.set': 'Above {percent}%, somebody has to agree',
  'approval.cap.save': 'Save',
  'noun.approval': 'That request',
  'refusal.approval.cap': 'A discount cap is a percentage from 0 to 100, and {value} is not one.',
  'refusal.approval.noActor': 'A decision is made by a person.',
  'refusal.approval.own': 'You asked for this discount, so somebody else has to agree to it.',
  'refusal.approval.notYours': 'Deciding a discount is not something this role does.',
  'refusal.approval.rejectReason': 'Say why, so the person who asked knows what to change.',
  'refusal.approval.decided': '{name} already {status} this request.',
  'refusal.approval.decidedRace': 'Somebody decided this request first.',
  'refusal.quote.awaitingApproval': '{number} is waiting on approval for its {percent}% discount.',
  // --- terms and renewals (F-051) --------------------------------------------
  'term.title': 'Term',
  'term.openEnded': 'Open-ended — this runs until somebody stops it.',
  'term.from': 'From {start}',
  'term.until': 'until {end}',
  'term.countdown': 'Renews in {days} days',
  'term.countdown.ends': 'Ends in {days} days',
  'term.overdue': 'The end date passed {days} days ago.',
  'term.ended': 'Ended.',
  'term.autoRenews': 'Renews automatically',
  'term.manual': 'Does not renew by itself',
  'term.history': 'Terms so far',
  'term.history.first': 'First term',
  'term.history.renewal': 'Renewal',
  'term.set': 'Set the term',
  'term.endsOn': 'Ends on',
  'term.months': 'Next term (months)',
  'term.notice': 'Warn this many days before',
  'term.save': 'Save',
  'noun.subscription': 'That subscription',
  'refusal.term.openEnded': 'An open-ended agreement has nothing to renew.',
  'refusal.term.noLength': 'A renewal has to know how long the next term runs for.',
  // --- playbooks and call scripts (F-056) ------------------------------------
  'playbook.title': 'Playbook for {stage}',
  'playbook.questions': 'Ask',
  'playbook.objections': 'If they say',
  'playbook.response': 'Say',
  'playbook.question.add': 'Add a question',
  'playbook.objection.add': 'Add an objection',
  'playbook.remove': 'Remove',
  'playbook.save': 'Save the playbook',
  'playbook.empty': 'Nothing written for this stage yet.',
  'playbook.edit': 'Playbook',
  'playbook.guidance': 'What this stage means',
  'playbook.exit': 'Before a deal can leave',
  'playbook.exit.add': 'Add a requirement',
  'playbook.ramp': 'Opens by itself for people in their first',
  'playbook.ramp.days': 'days',
  'playbook.ramp.off': 'Never opens by itself.',
  'playbook.show': 'Show the playbook',
  'playbook.hide': 'Hide the playbook',
  'refusal.playbook.noResponse': '"{objection}" has nothing to say back to it.',
  'refusal.playbook.noObjection': 'An answer needs the objection it answers.',
  'refusal.playbook.rampDays': 'A ramp is a whole number of days, from 0 to 3650.',
  // --- support access and the visit banner (F-144) ---------------------------
  'visit.who': '{staff} from support is signed in as you.',
  'visit.reason': 'Reason given: {reason}',
  'visit.end': 'End this visit',
  'support.title': 'Support access',
  'support.body': 'Nobody from support can open this workspace unless you let them in, and only until the time you set.',
  'support.state.never': 'Support has never been let in.',
  'support.state.open': 'Support may come in until {when}.',
  'support.state.withdrawn': 'Support access was withdrawn on {when}.',
  'support.state.lapsed': 'Support access lapsed on {when}.',
  'support.hours': 'Hours',
  'support.let': 'Let support in',
  'support.withdraw': 'Withdraw access',
  'support.visits': 'Visits',
  'support.visits.none': 'Nobody from support has been in.',
  'support.visit.ongoing': 'In progress',
  'refusal.platform.capability': 'Your console account does not hold {capability}.',
  'refusal.support.hours': 'Let support in for a whole number of hours, from 1 to 720.',
  'refusal.support.reason': 'Say why you are going in, in a sentence somebody else could read later.',
  'refusal.support.neverAsked': 'Nobody at {workspace} has let support in.',
  'refusal.support.withdrawn': '{workspace} withdrew support access.',
  'refusal.support.lapsed': 'Support access to {workspace} lapsed. Ask them to extend it.',
  'refusal.support.notActive': '{name} is not an active account.',
  'noun.supportGrant': 'An open support grant',
  'refusal.platform.noSession': 'The staff session could not be created.',
  'refusal.platform.noWorkspace': 'No workspace with that id.',
  'refusal.platform.staffEmail': 'A staff account needs an email address.',
  'refusal.platform.staffPassword': 'A staff password is at least 12 characters.',
  'refusal.platform.strayCapability': 'A staff account may only hold platform capabilities. {stray} is not one.',
  'refusal.platform.staffNotCreated': 'The staff account was not created.',
  'refusal.platform.noStaff': 'No active staff account with that address.',
  'refusal.support.openTreatedAsRefusal': 'An open grant was treated as a refusal.',
  'noun.workspace': 'That workspace',
  'refusal.platform.signIn': 'Sign in to the console to continue.',
  'refusal.platform.badRoute': 'A console route must carry a platform permission.',
  'refusal.platform.wrongPath': 'A console route reached the workspace path.',
  // --- backups and restore (F-145) -------------------------------------------
  'backup.title': 'Backup and restore',
  'backup.last': 'Last good backup: {when}.',
  'backup.never': 'No backup has ever been taken of this workspace.',
  'backup.failed': 'The attempt on {when} failed: {why}',
  'backup.storedIn': 'Archives are kept in {where}.',
  'backup.run': 'Back up now',
  'backup.retention': 'Keep backups for',
  'backup.days': 'days',
  'backup.list': 'Backups',
  'backup.none': 'Nothing here yet.',
  'backup.rows': 'rows',
  'backup.gone': 'the archive is not there',
  'backup.verify': 'Check it can be read',
  'backup.restore': 'Restore from this',
  'backup.scope': 'Restore',
  'backup.scope.record': 'One record',
  'backup.scope.object': 'One table',
  'backup.scope.workspace': 'The whole workspace',
  'backup.table': 'Table',
  'backup.record': 'Record id',
  'backup.preview': 'Restoring {covers} would put back {missing} and overwrite {changed}.',
  'backup.preview.note': 'Anything created since the backup is left alone.',
  'backup.commit': 'Do it',
  'backup.cancel': 'Leave it',
  'refusal.backup.alreadyRunning': 'A backup of this workspace is already running.',
  'refusal.backup.retention': 'Keep backups for a whole number of days, from 1 to 3650.',
  'refusal.backup.archiveMissing': 'That backup is recorded but its archive is not there.',
  'refusal.restore.unknownTable': '{table} is not a table this workspace backs up.',
  'refusal.restore.compositeRecord': 'A {table} row has no single record id. Restore the whole table instead.',
  'refusal.restore.notInBackup': 'That record is not in this backup.',
  'refusal.restore.cancelled': 'That restore was cancelled.',
  'refusal.restore.alreadyCommitted': 'That restore has already been committed.',
  'refusal.restore.moved': 'This workspace has changed since the preview. Look at it again.',
  'noun.backup': 'That backup',
  'noun.restore': 'That restore',
  'refusal.restore.noTable': 'Say which table to restore from.',
  'refusal.restore.noRecord': 'Say which record to restore.',
  // --- location from an address (F-152) --------------------------------------
  'geo.estimate': 'estimated from the address by {source}',
  'geo.sandbox': 'sandbox estimate, not a real lookup',
  'geo.unrecorded': 'recorded before anything said where it came from',
  'geo.private': 'came from inside a network, so there is nothing to place',
  'geo.title': 'Location from address',
  'geo.body': 'Country, region and city are estimated from the visitor address at capture. City-level accuracy is poor.',
  'geo.retention': 'Keep the raw address for',
  'geo.days': 'days',
  'geo.dropNow': 'Drop expired addresses now',
  'geo.provider.sandbox': 'No provider configured. Locations are sandbox estimates and say so.',
  'geo.provider.live': 'Resolved by {source}.',
  'geo.provider.none': 'Nothing is configured to resolve addresses.',
  'refusal.geo.noAddress': 'There is no address to resolve.',
  'refusal.geo.notLocated': 'That does not hold a location.',
  'refusal.geo.retention': 'Keep addresses for a whole number of days, from 0 to 3650.',
  'refusal.funnel.noWindow': 'Say how far back to look: either days, or a from date.',
  // --- provider modes (part 3) -----------------------------------------------
  'provider.allSandbox': '{count} provider(s) are in sandbox: messages and model calls are composed and kept here, not sent.',
  'provider.someMissing': '{sandbox} provider(s) in sandbox, {missing} not configured at all.',
  'provider.sandbox': 'sandbox — composed and kept here, not sent',
  'provider.notConfigured': 'not configured — nothing is sent, and nothing pretends it was',
  'provider.show': 'Which ones?',
  'provider.hide': 'Hide',
  'provider.title': 'Providers',
  'provider.body': 'What this deployment can actually reach. Sandbox is a working mode: messages are composed, validated and kept here, and every screen that shows one says so.',
  'provider.mode.live': 'live',
  // --- prospecting and enrichment (F-038, F-154) ------------------------------
  'mining.title': 'Find new companies',
  'mining.body': 'Search a data provider for companies nobody here knows yet. Looking is free; you pay when a company becomes a lead.',
  'mining.balance': '{balance} credits left.',
  'mining.industry': 'Industry',
  'mining.geography': 'Where',
  'mining.role': 'Role',
  'mining.look': 'Look (free)',
  'mining.found': '{found} found, {known} of them already here.',
  'mining.already': 'already here as {label}',
  'mining.cost': 'Importing these costs {count} of your {balance} credits.',
  'mining.import': 'Import the ticked ones',
  'enrich.title': 'Fill in what is known',
  'enrich.applied': 'Filled {fields}.',
  'enrich.differs': '{field}: this says "{ours}", the provider says "{theirs}".',
  'enrich.nothing': 'Nothing new to fill in.',
  'enrich.cached': 'From a lookup already paid for.',
  'refusal.credits.none': 'There are no enrichment credits left in this workspace.',
  'refusal.credits.notEnough': 'That costs {cost} credits and there are {balance} left. Select fewer, or add credits.',
  'refusal.mining.noCriteria': 'Say what to look for: an industry, a place, or a role.',
  'refusal.mining.nothingSelected': 'Choose at least one company to import.',
  'refusal.enrich.noDomain': '{name} has no website domain to look up.',
  'refusal.enrich.notConfigured': 'No company data provider is configured, so nothing was looked up.',
  'refusal.credits.wholeNumber': 'Credits are counted in whole numbers, one or more.',
  'content.block.lineItems': 'Line items',
  // --- document templates (F-050) ---------------------------------------------
  'doc.title': 'Document templates',
  'doc.body': 'Quotes and deals printed from a template. Fields are filled from the record when the document is generated.',
  'doc.new': 'New template',
  'doc.none': 'No templates yet. Make one to print quotes and deals.',
  'doc.edit': 'Edit',
  'doc.name': 'Name',
  'doc.object': 'Printed from',
  'doc.object.quote': 'Quote',
  'doc.object.deal': 'Deal',
  'doc.format': 'Format',
  'doc.formatNote': 'A PDF cannot print Arabic script or the rupee sign. Choose DOCX for any document that may contain them.',
  'doc.fields': 'Fields',
  'doc.save': 'Save template',
  'doc.cancel': 'Cancel',
  'doc.generateTitle': 'Print from a template',
  'doc.noTemplates': 'There are no quote templates yet. An administrator can make one in Setup.',
  'doc.template': 'Template',
  'doc.choose': 'Choose a template',
  'doc.unknownFields': 'The template uses {fields}, which a quote does not have. The template needs fixing.',
  'doc.emptyFields': 'This quote has nothing for {fields}. Fill it in before printing.',
  'doc.unsettable': 'A PDF cannot print {characters}. Use a DOCX template for this quote.',
  'doc.ready': 'Every field has a value. Nothing will print as a placeholder.',
  'doc.generate': 'Generate and attach',
  'doc.field.accountName': 'Company name',
  'doc.field.ownerName': 'Owner',
  'doc.field.workspaceName': 'Your company',
  'doc.field.today': 'Today',
  'doc.field.quoteNumber': 'Quote number',
  'doc.field.quoteName': 'Quote name',
  'doc.field.quoteValidUntil': 'Valid until',
  'doc.field.quoteTerms': 'Terms',
  'doc.field.quoteSubtotal': 'Subtotal',
  'doc.field.quoteDiscount': 'Discount',
  'doc.field.quoteTotal': 'Total',
  'doc.field.dealName': 'Deal name',
  'doc.field.dealAmount': 'Deal amount',
  'doc.field.dealStage': 'Stage',
  'doc.field.dealCloseDate': 'Close date',
  'refusal.document.noName': 'Give the template a name.',
  'refusal.document.object': 'Documents can be generated from quotes and deals, not from {object}.',
  'refusal.document.format': 'A document is a PDF or a DOCX.',
  'refusal.document.blocks': 'This template cannot be saved: {problem}',
  'refusal.document.dealLines': 'A deal has no line items to print. Line items belong in a quote template.',
  'refusal.document.unknownFields': 'The template uses {fields}, which a {object} does not have.',
  'refusal.document.nameTaken': 'There is already a template called "{name}".',
  'refusal.document.emptyFields': 'This {object} has nothing for {fields}, and the template gives no fallback. Fill it in first.',
  'refusal.document.unsettable': 'A PDF cannot print {characters}. Make this a DOCX template, which can.',
  'noun.documentTemplate': 'That template',
  'noun.quote': 'That quote',
  'partner.title': "Partners",
  'partner.body': "Companies that sell for you, the deals they register, and the links shared with them. A partner sees only the deals they registered, never another partner’s.",
  'partner.notPermitted': "Managing partners needs the Manage partners permission. Ask an administrator.",
  'partner.registrations': "Registrations",
  'partner.registrations.none': "No partner has registered a deal yet.",
  'partner.partners': "Partner companies",
  'partner.partners.none': "No company is a partner yet.",
  'partner.partners.help': "Make a company a partner from its own page, and issue links to its people there.",
  'partner.people': "People: {count}",
  'partner.protectionDays': "Protected for {days} days",
  'partner.protectionLabel': "Protection, in days",
  'partner.protectedUntil': "Protected until {date}",
  'partner.registeredBy': "Registered by {person} at {partner} on {date}",
  'partner.someone': "someone no longer on file",
  'partner.status.pending': "Under review",
  'partner.status.accepted': "Accepted",
  'partner.status.rejected': "Rejected",
  'partner.conflict.partner': "Collides with “{deal}”, registered by {partner}. The partner has not been told.",
  'partner.conflict.direct': "Collides with “{deal}”, which is sold directly. The partner has not been told.",
  'partner.accept': "Accept",
  'partner.reject': "Reject",
  'partner.cancel': "Cancel",
  'partner.rejectReason': "Why, in words the partner will read",
  'partner.collateral': "Shared with partners",
  'partner.collateral.none': "Nothing is shared with partners yet.",
  'partner.collateral.title': "Title",
  'partner.collateral.url': "Address (https://)",
  'partner.collateral.share': "Share",
  'partner.collateral.remove': "Stop sharing",
  'partner.dealPanel': "Registered by a partner",
  'partner.toldPartner': "The partner was told: {note}",
  'partner.accountPanel': "Partner",
  'partner.since': "A partner since {date}",
  'partner.notPartner': "This company is not a partner.",
  'partner.make': "Make a partner",
  'partner.saveDays': "Save protection",
  'partner.end': "End partnership",
  'partner.noPeople': "Nobody at this company is on file, so there is nobody to send a partner link to.",
  'partner.issueLink': "Issue partner link",
  'partner.linkIssued': "Send this link to them. It works until {date} and is not shown again.",
  'partner.portal.register': "Register a deal",
  'partner.portal.registered': "Registered. It is under review, and you can follow it below.",
  'partner.portal.company': "Customer company",
  'partner.portal.website': "Customer website (optional)",
  'partner.portal.dealName': "Deal name",
  'partner.portal.closeDate': "Expected close date",
  'partner.portal.amount': "Expected amount",
  'partner.portal.currency': "Currency",
  'partner.portal.submit': "Register",
  'partner.portal.deals': "Your registrations",
  'partner.portal.noDeals': "You have not registered a deal yet.",
  'partner.portal.status.under-review': "Under review",
  'partner.portal.status.accepted': "Accepted",
  'partner.portal.status.not-accepted': "Not accepted",
  'partner.portal.collateral': "Shared with you",
  'partner.portal.noCollateral': "Nothing has been shared yet.",
  'portal.field.name': "Deal",
  'portal.field.amount': "Amount",
  'portal.field.close_date': "Expected close",
  'portal.field.protected_until': "Protected until",
  'noun.link': "That link",
  'refusal.partner.protectionDays': "Protection is a whole number of days, from 1 to 730.",
  'refusal.partner.notAtPartner': "{name} does not work at a partner, so they cannot be given a partner link.",
  'refusal.partner.rejectReason': "Say why, in words the partner will read.",
  'refusal.partner.notRegistered': "That deal was not registered by a partner.",
  'refusal.partner.protected': "{partner} already holds this customer, and their protection has not run out.",
  'refusal.partner.alreadyDecided': "That registration was already {status}.",
  'refusal.partner.collateralTitle': "Give the link a title.",
  'refusal.partner.collateralUrl': "A shared link has to start with https://.",
  'refusal.partner.wrongSurface': "That link is not a partner link.",
  'refusal.partner.noLongerPartner': "This company is no longer a partner.",
  'refusal.partner.closed': "This partner portal is closed.",
  'refusal.partner.company': "Say which company this deal is with.",
  'refusal.partner.dealName': "Give the deal a name.",
  'refusal.partner.amount': "The amount is a number above zero, like 48000 or 48000.50.",
  'refusal.partner.currency': "The currency is a three-letter code, like AED.",
  'refusal.partner.closeDate': "The expected close date is a date, like 2026-12-31.",
  'refusal.partner.closeDatePast': "The expected close date is in the past.",
  'refusal.partner.ownCompany': "A partner cannot register a deal with their own company.",
  'refusal.partner.alreadyRegistered': "You registered “{deal}” already.",

  'quote.termsTitle': "Terms and validity",
  'quote.terms': "Terms",
  'quote.termsSave': "Save terms",
  'quote.noTerms': "No terms were set on this quote.",
  'quote.noValidity': "This quote has no validity date.",
  'quote.startedFrom': "Started from {template}, version {version}",
  'quoteTemplate.title': "Quotation templates",
  'quoteTemplate.body': "Quote skeletons a rep starts from: lines, terms, validity and branding. A quote made from one is an ordinary quote, and editing the template later never changes it.",
  'quoteTemplate.new': "New quotation template",
  'quoteTemplate.none': "There are no quotation templates yet.",
  'quoteTemplate.summary': "{currency} · {lines} lines · version {version} · saved {date}",
  'quoteTemplate.pickerTitle': "Start from",
  'quoteTemplate.blank': "A blank quote",
  'quoteTemplate.noneInCurrency': "No quotation templates are priced in {currency}.",
  'quoteTemplate.choice': "{name} ({lines} lines)",
  'quoteTemplate.version': "Version",
  'quoteTemplate.versionOption': "Version {version}",
  'quoteTemplate.save': "Save as a new version",
  'quoteTemplate.saved': "Saved as version {version}.",
  'quoteTemplate.archive': "Stop offering this template",
  'quoteTemplate.oldVersion': "This is version {version}, kept as a record of what quotes were started from. It cannot be edited; choose the latest version to make changes.",
  'quoteTemplate.notPermitted': "You can read this template. Changing it needs the Create and change quotation templates permission.",
  'quoteTemplate.name': "Name",
  'quoteTemplate.currency': "Currency",
  'quoteTemplate.validityDays': "Valid for (days)",
  'quoteTemplate.branding': "Printed with",
  'quoteTemplate.brandingNone': "No document template",
  'quoteTemplate.brandingChosen': "The document template already chosen",
  'quoteTemplate.searchProducts': "Search the catalogue",
  'quoteTemplate.addProduct': "Add a product line",
  'quoteTemplate.chooseProduct': "Choose a product",
  'quoteTemplate.productsCapped': "Showing the first 25 products. Search to find others.",
  'quoteTemplate.productPricing': "Only products priced in {currency} are offered. A product line is priced from the catalogue on the day the template is used, so the price shown here may change.",
  'quoteTemplate.readOnly': "Read only",
  'quoteTemplate.validFor': "Valid for {days} days from the day it is used",
  'quoteTemplate.optionalSection': "Optional - the customer chooses these",
  'quoteTemplate.emptyState': "A blank template, with one empty line ready to fill in.",
  'quoteTemplate.loadFailed': "This template could not be loaded.",
  'noun.quoteTemplate': "That quotation template",
  'refusal.quote.termsLength': "Terms are at most 10,000 characters.",
  'refusal.quote.validUntil': "Valid until is a date, like 2026-12-31.",
  'refusal.quote.validUntilPast': "That date has already passed, so the quote would be out of date the moment it saved.",
  'refusal.quoteTemplate.name': "Give the template a name of up to 200 characters.",
  'refusal.quoteTemplate.currency': "The currency is a three-letter code, like AED.",
  'refusal.quoteTemplate.validity': "A quote from this template is valid for 1 to 365 days.",
  'refusal.quoteTemplate.noLines': "A template needs at least one line.",
  'refusal.quoteTemplate.tooManyLines': "A template holds at most 200 lines.",
  'refusal.quoteTemplate.lineDescription': "Line {row} needs a description.",
  'refusal.quoteTemplate.lineQuantity': "Line {row} needs a quantity above zero.",
  'refusal.quoteTemplate.linePrice': "Line {row} needs a price of zero or more.",
  'refusal.quoteTemplate.lineDiscount': "Line {row} has a discount outside 0 to 100 per cent.",
  'refusal.quoteTemplate.lineProduct': "Line {row} is for a product that is not in the catalogue.",
  'refusal.quoteTemplate.document': "Choose a document template made for quotes.",
  'refusal.quoteTemplate.nameTaken': "There is already a quotation template called \"{name}\".",
  'refusal.quoteTemplate.stale': "Somebody saved version {current} of this template after you opened version {opened}. Reopen it to see their changes.",
  'refusal.quoteTemplate.notDraft': "{quote} has been sent, so a template cannot be applied to it.",
  'refusal.quoteTemplate.notEmpty': "{quote} already has lines. A template starts a quote; it does not replace one.",
  'refusal.quoteTemplate.currencyMismatch': "\"{template}\" is priced in {templateCurrency}, and this quote is in {quoteCurrency}.",
  'refusal.quoteTemplate.productGone': "Line {row}, \"{line}\", is for a product that is no longer sold. Edit the template.",
  'refusal.quoteTemplate.lineCurrency': "Line {row}, \"{line}\", is priced in {lineCurrency}, and this quote is in {quoteCurrency}.",
  'quoteTemplate.noDocuments': "No document templates for quotes exist yet. Make one in Setup, under Document templates, to choose how quotes from this template are printed.",
  'portal.quote.open': "Open",
  'portal.quote.close': "Close",
  'portal.quote.unavailable': "This quote cannot be shown.",
  'portal.quote.validUntil': "Valid until {date}",
  'portal.quote.lapsed': "This quote was valid until {date}, which has passed. Ask for a new one.",
  'portal.quote.optionalTitle': "Optional - tick what you would like to add",
  'portal.quote.accept': "Accept for {total}",
  'portal.quote.decline': "Decline",
  'portal.quote.back': "Back",
  'portal.quote.declineReason': "Would you tell us why? (optional)",
  'portal.quote.accepted': "You accepted this quote on {date}.",
  'portal.quote.declined': "You declined this quote on {date}.",
  'quote.decision.accepted': "{person} accepted this on {date}, for {total}, with {lines} optional lines.",
  'quote.decision.declined': "{person} declined this on {date}. {reason}",
  'quote.decision.someone': "The customer",
  'refusal.quoteAccept.wrongLink': "That link does not open quotes.",
  'refusal.quoteAccept.reasonLength': "Keep the reason under 2,000 characters.",
  'refusal.quoteAccept.declineWithLines': "A declined quote takes no optional lines.",
  'refusal.quoteAccept.notOptional': "One of the lines chosen is not an optional line on this quote.",
  'refusal.quoteAccept.alreadyDecided': "{quote} was already {status}.",
  'refusal.quoteAccept.lapsed': "{quote} was valid until {date}, which has passed. Ask for a new one.",
  'refusal.quoteAccept.notOpen': "{quote} cannot be decided on now.",
  'refusal.quoteAccept.closed': "Quotes are not available on this page any more.",
  'quote.unit': "Unit",
  'quote.unitConverted': "= {amount} {base}",
  'unit.title': "Units of measure",
  'unit.body': "The units products are counted and sold in. A unit made of another is stored as what it comes to in the base unit, so a case of 6 boxes of 12 is 72 each.",
  'unit.none': "No units yet. Start with a base unit, such as Each or Metre.",
  'unit.comesTo': "= {factor} {base}",
  'unit.isBase': "Base unit",
  'unit.name': "Name",
  'unit.madeOf': "Made of",
  'unit.aBaseUnit': "Nothing - a base unit",
  'unit.holdsSome': "Some {unit}",
  'unit.howMany': "How many",
  'unit.add': "Add unit",
  'unit.countedIn': "Counted in",
  'unit.noneYet': "No base units yet. Add them in Setup, under Units of measure.",
  'unit.notCounted': "Not set",
  'unit.countedInHelp': "Quote lines for this product can then be entered in any unit made of this one, and show what they come to.",
  'noun.unit': "That unit",
  'refusal.unit.label': "Give the unit a name of up to 60 characters.",
  'refusal.unit.taken': "There is already a unit called \"{label}\".",
  'refusal.unit.count': "Say how many it holds, as a number above zero.",
  'refusal.unit.notBase': "\"{label}\" is counted in another unit, so it cannot be a product's base unit.",
  'refusal.unit.wrongFamily': "Line {row}: {product} is not sold in {unit}.",
  'territory.title': "Territories",
  'territory.body': "Who works which companies. A named list beats criteria; among criteria the lowest priority number wins. Saving a territory moves nobody - moving is the rebalance below, previewed first.",
  'territory.new': "New territory",
  'territory.none': "No territories yet.",
  'territory.inactive': "(inactive)",
  'territory.counts': "{matching} match · {assigned} assigned · priority {priority} · {who}",
  'territory.edit': "Edit",
  'territory.overlap': "{count} of these companies also match another territory. The lower priority number gets them; somebody should check that is intended.",
  'territory.rebalance': "Rebalance",
  'territory.rebalanceBody': "Move every company to the territory it belongs in now, giving it that territory’s owner. Every move is kept on the company.",
  'territory.rebalanced': "{count} companies moved.",
  'territory.previewRebalance': "Preview what would move",
  'territory.nothingToMove': "Every company is already where it belongs.",
  'territory.wouldMove': "{total} companies would move. The first {shown} are listed.",
  'territory.move': "{company}: {from} → {to}",
  'territory.noTerritory': "No territory",
  'territory.applyRebalance': "Move {total} companies",
  'territory.cancel': "Cancel",
  'territory.name': "Name",
  'territory.priority': "Priority (lower wins)",
  'territory.owner': "Owner",
  'territory.nobody': "Nobody - the team works it",
  'territory.team': "Team",
  'territory.noTeam': "No team",
  'territory.activeLabel': "Active - claims companies",
  'territory.criteria': "Companies that match",
  'territory.named': "Named companies",
  'territory.namedHelp': "Named here, a company belongs to this territory whatever the criteria say. A company can be named in one territory only.",
  'territory.remove': "Remove",
  'territory.findCompany': "Find a company to name",
  'territory.addCompany': "Add {name}",
  'territory.save': "Save territory",
  'territory.accountPanel': "Territory",
  'territory.reason.created': "{date}: placed in {to} when created. Owner {owner}.",
  'territory.reason.changed': "{date}: moved from {from} to {to} when its details changed. Owner {owner}.",
  'territory.reason.rebalanced': "{date}: moved from {from} to {to} by a rebalance. Owner {owner}.",
  'noun.territory': "That territory",
  'refusal.territory.criteria': "Those criteria do not work on a company: {problems}",
  'refusal.territory.name': "Give the territory a name of up to 120 characters.",
  'refusal.territory.priority': "Priority is a whole number from 1 to 10,000; lower wins.",
  'refusal.territory.assignee': "Say who works this territory: a person, a team, or both.",
  'refusal.territory.nameTaken': "There is already a territory called \"{name}\".",
  'refusal.territory.namedTwice': "{company} is already named in {territory}.",
  'refusal.territory.off': "Territories are switched off for this workspace.",
  // --- automation lane: validation rules (F-091) ---------------------------
  'noun.object': "That object",
  'noun.validationRule': "That validation rule",
  'refusal.validation.noName': "A validation rule needs a name, because the name is what people are shown.",
  'refusal.validation.noMessage': "A validation rule needs a message saying what to fix.",
  'refusal.validation.severity': "A rule either warns or refuses the save.",
  'refusal.validation.scope': "A rule applies to everyone or to chosen roles.",
  'refusal.validation.field': "“{field}” is not a field this rule can be shown against.",
  'refusal.validation.conditions': "This rule cannot be judged as it stands.",
  'refusal.validation.noRoles': "A rule for chosen roles needs at least one role.",
  'refusal.validation.unknownRole': "One of the chosen roles does not exist in this workspace.",
  'refusal.validation.duplicateName': "This object already has a rule called “{name}”.",
  'refusal.validation.blocked': "{rule}: {message}",
  'refusal.validation.blockedMany': "This save breaks {count} rules. Each is shown on its field.",
  'refusal.validation.required': "{field} is required.",
  'validation.required.rule': "Required field",
  'validation.required.message': "{field} is required.",
  'validation.ruleName': "Rule: {rule}",
  'validation.elsewhere': "About fields not on this page",
  'validation.title': "Validation rules",
  'validation.body': "Refuse a save, or save and warn, when a record looks wrong. The person it stops is always told the rule's name.",
  'validation.object': "Object",
  'validation.new': "New rule",
  'validation.empty': "No validation rules on this object yet.",
  'validation.name': "Rule name",
  'validation.field': "Show the message against",
  'validation.severity': "When a record breaks it",
  'validation.severity.block': "Refuse the save",
  'validation.severity.warn': "Save, and warn",
  'validation.message': "Message",
  'validation.scope': "Applies to",
  'validation.scope.everyone': "Everyone",
  'validation.scope.roles': "Chosen roles",
  'validation.scope.nobody': "Nobody: the roles it named were removed",
  'validation.conditions': "A record breaks this rule when",
  'validation.makeRequired': "Require this field",
  'validation.save': "Save rule",
  'validation.cancel': "Cancel",
  'validation.edit': "Edit",
  'validation.delete': "Remove",
  'validation.off': "Off",
  'validation.chip.block': "Refuses",
  'validation.chip.warn': "Warns",

  'noun.lead': "That lead",
  'refusal.touch.noPerson': "A touch belongs to a lead or a contact.",
  'source.title': "Source",
  'source.first': "First touch",
  'source.last': "Last touch",
  'source.firstAndLast': "First and last touch",
  'source.count': "Touches recorded: {count}",
  'source.referrer': "Referrer",
  'source.notSent': "not sent",
  'source.via.form': "through the form {form}",
  'source.via.formRemoved': "through a capture form that has since been removed",
  'source.via.record': "stored on the lead when it was created",
  'source.none.person': "No arrival has been recorded for this person yet. Touches are recorded when somebody submits a capture form.",
  'source.none.deal': "This deal was created without a person, so it carries no source.",
  'source.deal.from': "Carried from",
  'source.deal.hidden': "Carried from a contact you cannot open",
  'source.deal.noTouch': "That person had no recorded touch when this deal was created.",
  'refusal.source.object': "A source is kept for leads, contacts and deals.",
  'attribution.title': "Attribution",
  'attribution.body': "Won revenue credited across the touches that came before each deal, by two models at once. One number alone is a model presented as a fact.",
  'attribution.modelA': "Model",
  'attribution.modelB': "Compared with",
  'attribution.model.first': "First touch",
  'attribution.model.last': "Last touch",
  'attribution.model.linear': "Linear",
  'attribution.model.u-shaped': "U-shaped (40/20/40)",
  'attribution.model.time-decay': "Time decay (7-day half-life)",
  'attribution.dimension': "Group by",
  'attribution.from': "Won from",
  'attribution.to': "Won to",
  'attribution.deals': "Deals",
  'attribution.none': "No won deal in this period came through a tracked touch.",
  'attribution.noValue': "(none)",
  'attribution.covers': "Covers {deals} won deals worth {revenue}. Not credited: {unattributed} won deals with no touch before they were created ({unattributedRevenue}), and {other} won deals in another currency.",
  'attribution.campaignTitle': "Attribution",
  'attribution.trackingCode': "Tracked by utm_campaign",
  'attribution.saveCode': "Save code",
  'attribution.trackingHelp': "Compared exactly as it appears in the link - \"Spring-Sale\" and \"spring-sale\" are two codes.",
  'attribution.noCode': "Give this campaign the utm_campaign value its links carry to see what it was credited with.",
  'refusal.attribution.sameModel': "Choose two different models to compare.",
  'refusal.attribution.period': "Choose a period that starts on or before it ends.",
  'refusal.attribution.codeLength': "A tracking code is at most 200 characters.",
  'refusal.attribution.codeTaken': "{campaign} is already tracked by \"{code}\".",
  'noun.campaign': "That campaign",
  'notify.lead.mql': "A lead I own becomes marketing-qualified",
  'mql.queue': "Marketing-qualified leads",
  'mql.off': "The MQL handoff is off: no threshold is set.",
  'mql.threshold': "Handed over at {points} points - the threshold marketing set.",
  'mql.onlyMine': "Only mine",
  'mql.empty': "Nothing waiting for an answer.",
  'mql.scored': "{handoff} points when handed over, {now} now",
  'mql.due': "Answer by {when}",
  'mql.overdue': "Overdue since {when}",
  'mql.why': "Why it is going back",
  'mql.chooseReason': "Choose a reason",
  'mql.reject': "Turn back",
  'mql.accept': "Accept",
  'mql.cancel': "Cancel",
  'mql.title': "MQL handoff",
  'mql.body': "The score at which marketing hands a lead to sales, how long sales has to answer, and the reasons sales can give for turning one back - one definition both teams read.",
  'mql.thresholdLabel': "Threshold (points)",
  'mql.hoursLabel': "Answer within (working hours)",
  'mql.save': "Save",
  'mql.thresholdHelp': "Leave the threshold empty to switch the handoff off. A lead is handed over once, when its score first reaches it.",
  'mql.reasons': "Reasons sales can give",
  'mql.retire': "Retire",
  'mql.newReason': "New reason",
  'mql.addReason': "Add reason",
  'mql.reportTitle': "What sales said, last 90 days",
  'mql.reportSummary': "{decided} answered: {accepted} accepted, {rejected} turned back.",
  'mql.band': "{from}-{to} points: {accepted} accepted, {rejected} turned back",
  'refusal.mql.threshold': "The threshold is a whole number of points from 1 to 1,000.",
  'refusal.mql.slaHours': "Sales has 1 to 720 working hours to answer.",
  'refusal.mql.reasonTaken': "\"{label}\" is already a reason.",
  'refusal.mql.noteLength': "Keep the note under 2,000 characters.",
  'refusal.mql.reasonRequired': "Choose why it is being turned back.",
  'refusal.mql.reasonRetired': "That reason is no longer offered. Choose another.",
  'refusal.mql.alreadyDecided': "This lead was already {decision}.",
  'refusal.mql.notMql': "This lead has not been handed over as marketing-qualified.",
  'refusal.mql.period': "The period is 1 to 730 days.",
  'campaign.refusal.awaitingConfirmation': "Asked to confirm by email, and has not yet",
  'campaign.refusal.erased': "Erased at their request",
  'consent.pending': "waiting for confirmation",
  'consent.evidence': "Consent evidence",
  'consent.evidenceBody': "Every consent this person gave or withdrew, with the words they were shown and where it came from. A boolean is not evidence.",
  'consent.none': "No consent has been recorded for this person.",
  'consent.wording': "They were shown",
  'consent.noWording': "The words shown were not recorded (recorded before wording was kept).",
  'consent.from': "From {source}",
  'consent.ip': "IP {ip}",
  'consent.expires': "The confirmation link stops working {date}.",
  'consent.recordedBy': "Recorded by {name}",
  'confirm.title': "Confirm your subscription",
  'confirm.confirmed': "Thank you - you are confirmed. We will only send what you agreed to.",
  'confirm.already': "You had already confirmed. Nothing more to do.",
  'confirm.expired': "This confirmation link has expired. Sign up again and we will send a new one.",
  'confirm.invalid': "This link does not work. Check that you copied the whole address from the email.",
  'confirm.agreed': "What you agreed to",
  'refusal.consent.doubleNeedsConsent': "Double opt-in confirms consent, so the form has to ask for consent first.",
  'refusal.consent.object': "Consent is kept for contacts and leads.",
  'form.doubleOptIn': "Ask them to confirm by email before counting it (double opt-in)",
  'landing.pages': "Landing pages",
  'landing.new': "New landing page",
  'landing.none': "No landing pages yet.",
  'landing.liveCurrent': "Live at {path}",
  'landing.liveOlder': "Live at {path} - an older version; the latest edit is not published",
  'landing.draftOnly': "Draft - not published",
  'landing.missing': "There is no page at this address.",
  'landing.previewBanner': "Preview - this is the draft, and nobody else can see it until it is published.",
  'landing.welcomeBack': "Welcome back - we only need what we do not already have.",
  'landing.previewNoSubmit': "The form does not send from a preview.",
  'landing.submit': "Send",
  'landing.liveAt': "Live at {path} since {date}",
  'landing.saveDraft': "Save draft",
  'landing.makePreview': "Make a preview link",
  'landing.publish': "Publish this version",
  'landing.unpublish': "Take down",
  'landing.previewFirst': "Open the preview of this version before publishing it.",
  'landing.previewReady': "Preview link ready.",
  'landing.openPreview': "Open the preview",
  'landing.name': "Name",
  'landing.slug': "Address (after /p/<workspace>/)",
  'landing.title': "Page title (search engines show this)",
  'landing.form': "Capture form",
  'landing.noForm': "No form",
  'landing.description': "Description (search engines show this)",
  'landing.content': "Content",
  'landing.emptyBuilder': "A blank page, ready for its first block.",
  'noun.landingPage': "That landing page",
  'refusal.landing.name': "Give the page a name.",
  'refusal.landing.slug': "The address is lower-case letters, numbers and single hyphens, like spring-offer.",
  'refusal.landing.title': "The page title is 1 to 120 characters.",
  'refusal.landing.description': "The description is at most 300 characters.",
  'refusal.landing.blocks': "This page cannot be saved: {problem}",
  'refusal.landing.form': "Choose a capture form that is still live.",
  'refusal.landing.slugTaken': "Another page already lives at /{slug}.",
  'refusal.landing.notPreviewed': "Open the preview of this version before publishing it.",
  'refusal.landing.changed': "The page changed while it was being published. Preview it again.",
  'refusal.form.email': "That email address does not look complete - it needs an @ and a domain.",
  'assets.library': "Asset library",
  'assets.fromLibrary': "From the library",
  'assets.chooseImage': "Choose from the library",
  'assets.search': "Search names and tags",
  'assets.kind': "Kind",
  'assets.kindAll': "Every kind",
  'assets.kind.image': "Image",
  'assets.kind.document': "Document",
  'assets.kind.snippet': "Snippet",
  'assets.kind.template': "Template",
  'assets.folders': "Folders",
  'assets.all': "Everything",
  'assets.folder': "Folder",
  'assets.noFolder': "No folder",
  'assets.newFolder': "New folder",
  'assets.folderName': "Folder name",
  'assets.editorRole': "Who can change it",
  'assets.anyoneEdits': "Anyone who edits content",
  'assets.createFolder': "Add folder",
  'assets.locked': "Only {role} and the roles above it can change this folder. Everyone can use what is in it.",
  'assets.none': "Nothing in the library here yet.",
  'assets.noneBody': "Add an image, a document, a snippet or a template, and it can be used in every email and page.",
  'assets.usedIn': "Used in {count}",
  'assets.unused': "Not used anywhere",
  'assets.whereUsed': "Where it is used",
  'assets.use.email': "email",
  'assets.use.landing-page': "landing page",
  'assets.use.article': "article",
  'assets.add': "Add to the library",
  'assets.name': "Name",
  'assets.tags': "Tags, separated by commas",
  'assets.file': "File",
  'assets.snippetText': "Text",
  'assets.save': "Save",
  'assets.delete': "Remove from library",
  'assets.deleteBlocked': "It is in use, so it cannot be removed. Take it out of those emails and pages first.",
  'assets.address': "Public address:",
  'noun.asset': "That asset",
  'noun.assetFolder': "That folder",
  'refusal.asset.folderLocked': "Only its editors can change what is in {folder}.",
  'refusal.asset.folderName': "A folder name is 1 to 120 characters.",
  'refusal.asset.folderTaken': "There is already a folder called {name} here.",
  'refusal.asset.name': "Give the asset a name of up to 200 characters.",
  'refusal.asset.empty': "That file is empty.",
  'refusal.asset.tooLarge': "A library file is at most 5 MB.",
  'refusal.asset.imageType': "An image is PNG, JPEG, GIF or WebP.",
  'refusal.asset.notWhatItSays': "That file is not the {type} it says it is.",
  'refusal.asset.documentType': "A document is a PDF, a text or CSV file, or a Word, Excel or PowerPoint file.",
  'refusal.asset.snippetEmpty': "A snippet needs its text.",
  'refusal.asset.templateEmpty': "A template needs at least one block.",
  'refusal.asset.templateBlocks': "This template cannot be saved: {problem}",
  'refusal.asset.inUse': "{name} is used in {count} place(s): {places}. Take it out of those first.",
  'events.title': "Webinars and events",
  'events.new': "New event",
  'events.none': "No events yet.",
  'events.missing': "There is no event at this address.",
  'events.save': "Save event",
  'events.status.draft': "Draft",
  'events.status.open': "Registration open",
  'events.status.closed': "Closed",
  'events.kind': "Kind",
  'events.kind.webinar': "Webinar",
  'events.kind.in-person': "In person",
  'events.name': "Name",
  'events.slug': "Address",
  'events.joinLink': "Join link (sent in reminders, not published)",
  'events.address': "Where",
  'events.startsAt': "Starts",
  'events.endsAt': "Ends",
  'events.capacity': "Capacity (empty for no limit)",
  'events.reminders': "Reminders, in hours before the start",
  'events.form': "Registration form",
  'events.noForm': "No form",
  'events.openRegistration': "Open for registration",
  'events.attendedJourney': "Follow-up for people who came",
  'events.noShowJourney': "Follow-up for people who did not",
  'events.noFollowUp': "No follow-up",
  'events.noJourneys': "Activate a journey to use it as a follow-up for attendees or no-shows.",
  'events.description': "Description",
  'events.funnel': "Registration funnel",
  'events.stage.registered': "Registered",
  'events.stage.confirmed': "Have a place",
  'events.stage.reminded': "Reminded",
  'events.stage.attended': "Attended",
  'events.waitlistCount': "{count} waiting for a place",
  'events.split': "{attended} attended and {noShow} did not; each group was started on its follow-up.",
  'events.remindersSent': "{count} reminder(s) that had come due were sent just now.",
  'events.attendees': "Attendees",
  'events.noAttendees': "Nobody has registered yet.",
  'events.registration.registered': "Registered",
  'events.registration.waitlisted': "Waiting for a place",
  'events.registration.cancelled': "Cancelled",
  'events.attendance.attended': "Attended",
  'events.attendance.no-show': "Did not attend",
  'events.minutes': "{count} min",
  'events.cancelRegistration': "Take off the event",
  'events.attendance': "Attendance",
  'events.attendanceHelp': "Paste the video provider's attendance report: one person per line, email then minutes attended.",
  'events.recordAttendance': "Record attendance",
  'events.unmatched': "Nobody registered with: {emails}",
  'events.close': "Close the event and start follow-ups",
  'events.rowCounts': "{registered} registered · {attended} attended",
  'events.register': "Register",
  'events.registrationClosed': "Registration for this event has closed.",
  'events.fullWaitlist': "This event is full. Register and you will be on the waiting list; you will hear from us if a place opens.",
  'events.youAreRegistered': "You are registered. A reminder will arrive before it starts.",
  'events.youAreWaitlisted': "The event is full, so you are on the waiting list. You will hear from us if a place opens.",
  'events.alreadyRegistered': "You are already registered for this event.",
  'event.waitlisted': "The event is full; you are on the waiting list.",
  'noun.event': "That event",
  'noun.registration': "That registration",
  'refusal.event.name': "Give the event a name of up to 200 characters.",
  'refusal.event.slug': "The address is lower-case letters, numbers and single hyphens, like spring-webinar.",
  'refusal.event.when': "Give the event a start and an end.",
  'refusal.event.endsBeforeStart': "The event ends after it starts.",
  'refusal.event.capacity': "Capacity is a whole number of places, or empty for no limit.",
  'refusal.event.reminder': "A reminder is a whole number of hours before the start, up to two weeks.",
  'refusal.event.noForm': "Choose the registration form before opening registration.",
  'refusal.event.form': "Choose a capture form that is still live.",
  'refusal.event.flow': "A follow-up is one of the workspace’s live journeys.",
  'refusal.event.slugTaken': "Another event already lives at /{slug}.",
  'refusal.event.closed': "This event is closed; its attendance and follow-ups are final.",
  'refusal.event.notOver': "An event is closed after it ends, when its attendance is known.",
  'refusal.event.started': "Registration for this event has closed.",
  'ab.title': "A/B test",
  'ab.none': "No test. Set one up to send variants to part of the audience and the winner to the rest.",
  'ab.setUp': "Set up a test",
  'ab.change': "Change the test",
  'ab.kind': "What to test",
  'ab.kind.subject': "Subject line",
  'ab.kind.content': "Email content",
  'ab.metric': "Winner by",
  'ab.metric.opened': "Opens",
  'ab.metric.clicked': "Clicks",
  'ab.testPercent': "Test share of the audience (%)",
  'ab.minimumSample': "Minimum sends per variant",
  'ab.windowHours': "Results window (hours)",
  'ab.subjectFor': "Subject line for {label}",
  'ab.emailFor': "Email for {label}",
  'ab.chooseEmail': "Choose an email",
  'ab.addVariant': "Add a variant",
  'ab.removeVariant': "Remove",
  'ab.save': "Save the test",
  'ab.cancel': "Cancel",
  'ab.remove': "Remove the test",
  'ab.status.draft': "Ready - starts when the campaign is sent",
  'ab.status.testing': "Testing",
  'ab.status.decided': "Winner sent",
  'ab.status.inconclusive': "Inconclusive - original sent",
  'ab.summary': "{percent}% of the audience, winner by {metric}, at least {minimum} sends per variant.",
  'ab.variant': "Variant",
  'ab.sent': "Sent",
  'ab.rate': "Rate",
  'ab.lift': "Lift vs A",
  'ab.winner': "Winner",
  'ab.sentToRest': "Sent to the rest",
  'ab.confidence': "Confidence the leader is better",
  'ab.decidesAt': "Decided after {at}; {held} people are waiting for the winner.",
  'ab.decide': "Decide now",
  'noun.abTest': "That test",
  'refusal.ab.started': "The test has started; its variants and settings are fixed.",
  'refusal.ab.emailOnly': "A/B tests are for email campaigns.",
  'refusal.ab.percent': "The test share is a whole percentage from 5 to 50.",
  'refusal.ab.sample': "The minimum sample is at least 10 sends per variant.",
  'refusal.ab.window': "The results window is 1 to 168 hours.",
  'refusal.ab.variantCount': "A test has 2 to 4 variants.",
  'refusal.ab.labels': "Each variant has its own short name.",
  'refusal.ab.subject': "Each variant needs its subject line.",
  'refusal.ab.same': "The variants must differ, or there is nothing to test.",
  'refusal.ab.document': "Each variant needs its email.",
  'refusal.ab.tooSmall': "This test needs {needed} people in its share ({variants} variants × {minimum}), and {percent}% of this audience is {share}. Raise the test share, lower the minimum sample, or send without a test.",
  'refusal.ab.decided': "This test has already been decided.",
  'refusal.ab.window.open': "The results window closes at {at}; a winner is not declared before it.",
  'refusal.ab.belowSample': "No winner yet: each variant needs {minimum} sends, and {detail}.",
  'nav.approvals': "Approvals",
  'approvals.title': "Approvals",
  'approvals.waitingOn': "Waiting on {names}",
  'approvals.status.pending': "Pending",
  'approvals.status.approved': "Approved",
  'approvals.status.rejected': "Rejected",
  'approvals.status.withdrawn': "Recalled",
  'approvals.mode.any': "any one approves",
  'approvals.mode.all': "everyone approves",
  'approvals.assignee.pending': "waiting",
  'approvals.assignee.approved': "approved",
  'approvals.assignee.rejected': "rejected",
  'approvals.assignee.delegated': "handed on",
  'approvals.assignee.escalated': "escalated",
  'approvals.assignee.closed': "not needed",
  'approvals.onBehalfOf': "for {name}",
  'approvals.history': "History",
  'approvals.system': "System",
  'approvals.action.submitted': "submitted",
  'approvals.action.approved': "approved",
  'approvals.action.rejected': "rejected",
  'approvals.action.delegated': "handed on",
  'approvals.action.escalated': "escalated",
  'approvals.action.recalled': "recalled",
  'approvals.action.completed': "finished",
  'approvals.viaEmail': "from email",
  'approvals.comment': "Comment (needed to reject)",
  'approvals.approve': "Approve",
  'approvals.reject': "Reject",
  'approvals.recall': "Recall the request",
  'approvals.process': "Approval process",
  'approvals.chooseProcess': "Choose a process",
  'approvals.submit': "Submit for approval",
  'approvals.askedBy': "asked by {name}",
  'approvals.handTo': "Hand to…",
  'approvals.handOn': "Hand on",
  'approvals.inboxEmpty': "Nothing is waiting on you.",
  'approvals.inboxEmptyBody': "Requests that need your approval appear here, with who else is on the chain.",
  'approvals.away': "While I am away",
  'approvals.awaySet': "Your approvals go to your delegate from {from} to {to}.",
  'approvals.awayHelp': "Choose somebody to receive your approvals while you are away.",
  'approvals.delegate': "Delegate",
  'approvals.chooseDelegate': "Choose a person",
  'approvals.from': "From",
  'approvals.until': "Until",
  'approvals.saveAway': "Save",
  'approvals.clearAway': "Stop delegating",
  'approvals.processes': "Approval processes",
  'approvals.newProcess': "New process",
  'approvals.noProcesses': "No processes yet.",
  'approvals.object.deal': "Deals",
  'approvals.object.quote': "Quotes",
  'approvals.object.account': "Companies",
  'approvals.object.contact': "Contacts",
  'approvals.object.lead': "Leads",
  'approvals.stepCount': "{count} step(s)",
  'approvals.inactive': "switched off",
  'approvals.edit': "Edit",
  'approvals.processName': "Name",
  'approvals.appliesTo': "Applies to",
  'approvals.active': "Active",
  'approvals.criteria': "Entry criteria",
  'approvals.criteriaHelp': "A record must match these to be submitted. No conditions means every record.",
  'approvals.stepName': "Step {number}",
  'approvals.approvers': "Approvers",
  'approvals.kind.owner-manager': "The owner's manager",
  'approvals.kind.users': "Named people",
  'approvals.kind.role': "Everyone in a role",
  'approvals.modeLabel': "Completes when",
  'approvals.chooseRole': "Choose a role",
  'approvals.timeout': "Escalate after (hours, optional)",
  'approvals.escalateTo': "Escalate to",
  'approvals.removeStep': "Remove step",
  'approvals.addStep': "Add a step",
  'approvals.saveProcess': "Save process",
  'approvals.cancel': "Cancel",
  'approvals.linkInvalid': "This approval link is not valid or has expired.",
  'approvals.recorded': "Your decision is recorded.",
  'noun.approvalProcess': "That approval process",
  'noun.record': "That record",
  'refusal.process.name': "Give the process a name of up to 200 characters.",
  'refusal.process.steps': "A process has 1 to 10 steps.",
  'refusal.process.stepName': "Step {step} needs a name.",
  'refusal.process.noApprover': "Step {step} needs at least one approver.",
  'refusal.process.noRole': "Step {step} needs its role.",
  'refusal.process.noEscalation': "Step {step} times out, so it needs somebody to escalate to.",
  'refusal.process.criteria': "The entry criteria cannot be used: {problem}",
  'refusal.process.nobody': "Step \"{step}\" has nobody to approve it.",
  'refusal.process.inactive': "{name} is switched off.",
  'refusal.process.criteriaNotMet': "{record} does not meet the criteria for {process}.",
  'refusal.process.alreadyPending': "{record} is already waiting for an approval.",
  'refusal.process.decided': "This request is already {status}.",
  'refusal.process.notYours': "This step is waiting on {names}, not you.",
  'refusal.process.reason': "Say why it is rejected - a \"no\" without a reason is a message somebody has to go and ask about.",
  'refusal.process.recallOwn': "Only the person who asked can recall a request.",
  'refusal.process.delegateOwn': "Only somebody this step is waiting on can hand it on.",
  'refusal.process.delegateSelf': "Choose somebody else to hand it to.",
  'refusal.process.delegationDates': "The delegation ends on or after the day it starts.",
  'jobs.title': "Scheduled jobs",
  'jobs.help': "Work the workspace does on a timetable. Every job can be run now to check it.",
  'jobs.status.succeeded': "Last run succeeded",
  'jobs.status.failed': "Last run failed",
  'jobs.status.missed': "Missed",
  'jobs.status.running': "Running",
  'jobs.next': "Next {at}",
  'jobs.noNext': "No next run",
  'jobs.off': "Switched off",
  'jobs.runNow': "Run now",
  'jobs.details': "Details",
  'jobs.hide': "Hide",
  'jobs.last': "Last run {at}, {ms} ms: {detail}",
  'jobs.schedule': "Schedule (cron, workspace time)",
  'jobs.enabled': "On",
  'jobs.save': "Save",
  'jobs.cronHelp': "Minute, hour, day of month, month, day of week. \"0 8 * * 1-5\" is 08:00 on weekdays; \"*/15 * * * *\" is every fifteen minutes.",
  'jobs.upcoming': "Next runs: {times}",
  'jobs.trigger.schedule': "on schedule",
  'jobs.trigger.manual': "run by hand",
  'noun.jobSchedule': "That scheduled job",
  'refusal.job.cron': "That schedule cannot be used: {reason}.",
  'refusal.job.never': "That schedule never runs.",
  'refusal.job.running': "That job is already running.",
  'webhooks.title': "Webhooks",
  'webhooks.secretOnce': "Signing secret - shown once, copy it now:",
  'webhooks.new': "New webhook",
  'webhooks.name': "Name",
  'webhooks.url': "Address",
  'webhooks.events': "Events",
  'webhooks.create': "Add webhook",
  'webhooks.inbound': "Inbound events",
  'webhooks.inboundHelp': "Another system posts a flat JSON object to a source's address; it starts the chosen automation with those fields.",
  'webhooks.noAutomation': "(automation deleted)",
  'webhooks.off': "switched off",
  'webhooks.tokenOnce': "The source's address - shown once, copy it now:",
  'webhooks.sourceName': "Source name",
  'webhooks.automation': "Automation",
  'webhooks.chooseAutomation': "Choose an automation",
  'webhooks.createSource': "Add source",
  'webhooks.countNote': "{count} endpoint(s). Every delivery is signed (X-Whinerp-Signature) and retried with growing gaps.",
  'webhooks.recent': "Last 7 days: {ok} delivered, {failed} failed",
  'webhooks.test': "Send a test",
  'webhooks.log': "Delivery log",
  'webhooks.hideLog': "Hide log",
  'webhooks.status.pending': "Waiting",
  'webhooks.status.succeeded': "Delivered",
  'webhooks.status.failed': "Failed - will retry",
  'webhooks.status.dead': "Gave up after every retry",
  'webhooks.nextTry': "Next try {at}",
  'webhooks.replay': "Replay",
  'webhooks.payloadAndAttempts': "Payload and {count} attempt(s)",
  'noun.webhook': "That webhook",
  'noun.webhookDelivery': "That delivery",
  'refusal.webhook.url': "That is not a web address.",
  'refusal.webhook.https': "A webhook goes to an https:// address.",
  'refusal.webhook.private': "A webhook cannot point at a private network address.",
  'refusal.webhook.name': "Give the webhook a name.",
  'refusal.webhook.events': "Choose at least one event.",
  'refusal.webhook.unknownEvent': "Not an event this product sends: {events}.",
  'refusal.inbound.name': "Give the source a name.",
  'refusal.inbound.flow': "An inbound source starts one of the workspace’s automations.",
  'stream.title': "Event stream",
  'stream.help': "Subscribers read record and custom events in order from their position, acknowledge what they have handled, and can replay.",
  'stream.behind': "{count} behind; oldest waiting {minutes} min",
  'stream.caughtUp': "Caught up",
  'stream.position': "At {cursor} of {latest}",
  'stream.polled': "last read {at}",
  'stream.neverPolled': "never read",
  'stream.replayAll': "Replay from the start",
  'stream.name': "Subscriber name",
  'stream.events': "Events, e.g. deal.*, contact.created",
  'stream.fromStart': "Read existing events too",
  'stream.add': "Add subscriber",
  'noun.eventSubscriber': "That subscriber",
  'refusal.stream.name': "A custom event is named in lower-case letters and underscores, like order_shipped.",
  'refusal.stream.subscriberName': "Give the subscriber a name.",
  'refusal.stream.pattern': "Events are like deal.*, contact.created, custom.* or *.",
  'recipes.browse': "Browse ready-made templates",
  'recipes.title': "Automation templates",
  'backup.notRestorable': 'These tables are in the backup but cannot be restored, so this restore will leave them as they are:',
  'viewings.title': "Viewings and site visits",
  'viewings.none': "No viewings booked yet.",
  'viewings.status.planned': "Booked",
  'viewings.status.arrived': "Arrived",
  'viewings.status.done': "Held",
  'viewings.status.missed': "Missed by us",
  'viewings.status.no-show': "They did not come",
  'viewings.status.cancelled': "Cancelled",
  'viewings.status.rescheduled': "Moved",
  'viewings.interest': "How interested",
  'viewings.interest.hot': "Hot",
  'viewings.interest.warm': "Warm",
  'viewings.interest.cold': "Cold",
  'viewings.interest.not-interested': "Not interested",
  'viewings.outcome': "Say what happened",
  'viewings.note': "Note",
  'viewings.held': "It was held",
  'viewings.noShow': "They did not come",
  'viewings.cancel': "Cancel it",
  'viewings.moved': "Moved {count} time(s).",
  'viewings.starts': "Starts",
  'viewings.ends': "Ends",
  'viewings.location': "Where",
  'viewings.book': "Book a viewing",
  'dealDocuments.title': "Documents",
  'dealDocuments.unsorted': "Not sorted yet",
  'dealDocuments.kind': "Kind of document",
  'dealDocuments.kind.proposal': "Proposals",
  'dealDocuments.kind.drawing': "Drawings",
  'dealDocuments.kind.specification': "Specifications",
  'dealDocuments.kind.contract': "Contracts",
  'dealDocuments.kind.other': "Other",
  'dealDocuments.version': "version {n}",
  'dealDocuments.onQuote': "on quote {number}",
  'dealDocuments.generated': "generated",
  'activityOutcomes.title': "What came before won deals",
  'activityOutcomes.help': "Deals closed in the last year: the win rate of those that had each kind of activity before closing, beside those that had none. It shows what tended to happen, not what caused a win.",
  'activityOutcomes.none': "No closed deals with activity yet.",
  'activityOutcomes.activity': "Activity",
  'activityOutcomes.withIt': "Win rate with it",
  'activityOutcomes.withoutIt': "Win rate without",
  'activityOutcomes.beforeWin': "Typical before a win",
  'activityOutcomes.beforeLoss': "Typical before a loss",
  'activityOutcomes.tooFew': "Too few deals to say (fewer than {n} on one side)",
  'billingPlan.title': "Payment plan",
  'billingPlan.help': "Split each order into deposit and stage payments. A pro-forma asks for a payment without being a tax invoice, and nothing is counted as owed until it is converted.",
  'billingPlan.order': "Order {number} - {planned}% planned",
  'billingPlan.draft': "draft",
  'billingPlan.stageInvoiced': "Invoiced ({number})",
  'billingPlan.stageRequested': "Requested on pro-forma {number}",
  'billingPlan.convert': "Paid - raise the invoice",
  'billingPlan.requestProforma': "Request with a pro-forma",
  'billingPlan.invoice': "Invoice this stage",
  'billingPlan.invoiced': "Draft invoice raised for {amount}. Issue it from Invoices.",
  'billingPlan.requested': "Pro-forma {number} raised for {amount}.",
  'billingPlan.converted': "Invoice raised for exactly what the pro-forma asked. Issue it from Invoices.",
  'billingPlan.label': "Stage",
  'billingPlan.percent': "% of order",
  'billingPlan.add': "Add stage",
  'revenue.more': "More on revenue",
  'commission.title': "Commission",
  'commission.me': "Me",
  'commission.person': "Person",
  'commission.period': "Period",
  'commission.noPlan': "{name} is not on a commission plan.",
  'commission.onPlan': "On {plan}",
  'commission.credited': "Credited",
  'commission.target': "Target",
  'commission.noTarget': "No target set - base rate only",
  'commission.attainment': "Of target",
  'commission.payout': "Commission",
  'commission.band': "Band",
  'commission.amount': "Credit in band",
  'commission.rate': "Rate",
  'commission.base': "Base",
  'commission.above': "Above {from}% of target",
  'commission.approved': "Approved at {amount}.",
  'commission.approvedDiffers': "Approved at {amount}. Deals have changed since: today it would be {difference} different. The approved statement stays as it was.",
  'commission.approve': "Approve this statement",
  'commission.deals': "Deals credited",
  'commission.split': "{split}% of {amount}",
  'commission.leftOut': "Left out: {reason}",
  'commission.plans': "Commission plans",
  'commission.planRates': "{base}% base; {tiers}",
  'commission.assignWho': "Put on a plan:",
  'commission.putOn': "Put on this plan",
  'commission.planName': "Plan name",
  'commission.currency': "Currency",
  'commission.basePercent': "Base %",
  'commission.tiers': "Accelerators (from % of target : rate)",
  'commission.savePlan': "Add plan",
  'commission.failed': "Commission could not be loaded.",
  'capacity.title': "Capacity and coverage - {period}",
  'capacity.wanted': "Coverage wanted",
  'capacity.person': "Person",
  'capacity.remaining': "Quota still to find",
  'capacity.pipeline': "Open pipeline this period",
  'capacity.coverage': "Coverage",
  'capacity.noTarget': "No target",
  'capacity.short': "short",
  'capacity.uncovered': "{accounts} companies, and nobody active is assigned",
  'capacity.territory': "{accounts} companies, {people} people, {each} each",
  'capacity.headcount': "The quota of {target} needs {needed} people at {each} each per quarter, what a person has brought in. {have} have targets.",
  'capacity.headcountUnknown': "Too little history to say how many people the quota needs ({quarters} quarter(s) of won business; two are needed).",
  'capacity.otherCurrency': "{count} target(s) or deal group(s) in another currency are left out; this reads {currency} only.",
  'sendTime.title': "Best time to email",
  'sendTime.unknown': "Not enough engagement yet to say. Emails go when they are sent.",
  'sendTime.person': "Around {hour}, from their own {clicks} click(s) and {opens} open(s).",
  'sendTime.workspace': "Around {hour} - too little of their own history, so the hour your audience engages most.",
  'directMail.title': "Mailing file and replies",
  'directMail.help': "Prepare the file for your mailing house. Everyone on it gets a code for their reply card; codes stay the same if you prepare it again.",
  'directMail.rate': "{mailed} mailed, {responded} replied",
  'directMail.prepare': "Prepare the mailing file",
  'directMail.included': "{count} on the file",
  'directMail.excluded': "{count} left off: {reason}",
  'directMail.code': "Response code",
  'directMail.note': "Note",
  'directMail.record': "Record reply",
  'directMail.recorded': "Recorded for {name}.",
  'directMail.already': "{name} had already replied; counted once.",
  'capture.title': "People met at the event",
  'capture.scan': "Scan a badge",
  'capture.scanHint': "Scan with the handheld scanner, or paste the badge text",
  'capture.firstName': "First name",
  'capture.lastName': "Last name",
  'capture.email': "Email",
  'capture.phone': "Phone",
  'capture.company': "Company",
  'capture.jobTitle': "Job title",
  'capture.interest': "Interest",
  'capture.interest.hot': "Hot",
  'capture.interest.warm': "Warm",
  'capture.interest.cold': "Cold",
  'capture.note': "What they asked about",
  'capture.save': "Save",
  'capture.newLead': "Saved as a new lead.",
  'capture.known': "Already known - added to their record.",
  'capture.duplicate': "Already captured - nothing added twice.",
  'capture.queued': "No connection: kept on this device, and sent when there is one.",
  'referrals.title': "Referrals",
  'referrals.code': "Their referral code:",
  'referrals.issue': "Give them a referral code",
  'referrals.none': "Nobody referred yet.",
  'referrals.status.referred': "Referred",
  'referrals.status.won': "Won",
  'referrals.status.lost': "Lost",
  'referrals.reward.pending': "Reward {amount} to pay",
  'referrals.reward.paid': "Reward {amount} paid",
  'referrals.markPaid': "Mark paid",
  'referrals.referredBy': "Referred by (code)",
  'referrals.link': "Link",
  'referrals.linked': "Referred by {name}.",
  'referrals.programme': "Referral programme",
  'referrals.active': "On",
  'referrals.rewardKind': "Reward",
  'referrals.kind.fixed': "A fixed amount per won deal",
  'referrals.kind.percent': "A percentage of the won deal",
  'referrals.amount': "Amount",
  'referrals.currency': "Currency",
  'referrals.save': "Save",
  'budget.title': "Budget and spend",
  'budget.planned': "Planned",
  'budget.actual': "Spent",
  'budget.remaining': "Left",
  'budget.unknown': "Not recorded",
  'budget.over': "{amount} over",
  'budget.setPlanned': "Planned budget",
  'budget.save': "Save",
  'budget.remove': "Remove",
  'budget.otherCurrency': "In {currency} - not added",
  'budget.amount': "Amount",
  'budget.description': "What it was for",
  'budget.supplier': "Supplier",
  'budget.record': "Record spend",
  'segment.kind.live': "Live",
  'segment.kind.static': "Frozen list",
  'segment.list.freeze': "Freeze as a list",
  'lifecycle.title': "Lifecycle",
  'lifecycle.stage.subscriber': "Subscriber",
  'lifecycle.stage.lead': "Lead",
  'lifecycle.stage.mql': "MQL",
  'lifecycle.stage.sql': "SQL",
  'lifecycle.stage.customer': "Customer",
  'lifecycle.stage.advocate': "Advocate",
  'lifecycle.reportTitle': "Where new people got to",
  'lifecycle.period': "Arrived in",
  'lifecycle.lastDays': "Last {days} days",
  'lifecycle.move': "{from} to {to}: {went} of {reached}, typically {days} days",
  'frequencyCap.title': "Frequency cap",
  'frequencyCap.help': "Across every campaign and journey, per channel. Receipts, invoices and other transactional messages are never held back.",
  'frequencyCap.atMost': "At most",
  'frequencyCap.messages': "Messages",
  'frequencyCap.every': "marketing messages per person every",
  'frequencyCap.days': "Days",
  'frequencyCap.daysSuffix': "days",
  'frequencyCap.save': "Save",
  'journey.earlierVersions': "{count} people are part-way through an earlier version. They will finish the version they entered; changes here (version {version} onwards) reach people who start from now.",
  'influence.title': "Campaigns behind won deals",
  'influence.period': "Deals won in",
  'influence.lastDays': "Last {days} days",
  'influence.help': "{deals} deals won, {won}. Every campaign that reached somebody on a deal before it was won counts the whole deal, so these overlap and do not add up - attribution splits it.",
  'influence.none': "No campaign reached anybody on a deal won in this period.",
  'influence.campaign': "Campaign",
  'influence.engaged': "Engaged (opened, clicked, replied)",
  'influence.reached': "Reached (sent)",
  'abm.title': "Target accounts",
  'abm.list': "Target list",
  'abm.company': "Company",
  'abm.tier': "Tier",
  'abm.engaged': "People engaged (30 days)",
  'abm.lastTouch': "Last touch",
  'abm.pipeline': "Open pipeline",
  'abm.ofPeople': "{engaged} of {people}",
  'abm.never': "Never",
  'abm.emptyList': "No companies on this list yet. Add them from a company’s record.",
  'abm.audience.1': "Audience: tier 1",
  'abm.audience.2': "Audience: tiers 1-2",
  'abm.audience.3': "Audience: all tiers",
  'abm.audienceMade': "Audience of {count} people saved to Audiences.",
  'abm.newList': "New target list",
  'abm.create': "Create",
  'abm.onLists': "Target lists",
  'abm.notTargeted': "Not on a target list.",
  'abm.tierN': "Tier {tier}",
  'abm.chooseList': "Choose a list",
  'abm.add': "Add",
  'dynamic.title': "Who sees each block",
  'dynamic.help': "Everybody sees a block unless you narrow it. The unsubscribe footer is always shown.",
  'dynamic.who': "Who sees it",
  'dynamic.kind.everyone': "Everybody",
  'dynamic.kind.field': "People whose…",
  'dynamic.kind.segment': "An audience",
  'dynamic.kind.engaged': "Recent clickers",
  'dynamic.field': "Field",
  'dynamic.field.country': "Country",
  'dynamic.field.city': "City",
  'dynamic.field.company': "Company",
  'dynamic.field.title': "Job title",
  'dynamic.field.language': "Language",
  'dynamic.operator': "Condition",
  'dynamic.operator.eq': "is",
  'dynamic.operator.neq': "is not",
  'dynamic.operator.set': "is known",
  'dynamic.operator.unset': "is not known",
  'dynamic.value': "Value",
  'dynamic.inOrOut': "In or out",
  'dynamic.inSegment': "In",
  'dynamic.notInSegment': "Not in",
  'dynamic.clicked': "Clicked an email in the last",
  'dynamic.notClicked': "Has not clicked in the last",
  'dynamic.days': "days",
  'dynamic.block.text': "Text",
  'dynamic.block.heading': "Heading",
  'dynamic.block.image': "Image",
  'dynamic.block.button': "Button",
  'dynamic.block.columns': "Columns",
  'dynamic.block.quote': "Quote",
  'dynamic.block.video': "Video",
  'dynamic.block.line-items': "Line items",
  'chatbot.script': "Chat script",
  'chatbot.none': "No script - every chat goes straight to a person",
  'chatbot.save': "Save",
  'chatbot.try': "Try the chat",
  'chatbot.message': "Message",
  'chatbot.send': "Send",
  'bookingPage.missing': "This booking page is not available.",
  'bookingPage.booked': "You're booked",
  'bookingPage.bookedWith': "{title} with {owner}, {when}. A confirmation is on its way.",
  'bookingPage.addToCalendar': "Add to your calendar",
  'bookingPage.refused.taken': "Somebody booked that time while you were choosing. Pick another - what you typed is kept.",
  'bookingPage.refused.not-offered': "That time is not on offer. Pick one of the times shown.",
  'bookingPage.refused.missing': "This booking page is not available.",
  'bookingPage.noSlots': "No free times in the next two weeks.",
  'bookingPage.name': "Your name",
  'bookingPage.email': "Your email",
  'bookingPage.note': "Anything to tell them first?",
  'bookingPage.confirm': "Book this time",
  'bookingPages.title': "Your hours and booking pages",
  'bookingPages.help': "The hours you can be booked in, and the pages people book them from.",
  'bookingPages.day.0': "Sunday",
  'bookingPages.day.1': "Monday",
  'bookingPages.day.2': "Tuesday",
  'bookingPages.day.3': "Wednesday",
  'bookingPages.day.4': "Thursday",
  'bookingPages.day.5': "Friday",
  'bookingPages.day.6': "Saturday",
  'bookingPages.from': "From",
  'bookingPages.to': "To",
  'bookingPages.length': "Meetings last",
  'bookingPages.minutes': "{n} minutes",
  'bookingPages.saveHours': "Save hours",
  'bookingPages.pageTitle': "Page title",
  'bookingPages.address': "Address",
  'bookingPages.create': "Create booking page",
  'inbound.outcome.case-created': "Opened a case",
  'inbound.outcome.case-threaded': "Added to its case",
  'inbound.purpose': "What it receives",
  'inbound.purpose.leads': "Enquiries - become leads",
  'inbound.purpose.cases': "Support - become cases",
  'chat.toCase': "Make it a case",
  'chat.caseMade': "{number} opened",
  'chat.caseExists': "Already {number}",
  'case.new': "Log a case",
  'case.cameIn': "Came in by",
  'case.source.phone': "Phone",
  'case.source.manual': "In person",
  'case.source.email': "Email",
  'case.source.chat': "Chat",
  'case.source.portal': "Portal",
  'case.whatTheySaid': "What they said",
  'case.log': "Log case",
  'portal.help.title': "Ask for help",
  'portal.help.subject': "What is it about?",
  'portal.help.body': "What happened?",
  'portal.help.send': "Send",
  'portal.help.raised': "Thanks - your request is {number}. Quote it if you write to us about it.",
  'survey.thanks': "Thank you - that helps.",
  'survey.already': "This was already answered. Thank you.",
  'survey.missing': "This survey link is not valid or has expired.",
  'survey.question.csat': "How satisfied were you with how we handled this?",
  'survey.question.nps': "How likely are you to recommend us to a colleague?",
  'survey.question.ces': "How easy was it to get this resolved?",
  'survey.scale.csat': "1 = very dissatisfied, 5 = very satisfied",
  'survey.scale.nps': "0 = not at all likely, 10 = extremely likely",
  'survey.scale.ces': "1 = very hard, 7 = very easy",
  'survey.comment': "Anything you would like to add?",
  'survey.send': "Send",
  'survey.panel': "Customer satisfaction",
  'survey.measure': "Measure",
  'survey.off': "Surveys off",
  'survey.kind.csat': "CSAT",
  'survey.kind.nps': "NPS",
  'survey.kind.ces': "Effort (CES)",
  'survey.summary': "{measure} {score} - {answered} answered of {sent} asked, last 90 days",
  'survey.unassigned': "Unassigned",
  'custHealth.title': "Customer health",
  'custHealth.noData': "Not enough on record to score yet.",
  'custHealth.score': "{score} - {band}",
  'custHealth.band.healthy': "healthy",
  'custHealth.band.watch': "worth watching",
  'custHealth.band.at-risk': "at risk",
  'custHealth.signal.usage': "Usage",
  'custHealth.signal.support': "Support",
  'custHealth.signal.engagement': "Contact",
  'custHealth.signal.payment': "Payment",
  'custHealth.missing': "Not scored, no data: {signals}.",
  'custHealth.listTitle': "Customers needing attention",
  'custHealth.allWell': "All {count} customers look healthy.",
  'custOnboarding.title': "Onboarding",
  'custOnboarding.none': "Not being onboarded.",
  'custOnboarding.start': "Start onboarding",
  'custOnboarding.complete': "Onboarding complete.",
  'custOnboarding.progress': "{done} of {total} steps done",
  'custOnboarding.doneBy': "Done by {name}",
  'custOnboarding.overdue': "Overdue since {date}",
  'custOnboarding.due': "Due {date}",
  'custOnboarding.template': "Onboarding steps for new customers",
  'custOnboarding.templateHelp': "A won deal starts these for the customer. Plans already running keep the steps they started with.",
  'custOnboarding.stepName': "Step",
  'custOnboarding.days': "Days",
  'custOnboarding.daysAfter': "days after the win",
  'custOnboarding.addStep': "Add step",
  'custOnboarding.save': "Save steps",
  'success.title': "Success plan",
  'success.reviewOverdue': "A business review is due now.",
  'success.reviewDue': "Next business review due {date}.",
  'success.status': "Status",
  'success.status.on-track': "On track",
  'success.status.at-risk': "At risk",
  'success.status.achieved': "Achieved",
  'success.status.dropped': "Dropped",
  'success.objective': "Objective",
  'success.measure': "How you will both know it is met",
  'success.due': "Due",
  'success.addObjective': "Add objective",
  'success.agreed': "Agreed: {text}",
  'success.reviewSummary': "What the review covered",
  'success.reviewAgreed': "What was agreed",
  'success.recordReview': "Record today’s review",
  'dispatch.title': "On-site jobs",
  'dispatch.day': "Day",
  'dispatch.none': "No on-site jobs this day.",
  'dispatch.technician': "Technician",
  'dispatch.unassigned': "Nobody yet",
  'dispatch.caseTitle': "On-site visits",
  'dispatch.report': "What was done on site",
  'dispatch.complete': "Mark done",
  'dispatch.starts': "Starts",
  'dispatch.ends': "Ends",
  'dispatch.location': "Where",
  'dispatch.schedule': "Book a visit",
  'installed.title': "Installed assets",
  'installed.cover.under-warranty': "Under warranty until {date}",
  'installed.cover.expired': "Warranty ended {date}",
  'installed.cover.none': "No warranty recorded",
  'installed.name': "What it is",
  'installed.serial': "Serial number",
  'installed.location': "Where it is",
  'installed.installed': "Installed on",
  'installed.register': "Register asset",
  'installed.caseTitle': "Asset",
  'installed.noneChosen': "Not about a registered asset",
  'complaints.title': "Complaints register",
  'complaints.policy': "Acknowledge within {ack} days; final response within {respond} days.",
  'complaints.acknowledged': "Acknowledged {date}",
  'complaints.ackDue': "Acknowledge by {date}",
  'complaints.responded': "Final response {date}",
  'complaints.respondDue': "Final response due {date}",
  'complaints.caseTitle': "Formal complaint",
  'complaints.category': "What the complaint is about",
  'complaints.mark': "Record as a complaint",
  'complaints.finalPlaceholder': "The final written response to the customer",
  'complaints.sendFinal': "Send final response",
  'dealAnalysis.title': "Closed deals by segment",
  'dealAnalysis.by': "Break down by",
  'dealAnalysis.period': "Closed in",
  'dealAnalysis.lastDays': "Last {days} days",
  'dealAnalysis.dimension.owner': "Owner",
  'dealAnalysis.dimension.source': "Source",
  'dealAnalysis.dimension.industry': "Industry",
  'dealAnalysis.dimension.segment': "Segment",
  'dealAnalysis.dimension.size': "Deal size",
  'dealAnalysis.closed': "Closed",
  'dealAnalysis.winRate': "Win rate",
  'dealAnalysis.wonValue': "Won",
  'dealAnalysis.avgSize': "Average won",
  'dealAnalysis.daysToWin': "Days to win",
  'dealAnalysis.few': "(too few to read much into)",
  'dealAnalysis.lossReasons': "Why {lost} were lost",
  'dealAnalysis.competitors': "Against competitors",
  'dealAnalysis.record': "won {won}, lost {lost}",
  'roi.title': "Return by source",
  'roi.by': "Group by",
  'roi.byCampaign': "Campaign",
  'roi.bySource': "Source",
  'roi.campaign': "Campaign",
  'roi.source': "Source",
  'roi.leads': "Leads",
  'roi.won': "Won",
  'roi.revenue': "Revenue",
  'roi.cost': "Cost",
  'roi.return': "Return",
  'roi.noCost': "not recorded",
  'marginReport.title': 'Revenue and margin',
  'marginReport.by': 'Group by',
  'marginReport.group.product': 'Product',
  'marginReport.group.owner': 'Owner',
  'marginReport.group.month': 'Month',
  'marginReport.revenue': 'Revenue',
  'marginReport.cost': 'Cost',
  'marginReport.margin': 'Margin',
  'marginReport.marginPct': 'Margin %',
  'marginReport.uncosted': 'Revenue with no cost',
  'marginReport.unknown': 'Unknown',
  'marginReport.basis': "Confirmed orders in the last year, costed at each product's current landed cost. Revenue on products with no cost is shown apart and left out of the margin.",
  'execPack.title': "Leadership pack",
  'execPack.span': "{from} to {to}, against {prevFrom} to {prevTo}",
  'execPack.period': "Period",
  'execPack.period.month': "Month",
  'execPack.period.quarter': "Quarter",
  'execPack.period.year': "Year",
  'execPack.print': "Print",
  'execPack.current': "This period",
  'execPack.previous': "Previous",
  'execPack.change': "Change",
  'execPack.marginWithheld': "Margin is left out: you are not permitted to see cost.",
  'execPack.metric.bookings': "Bookings (won value)",
  'execPack.metric.dealsWon': "Deals won",
  'execPack.metric.winRate': "Win rate",
  'execPack.metric.newPipeline': "New pipeline",
  'execPack.metric.ordered': "Orders confirmed",
  'execPack.metric.margin': "Margin on orders",
  'execPack.metric.invoiced': "Invoiced",
  'execPack.metric.overdue': "Overdue",
  'execPack.metric.casesOpened': "Cases opened",
  'execPack.metric.casesResolved': "Cases resolved",
  'execPack.lastYear': "Last year",
  'execPack.baseline': "Usual",
  'execPack.againstBaseline': "Against usual",
  'execPack.baselineNote': "Last year is the same span a year earlier. Usual is the average of the four spans before this one.",
  'recordTrigger.title': "What starts it",
  'recordTrigger.object': "Kind of record",
  'recordTrigger.choose': "Choose…",
  'recordTrigger.field': "Only when this field changes",
  'recordTrigger.anyField': "Any field",
  'recordTrigger.to': "To this value",
  'recordTrigger.anyValue': "Any value",
  'recordTrigger.note': "An automation does not set itself off: changes it makes do not start it again. If it fails, the record is still saved and the failure shows in the run log.",
  'recordTrigger.objectName.contact': "Contact",
  'recordTrigger.objectName.account': "Company",
  'recordTrigger.objectName.lead': "Lead",
  'recordTrigger.objectName.deal': "Deal",
  'recordTrigger.objectName.quote': "Quote",
  'recordTrigger.objectName.support_case': "Case",
  'recordTrigger.objectName.campaign': "Campaign",
  'routing.object': "What the rules assign",
  'routing.object.lead': "Leads",
  'routing.object.support_case': "Cases",
  'routing.priority': "Only cases of priority",
  'routing.priority.any': "Any priority",
  'routing.casesNote': "A new case goes to the first rule that matches it. A case no rule matches waits in the unassigned queue; a rule never takes a case from the agent already working it.",
  'escalationRules.title': "Escalation rules",
  'escalationRules.body': "When work goes undone for too long, the owner and the first staffed role above them hear, or the person you name. Each record is escalated once per rule; the check runs every 15 minutes.",
  'escalationRules.name': "Name",
  'escalationRules.object': "Watch",
  'escalationRules.object.task': "Overdue tasks",
  'escalationRules.object.deal': "Untouched open deals",
  'escalationRules.object.lead': "Unworked leads",
  'escalationRules.object.case': "Unanswered cases",
  'escalationRules.after': "After (hours)",
  'escalationRules.who': "Tell",
  'escalationRules.tellsManager': "The owner and their manager",
  'escalationRules.tells': "Tells {name}",
  'escalationRules.fired': "{count} escalated so far",
  'escalationRules.add': "Add rule",
  'escalationRules.when.task': "A task {hours} h past due",
  'escalationRules.when.deal': "An open deal with nothing logged for {hours} h",
  'escalationRules.when.lead': "A lead unworked {hours} h after it came in",
  'escalationRules.when.case': "A case unanswered {hours} h after it was raised",
  'setup.import.object': "What you are bringing in",
  'setup.import.object.account': "Companies",
  'setup.import.object.contact': "Contacts",
  'setup.import.object.lead': "Leads",
  'setup.import.source': "Exported from",
  'setup.import.source.other': "A spreadsheet or another tool",
  'setup.import.source.salesforce': "Salesforce",
  'setup.import.source.hubspot': "HubSpot",
  'setup.import.source.zoho': "Zoho CRM",
  'setup.import.source.pipedrive': "Pipedrive",
  'setup.import.file': "CSV file",
  'setup.import.unmatched': "Not brought in unless you map them: {columns}",
  'dataQuality.title': "Data quality",
  'dataQuality.body': "How complete, duplicated and stale the records are. Duplicates share an exact email or company name; stale means nothing edited or logged for the window.",
  'dataQuality.object.contact': "Contacts",
  'dataQuality.object.account': "Companies",
  'dataQuality.object.lead': "Open leads",
  'dataQuality.object.deal': "Open deals",
  'dataQuality.complete': "{percent} complete",
  'dataQuality.duplicates': "In {groups} duplicate group(s)",
  'dataQuality.stale': "Untouched for {days} days",
  'dataQuality.topDuplicates': "Most repeated: {list}",
  'dataQuality.check.noEmail': "No email",
  'dataQuality.check.noPhone': "No phone",
  'dataQuality.check.noCompany': "No company",
  'dataQuality.check.noTitle': "No job title",
  'dataQuality.check.noOwner': "No owner",
  'dataQuality.check.noDomain': "No domain or website",
  'dataQuality.check.noIndustry': "No industry",
  'dataQuality.check.noContacts': "No contacts",
  'dataQuality.check.noSource': "No source",
  'dataQuality.check.noAmount': "No amount",
  'dataQuality.check.noCloseDate': "No close date",
  'dataQuality.check.closeDatePassed': "Close date passed",
  'audit.export': "Export the trail",
  'audit.exported': "{count} entries exported as JSON lines, each hash-chained to the last. Last hash {hash}… — keep it to check the next export against.",
  'sessions.title': "Signed-in sessions",
  'sessions.body': "Everyone signed in, on which device and from where. Signing a person out ends all their sessions at once; the reason goes on the audit trail.",
  'sessions.signOutOthers': "Sign out my other devices",
  'sessions.othersEnded': "Signed out of {count} other device(s).",
  'sessions.reason': "Reason for signing someone out",
  'sessions.reasonFirst': "Give a reason first",
  'sessions.count': "{count} session(s)",
  'sessions.forceSignOut': "Sign out everywhere",
  'sessions.unknownDevice': "Unknown device",
  'sessions.lastSeen': "last seen {when}",
  'sessions.thisOne': "this session",
  'sessions.support': "support visit",
  'ipAllowlist.title': "Allowed networks",
  'ipAllowlist.body': "Leave empty to allow sign-in from anywhere. With any range here, people and API tokens outside every range are refused. Support visits are not affected.",
  'ipAllowlist.yourIp': "You are on {ip}.",
  'ipAllowlist.off': "No restriction: the workspace can be used from anywhere.",
  'ipAllowlist.range': "Address or range",
  'ipAllowlist.label': "Which network",
  'ipAllowlist.add': "Allow",
  'evidence.title': "Security evidence",
  'evidence.body': "Each control measured from this workspace as of {when}, with where it usually sits in SOC 2 and ISO 27001.",
  'evidence.download': "Download",
  'evidence.print': "Print",
  'evidence.status.met': "Met",
  'evidence.status.partial': "Partly met",
  'evidence.status.not-met': "Not met",
  'evidence.status.hosting': "Hosting provider",
  'evidence.control.mfa': "Second factor at sign-in",
  'evidence.control.sso': "Single sign-on",
  'evidence.control.privilegedAccess': "Administrator access",
  'evidence.control.leavers': "Leavers signed out",
  'evidence.control.network': "Network restriction",
  'evidence.control.sessions': "Session oversight",
  'evidence.control.apiTokens': "API tokens",
  'evidence.control.auditTrail': "Audit trail",
  'evidence.control.backups': "Backups and restore checks",
  'evidence.control.encryption': "Encryption",
  'evidence.control.secrets': "Stored secrets encrypted",
  'wsSandbox.banner': "{name} is a sandbox: changes here do not reach production, and its records are not real customers.",
  'wsSandbox.mark': "This workspace is a sandbox",
  'wsSandbox.how': "To set one up: create a second workspace (a separate sign-up), mark it as a sandbox here, then export this configuration and apply it there. Pipelines, fields, layouts, permission sets, automations (as drafts), routing and escalation rules and close reasons travel; records never do. Promote changes back the same way.",
  'ask.title': "Ask a question",
  'ask.placeholder': "How many open deals by stage? Total deal value by owner?",
  'ask.submit': "Ask",
  'ask.byModel': "Understood by the model, and run as a report with your access.",
  'ask.byKeywords': "Read from the words of the question, and run as a report with your access.",
  'ask.modelProblem': "(The model’s plan could not be used: {problem})",
  'ask.save': "Save as a report",
  'account.signOut': "Sign out",
  'account.signingOut': "Signing out…",
  'invoices.title': "Invoices",
  'nurture.title': "Nurture for leads not ready yet",
  'nurture.help': "Leads that are not ready for sales go into a nurture journey on their own - once each, and only if they would receive its emails - and come out the moment they become marketing-qualified or are converted.",
  'nurture.journey': "Nurture journey",
  'nurture.off': "No nurture",
  'nurture.days': "Nurture a lead still below the threshold after this many days",
  'nurture.days.off': "Off",
  'nurture.onRejection': "Nurture a lead sales turns back, for these reasons:",
  'nurture.reasons': "Reasons that mean “not yet”",
  'nurture.reasons.none': "No reason chosen, so no turned-back lead will be nurtured.",
  'nurture.save': "Save nurture",
  'nurture.wayIn.rejected': "turned back by sales",
  'nurture.wayIn.not-ready': "not ready yet",
  'nurture.inNow': "in nurture now",
  'nurture.left': "left: {reason}",
  'journey.emails': "What the emails say",
  'journey.emails.help': "Each email step sends to the person in the journey, if they have agreed to marketing email. {firstName}, {lastName} and {company} are filled in for each person.",
  'journey.emails.step': "Email {n}",
  'journey.emails.empty': "Nothing written yet - this step will not send",
  'journey.emails.subject': "Subject",
  'journey.emails.body': "Message",
  'inbound.title': "Inbound mailboxes",
  'inbound.help': "Mail posted to a mailbox becomes a lead, or goes on the person it is already from. Automatic replies and bounces are ignored, and replies stay with their conversation. An email provider's inbound webhook posts the mail here.",
  'inbound.empty': "No inbound mailboxes yet.",
  'inbound.label': "Mailbox name",
  'inbound.create': "Create mailbox",
  'inbound.created.once': "Copy these now. They are not shown again - only a fingerprint of the address is kept.",
  'inbound.created.url': "Post mail to (on this workspace's API address):",
  'inbound.created.secret': "Signing secret (HMAC-SHA256 of the body, in the x-whinerp-signature header):",
  'inbound.created.done': "I have copied them",
  'inbound.state.on': "On",
  'inbound.state.off': "Switched off",
  'inbound.received': "{count} received",
  'inbound.switchOff': "Switch off",
  'inbound.switchOn': "Switch on",
  'inbound.recent': "Recent mail",
  'inbound.recent.none': "Nothing received yet.",
  'inbound.outcome.lead-created': "New lead",
  'inbound.outcome.lead-matched': "Added to their lead",
  'inbound.outcome.contact-matched': "Added to their contact",
  'inbound.outcome.threaded': "Added to the conversation",
  'inbound.outcome.ignored': "Ignored",
  'customerHistory.title': "History",
  'customerHistory.filter': "Show",
  'customerHistory.empty': "Nothing on record with this company yet.",
  'customerHistory.more': "Show older",
  'customerHistory.withheld': "{count} {kind} you do not have access to.",
  'customerHistory.balance.clear': "Nothing outstanding.",
  'customerHistory.balance.detail': "outstanding on {invoices} invoice(s), {overdue} of it overdue",
  'customerHistory.kind.all': "All",
  'customerHistory.kind.deal': "Deals",
  'customerHistory.kind.quote': "Quotes",
  'customerHistory.kind.order': "Orders",
  'customerHistory.kind.invoice': "Invoices",
  'customerHistory.kind.payment': "Payments",
  'customerHistory.kind.ticket': "Tickets",
  'customerHistory.kind.campaign': "Campaigns",
  'customerHistory.kind.subscription': "Subscriptions",
  'customerPrefs.title': "Preferences",
  'customerPrefs.edit': "Edit",
  'customerPrefs.save': "Save preferences",
  'customerPrefs.cancel': "Cancel",
  'customerPrefs.notSaid': "Not said",
  'customerPrefs.source.contact': "their own",
  'customerPrefs.source.account': "from the company",
  'customerPrefs.source.workspace': "workspace default",
  'customerPrefs.source.none': "not set",
  'customerPrefs.terms': "Payment terms",
  'customerPrefs.terms.days': "{days} days",
  'customerPrefs.terms.field': "Payment terms (days)",
  'customerPrefs.terms.default': "Workspace default",
  'customerPrefs.language': "Language",
  'customerPrefs.language.field': "Language (two letters)",
  'customerPrefs.language.en': "English",
  'customerPrefs.language.ar': "Arabic",
  'customerPrefs.language.notHonoured': "They prefer “{stated}”, which documents cannot be written in, so documents go out in {used}.",
  'customerPrefs.channel': "Prefers to be reached by",
  'customerPrefs.channel.email': "Email",
  'customerPrefs.channel.phone': "Phone",
  'customerPrefs.channel.sms': "SMS",
  'customerPrefs.channel.whatsapp': "WhatsApp",
  'customerPrefs.channel.post': "Post",
  'customerPrefs.channel.in-person': "In person",
  'customerPrefs.channel.notConsent': "A preference, not consent: marketing still needs their permission.",
  'customerPrefs.delivery': "Documents sent by",
  'customerPrefs.delivery.email': "Email",
  'customerPrefs.delivery.portal': "Customer portal",
  'customerPrefs.delivery.post': "Post",
  'customerPrefs.instructions': "Delivery instructions",
  'relmap.title': "Who reports to whom",
  'relmap.add': "Add a line",
  'relmap.empty': "Nobody at this company is on record yet.",
  'relmap.withheld': "Someone you do not have access to",
  'relmap.unnamed': "Unnamed",
  'relmap.unknown': "Unknown",
  'relmap.dotted': "Also answers to {name} (dotted line)",
  'relmap.influences': "Influences {name}",
  'relmap.remove': "Remove",
  'relmap.removeLine': "Remove this line",
  'relmap.removeManager': "Remove reporting line",
  'relmap.kind': "Line",
  'relmap.kind.reports-to': "reports to",
  'relmap.kind.dotted-line': "has a dotted line to",
  'relmap.kind.influences': "influences",
  'relmap.from': "Person",
  'relmap.to': "Other person",
  'relmap.choose': "Choose…",
  'relmap.save': "Save line",
  'relmap.cancel': "Cancel",
  'relmap.role.decision-maker': "Decision-maker",
  'relmap.role.champion': "Champion",
  'relmap.role.blocker': "Blocker",
  'relmap.role.influencer': "Influencer",
  'relmap.role.user': "User",
  'relmap.role.technical': "Technical",
  'address.title': "Addresses",
  'address.add': "Add address",
  'address.empty': "No addresses yet.",
  'address.default': "Default",
  'address.makeDefault': "Make default",
  'address.remove': "Remove",
  'address.noDefault': "There is no default {kind} address now. Choose one, so documents know where to go.",
  'address.kind': "Kind",
  'address.kind.billing': "Billing",
  'address.kind.shipping': "Shipping",
  'address.kind.site': "Site",
  'address.kind.branch': "Branch",
  'address.label': "Name (optional)",
  'address.line1': "Address line 1",
  'address.line2': "Address line 2",
  'address.city': "City",
  'address.region': "Emirate / region",
  'address.postcode': "Postcode",
  'address.country': "Country code (two letters)",
  'address.save': "Save address",
  'address.cancel': "Cancel",
  'palette.group.create': "Create",
  'palette.group.go': "Go to",
  'palette.create.contact': "New contact",
  'palette.create.contact.hint': "Add a person",
  'palette.create.product': "New product",
  'palette.create.product.hint': "Add to the catalogue",
  'palette.empty': "Nothing matches “{query}”.",
  'palette.searchHint': "Looking for a record? Press / to search.",
  'palette.keys': "↑ ↓ to move · Enter to run · Esc to close",
  'renewals.title': "Renewals",
  'renewals.help': "Every subscription term coming up, worked as a deal. Winning or losing one on the board is what extends the term or stops it renewing.",
  'renewals.open': "Open renewals now",
  'renewals.opened': "{count} renewal(s) opened",
  'renewals.atRisk': "Will lapse unless somebody wins them",
  'renewals.atRisk.help': "Due soon, not yet committed, and set not to renew by themselves.",
  'renewals.atRisk.none': "Nothing is at risk of lapsing.",
  'renewals.daysLeft': "{days} days left",
  'renewals.total': "{count} renewal(s) being worked, worth {amount} {currency} a year. This is revenue already held, not bookings, and is kept out of the forecast and quota.",
  'renewals.otherCurrency': "{count} renewal(s) in another currency are counted but left out of the total.",
  'renewals.empty': "No renewals coming up",
  'renewals.empty.help': "A renewal deal opens for each subscription term as it comes within its notice period.",
  'economics.title': "Unit economics",
  'economics.help': "What a customer is worth against what it cost to win them. Every figure here is a measurement, a projection, or missing — and it says which.",
  'economics.ltv': "Projected lifetime value",
  'economics.ltv.detail': "What a customer would be worth if today's churn held for ever. Not money anyone has.",
  'economics.cac': "Marketing cost per customer",
  'economics.cac.detail': "Campaign spend only. Sales salaries and overhead are not tracked here.",
  'economics.ratio': "Value to cost",
  'economics.ratio.detail': "Projected lifetime value divided by what was spent to win a customer.",
  'economics.payback': "Payback",
  'economics.payback.detail': "Months of revenue to earn back the cost of winning a customer. Revenue, not margin.",
  'economics.months': "months",
  'economics.caveats': "What these figures do not include",
  'economics.campaigns': "By campaign",
  'economics.campaigns.help': "Credit comes from the attribution report, so this and the attribution screen cannot disagree.",
  'economics.column.campaign': "Campaign",
  'economics.column.cost': "Cost",
  'economics.column.deals': "Deals won",
  'economics.column.perDeal': "Per deal",
  'economics.cost.unknown': "Not recorded",
  'economics.empty': "Not enough to work from yet",
  'economics.empty.help': "Once there are subscriptions running and campaign costs recorded, the unit economics will be shown here.",
  'churn.title': "Churn and retention",
  'churn.help': "There is no single churn rate. Losing customers and losing revenue are different things that routinely disagree, and the disagreement is usually the finding.",
  'churn.logo': "Customers lost",
  'churn.logo.detail': "{lost} of {opening} who were paying at the start",
  'churn.net': "Net revenue retention",
  'churn.net.detail': "What the customers who were already here are worth now. Above 100% means expansion outweighed losses.",
  'churn.gross': "Gross revenue retention",
  'churn.gross.detail': "Losses only, so it never rises above 100%.",
  'churn.no-rate': "No rate — nobody was paying",
  'churn.cohorts': "Retention by joining month",
  'churn.cohorts.help': "Each cohort measured against its own first month. Cohorts stop where their history stops rather than being padded out.",
  'churn.cohorts.incomplete': "{count} cohort(s) are too young to fill the whole window, so their later months are left blank rather than shown as zero.",
  'churn.column.cohort': "Joined",
  'churn.column.size': "Size",
  'churn.column.retained': "Still paying",
  'churn.empty': "Nothing to measure yet",
  'churn.empty.help': "Once subscriptions have run for a month or two, churn and retention will be shown here.",
  'dunning.title': "Chasing overdue invoices",
  'dunning.help': "What happens, and when, once an invoice passes its due date. The money is checked at the moment each message goes out, so a customer who has paid is never chased.",
  'dunning.limits': "One rung per run: an invoice found months late is not sent the whole ladder in one morning. And nothing here cancels anything — the last rung puts it in front of a person, because cutting a customer off cannot be undone.",
  'dunning.after': "{days} days after it fell due",
  'dunning.action.email-customer': "write to the customer",
  'dunning.action.notify-owner': "tell the account owner",
  'dunning.action.escalate': "raise it for a person to decide",
  'dunning.inactive': "(switched off)",
  'dunning.run': "Chase overdue invoices now",
  'dunning.run.result': "{sent} written to, {notified} owners told, {escalated} raised, {skipped} skipped",
  'dunning.empty': "No chasing ladder set up",
  'dunning.empty.help': "Add a first rung and overdue invoices will be chased from then on.",
  'recognition.title': "Earned and deferred",
  'recognition.help': "An invoice is not revenue. Money billed in advance is owed back as service until it is delivered, so what was earned in a month sits beside what is still owed at the end of it.",
  'recognition.column.month': "Month",
  'recognition.column.recognised': "Earned",
  'recognition.column.deferred': "Still owed as service",
  'usage.meters.title': "Metered usage",
  'usage.meters.help': "How each meter turns its readings into one figure for the period. Getting this wrong bills an order of magnitude too much, and the invoice still looks ordinary.",
  'usage.aggregation.sum': "every reading added up, in {unit}",
  'usage.aggregation.max': "the highest the period reached, in {unit}",
  'usage.aggregation.last': "the figure the period ended on, in {unit}",
  'invoices.help': "Subscriptions are billed on their own date each period. A draft here is an invoice billing refused to issue, not unfinished work.",
  'invoices.run': "Run billing now",
  'invoices.run.result': "{issued} issued, {drafted} left as drafts, {skipped} skipped",
  'invoices.drafts': "{count} invoice(s) are drafts because their tax could not be worked out. Nobody has been asked to pay them.",
  'invoices.empty': "No invoices yet",
  'invoices.empty.help': "Give a subscription a billing date and a tax jurisdiction, and it will be invoiced from then on.",
  'invoices.unnumbered': "Not yet numbered",
  'invoices.column.number': "Number",
  'invoices.column.account': "Account",
  'invoices.column.period': "Period",
  'invoices.column.status': "Status",
  'invoices.column.gross': "Total",
  'invoices.status.draft': "Draft",
  'invoices.status.issued': "Issued",
  'invoices.status.paid': "Paid",
  'invoices.status.void': "Void",
  'recipes.help': "Ready-made automations and journeys. Installing one opens it as a draft in the builder - it does nothing until you switch it on.",
  'recipes.category': "Category",
  'recipes.category.all': "All",
  'recipes.category.routing': "Routing",
  'recipes.category.hygiene': "Data hygiene",
  'recipes.category.nurture': "Nurture",
  'recipes.category.follow-up': "Follow-up",
  'recipes.category.alerting': "Alerts",
  'recipes.palette.automation': "Automation",
  'recipes.palette.journey': "Journey",
  'recipes.install': "Install as a draft",
  'noun.recipe': "That template",
  'governor.title': "Automation limits",
  'governor.runs': "Automation runs this hour",
  'governor.calls': "Outside calls this hour",
  'governor.steps': "A run stops after {steps} steps. At 80% of an hourly limit administrators are warned; at the limit, new runs are refused until the hour rolls over.",
  'governor.designWarning': "This automation is likely to be expensive:",
  'refusal.governor.runs': "Automation limit reached: {cap} runs an hour, and all of them are used. The budget refills as each run passes an hour old.",
  'subscribe.title': "Send on a schedule",
  'subscribe.help': "The figures go in the email itself, with a link. Anyone can receive it; it shows what you may see.",
  'subscribe.next': "Next {at}",
  'subscribe.sendNow': "Send now",
  'subscribe.stop': "Stop sending",
  'subscribe.frequency': "How often",
  'subscribe.preset.daily': "Every day at 08:00",
  'subscribe.preset.weekdays': "Weekdays at 08:00",
  'subscribe.preset.weekly': "Mondays at 08:00",
  'subscribe.preset.monthly': "The 1st of each month at 08:00",
  'subscribe.recipients': "Email addresses, separated by commas",
  'subscribe.includeRows': "Include the first rows",
  'subscribe.save': "Subscribe",
  'refusal.subscription.recipients': "Send it to between 1 and 50 people.",
  'refusal.subscription.email': "Not an email address: {emails}.",
  'analyses.title': "Analyses",
  'cohorts.title': "Cohorts",
  'cohorts.basis': "Group companies by",
  'cohorts.basis.first-purchase': "When they first bought",
  'cohorts.basis.created': "When they were added",
  'cohorts.grain': "Period",
  'cohorts.grain.month': "Month",
  'cohorts.grain.quarter': "Quarter",
  'cohorts.measure': "Show",
  'cohorts.measure.retention': "Who bought again",
  'cohorts.measure.expansion': "What they spent",
  'cohorts.none': "No won deals with a company yet, so there are no cohorts to show.",
  'cohorts.cohort': "Cohort",
  'cohorts.size': "Companies",
  'cohorts.note': "Columns are periods since the cohort began. Every cell shows its number; the shading repeats it. Revenue counts {currency} deals only.",
  'cohorts.otherCurrency': "{count} won deal(s) in other currencies are not in the revenue figures.",
  'coaching.title': "Coaching view",
  'coaching.help': "Each person's activity and results beside the team's median - a starting point for a coaching conversation, not a ranking.",
  'coaching.period': "Period",
  'coaching.lastDays': "Last {days} days",
  'coaching.none': "No activity or closed deals in this period.",
  'coaching.person': "Person",
  'coaching.type.call': "Calls",
  'coaching.type.email': "Emails",
  'coaching.type.meeting': "Meetings",
  'coaching.type.task': "Tasks",
  'coaching.won': "Won",
  'coaching.winRate': "Win rate",
  'coaching.wonValue': "Won value",
  'coaching.trend': "Last 8 weeks",
  'coaching.teamMedian': "Team median",
  'coaching.atOrAbove': "At or above the team's median",
  'coaching.worthTalking': "Below the team's median - worth talking about",
  'export.title': "Data export",
  'export.objects': "What to export",
  'export.schedule': "Schedule (cron, optional)",
  'export.run': "Export now",
  'export.fileMeta': "{rows} rows · {at}",
  'export.download': "Download",
  'export.feeds': "BI feeds",
  'export.feedsHelp': "A live CSV address for one kind of record, for Power BI, Excel, Google Sheets or Metabase. A direct SQL connection is not offered yet.",
  'export.lastRead': "Last read {at}",
  'export.neverRead': "Not read yet",
  'export.stopFeed': "Stop this feed",
  'export.feedObject': "Records to feed",
  'export.createFeed': "Create feed",
  'export.feedOnce': "The address is shown once - copy the line for your tool:",
  'noun.dataExport': "That export",
  'noun.exportFile': "That file",
  'refusal.export.object': "That cannot be exported.",
  'refusal.export.objects': "Choose what to export.",
  'deliver.title': "Deliverability (last 30 days)",
  'deliver.sent': "{count} sent",
  'deliver.bounces': "Bounces {rate} (keep under {line})",
  'deliver.complaints': "Spam complaints {rate} (keep under {line})",
  'deliver.suppressed': "{count} addresses suppressed",
  'deliver.warning': "Above the line mailbox providers use to start filtering. Clean the audience before the next send.",
  'deliver.noWarmup': "No warm-up: campaigns send in full.",
  'deliver.warmedUp': "Warm-up finished: campaigns send in full.",
  'deliver.warmupDay': "Warm-up day {day}: {sent} of {cap} sent today; {held} waiting for tomorrow.",
  'deliver.startWarmup': "Start warm-up",
  'deliver.stopWarmup': "Stop warm-up",
  'deliver.domains': "Sending domains",
  'deliver.domain.pending': "Not checked yet",
  'deliver.domain.verified': "Verified",
  'deliver.domain.failed': "A record is wrong - see below",
  'deliver.verify': "Check DNS now",
  'deliver.copy': "Copy",
  'deliver.domain': "Domain",
  'deliver.addDomain': "Add domain",
  'web.title': "Website activity",
  'web.seen': "First seen {first}, last seen {last}; {sessions} visit(s)",
  'web.sessionLine': "{pages} page(s), about {minutes} min",
  'web.from': "from {referrer}",
  'journey.entry': "What starts this journey",
  'journey.startsWhen': "Starts when",
  'journey.trigger.entered-segment': "They join the audience",
  'journey.trigger.page-viewed': "They view a page",
  'journey.trigger.form-submitted': "They submit a form",
  'journey.trigger.email-clicked': "They click an email",
  'journey.trigger.custom-event': "Your product sends an event",
  'journey.trigger.field-changed': "A field changes",
  'journey.urlContains': "Page address contains",
  'journey.embedId': "Form (empty for any)",
  'journey.campaignId': "Campaign (empty for any)",
  'journey.eventName': "Event name",
  'journey.field': "Field",
  'journey.value': "Changes to (empty for any)",
  'journey.recentEvents': "Recent events",
  'journey.anonymous': "not identified yet",
  'journey.noEvents': "Nothing has arrived yet. Add the tracking script to your site to see page views here.",
  'sms.title': "SMS templates",
  'sms.segmentsShort': "{count} segment(s), {encoding}",
  'sms.dlt.not-needed': "No DLT needed",
  'sms.dlt.pending': "DLT pending",
  'sms.dlt.approved': "DLT approved",
  'sms.dlt.rejected': "DLT rejected",
  'sms.name': "Template name",
  'sms.body': "Message",
  'sms.bodyHint': "Hi {{firstName|there}}, …",
  'sms.counter': "{chars} characters · {segments} segment(s) · {encoding} · {left} left in this segment",
  'sms.unicode': "\"{char}\" is not in the standard SMS alphabet, so each segment holds 70 characters instead of 160.",
  'sms.to': "Estimate for",
  'sms.country': "Country",
  'sms.cost': "≈ {amount} (an estimate)",
  'sms.dltLabel': "DLT registration",
  'sms.dltId': "DLT template id",
  'sms.save': "Save template",
  'sms.campaignTemplate': "SMS template",
  'sms.chooseTemplate': "Choose a template",
  'noun.smsTemplate': "That SMS template",
  'refusal.sms.name': "Give the template a name.",
  'refusal.sms.body': "Write the message.",
  'refusal.sms.tooLong': "A message is at most 10 segments.",
  'refusal.sms.dltId': "An approved DLT template has its template id.",
  'refusal.sms.country': "A country is its two-letter code, like AE or IN.",
  'refusal.sms.dltEntity': "Sending to India needs your DLT entity id.",
  'refusal.sms.notDraft': "Only a draft SMS campaign takes an SMS template.",
  'refusal.sms.noTemplate': "This SMS campaign has no template yet.",
  'refusal.sms.dlt': "{count} recipient(s) are in India, and \"{template}\" is not DLT-approved. Register it with your operator and record its approval first.",
  'predict.title': "Chance of converting",
  'predict.waiting': "Not enough history yet: {needed} more closed leads are needed ({closed} so far). Until then, use the rule-based score.",
  'predict.chance': "{percent} likely to convert",
  'predict.ruleScore': "Rule-based score: {score}",
  'predict.trust': "Learned from {closed} closed leads; when tested on leads it had not seen, it ranked a real conversion above a non-conversion {auc} of the time.",
  'nav.cadences': "Cadences",
  'cadence.title': "Cadences",
  'cadence.new': "New cadence",
  'cadence.none': "No cadences yet.",
  'cadence.on': "on",
  'cadence.off': "off",
  'cadence.name': "Cadence name",
  'cadence.exitOnReply': "Stop for a person as soon as they reply or book a meeting",
  'cadence.exitWarning': "With this off, people keep getting steps after they reply. That is rarely what anyone wants.",
  'cadence.wait': "wait {days} day(s)",
  'cadence.channel': "Step",
  'cadence.channel.email': "Email",
  'cadence.channel.call': "Call",
  'cadence.channel.linkedin': "LinkedIn",
  'cadence.channel.task': "Task",
  'cadence.after': "after",
  'cadence.days': "days",
  'cadence.removeStep': "Remove",
  'cadence.subject': "Subject",
  'cadence.words': "Words",
  'cadence.emailHint': "Hi {{firstName|there}}, …",
  'cadence.taskHint': "What the rep should do",
  'cadence.addStep': "Add a step",
  'cadence.switchOn': "Switched on",
  'cadence.save': "Save cadence",
  'cadence.enrolled': "Enrolled",
  'cadence.nobody': "Nobody is enrolled.",
  'cadence.atStep': "At step {step}, next {at}",
  'cadence.status.completed': "Finished",
  'cadence.status.exited': "Left",
  'cadence.status.paused': "Paused",
  'cadence.status.active': "Active",
  'cadence.takeOut': "Take out",
  'cadence.pick': "Choose a cadence",
  'cadence.pickBody': "Pick one on the left, or start a new one.",
  'cadence.stepShort': "step {step}",
  'cadence.choose': "Choose a cadence",
  'cadence.enrol': "Enrol",
  'cadence.theyReplied': "They replied",
  'noun.cadence': "That cadence",
  'refusal.cadence.name': "Give the cadence a name.",
  'refusal.cadence.steps': "A cadence has 1 to 20 steps.",
  'refusal.cadence.email': "Step {step} is an email and needs its subject and words.",
  'refusal.cadence.delay': "A delay is 0 to 90 days.",
  'refusal.cadence.inactive': "{name} is not switched on.",
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
